import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  SettingOutlined,
  ShoppingCartOutlined,
  InfoCircleOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  HomeOutlined,
  TeamOutlined,
  UploadOutlined,
  EllipsisOutlined,
  BellOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Steps,
  Statistic,
  Table,
  Tag,
  Tabs,
  Tooltip,
  Typography,
  message,
  Drawer,
  Radio,
  Checkbox,
  Image,
  Dropdown,
  Badge,
  Popover,
  List,
  Upload,
} from "antd";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { getCatalog } from "../services/catalogApi";
import { RootState, useAppDispatch, useAppSelector } from "../store";
import { LocationReorderData, ReorderItem } from "../store/reorderSlice";
import { SimpleProduct } from "../types/shopify";
import { getStorages, createStorage, updateStorage, deleteStorage, Storage } from "../services/storageApi";
import { getRetailers, updateRetailer, Project, Retailer } from "../services/retailerApi";
import { createProjectGlobal, deleteProjectGlobal, getProjects, updateProjectGlobal } from "../services/projectApi";
import ProjectKanban from "../components/Projects/ProjectKanban";
import ProjectDetails from "../components/Projects/ProjectDetails";
import {
  pruneSupplyNotifications,
  removeSupplyNotifications,
  upsertSupplyNotifications,
} from "../store/supplyNotificationsSlice";

// Fonctions utilitaires pour l'affichage des statuts
import * as purchaseOrderService from "../services/purchaseOrderService";
import * as supplierApi from "../services/supplierApi";
import { CARRIERS } from "../config/carriers";
import * as supplyRuleApi from "../services/supplyRuleApi";
import { exportPurchaseOrderToXLSX as exportPOXLSX } from "../utils/exportPurchaseOrderXLSX";
import {
  getStockReportHeaders,
  parseStockReportFile,
} from "../utils/stockReportParser";

// Fonctions utilitaires pour l'affichage des statuts
const getStatusColor = (status: string): string => {
  const statusColors: Record<string, string> = {
    draft: "gray",
    sent: "blue",
    in_delivery: "processing",
    noted_delivered: "gold",
    received: "green",
    completed: "green",
    cancelled: "red",
    partially_received: "orange",
    dispute: "volcano",
  };
  return statusColors[status] || "default";
};

const getStatusLabel = (status: string): string => {
  const statusLabels: Record<string, string> = {
    draft: "Brouillon",
    sent: "Envoyée",
    in_delivery: "En cours de livraison",
    noted_delivered: "Noté livré",
    // Ancien statut "received" conservé pour compat, mais affiché comme "Complété"
    received: "Complété",
    completed: "Complété",
    cancelled: "Annulée",
    partially_received: "Partiellement reçue",
    dispute: "Litige",
  };
  return statusLabels[status] || status;
};

const roundTo2 = (value: number): number => Math.round(value * 100) / 100;

const exportPurchaseOrderToPdf = (order: PurchaseOrder) => {
  const win = window.open("", "_blank", "width=900,height=700");
  if (!win) return;
  const rows = (order.items || [])
    .map(
      (item) => `
      <tr>
        <td>${item.productName || ""}</td>
        <td style="text-align:right;">${item.quantity || 0}</td>
        <td style="text-align:right;">${(item.unitPrice || 0).toFixed(2)} €</td>
        <td style="text-align:right;">${(item.totalPrice || 0).toFixed(2)} €</td>
      </tr>`
    )
    .join("");
  const html = `<!doctype html>
  <html><head><meta charset="utf-8" />
  <title>PO ${order.reference || ""}</title>
  <style>
    body{font-family:Arial,sans-serif;padding:24px;color:#0f172a}
    h1{font-size:20px;margin:0 0 8px}
    .muted{color:#64748b;font-size:12px}
    table{width:100%;border-collapse:collapse;margin-top:16px}
    th,td{padding:8px;border-bottom:1px solid #e2e8f0;font-size:12px}
    th{text-align:left;background:#f8fafc}
    .total{margin-top:14px;text-align:right;font-weight:700}
  </style></head>
  <body>
    <h1>Bon de commande ${order.reference || ""}</h1>
    <div class="muted">Fournisseur: ${order.supplier || "-"} • Destination: ${order.deliveryAddress?.name || "-"}</div>
    <table>
      <thead><tr><th>Produit</th><th style="text-align:right;">Qté</th><th style="text-align:right;">P.U.</th><th style="text-align:right;">Total</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>
    <div class="total">Montant total: ${(order.totalAmount || 0).toFixed(2)} €</div>
  </body></html>`;
  win.document.write(html);
  win.document.close();
  win.focus();
  win.print();
};

// Types pour la gestion des approvisionnements
interface Product extends SimpleProduct {
  name: string; // Correspond à title dans SimpleProduct
  supplier?: string;
  category?: string;
  minStock?: number;
  currentStock?: number; // Correspond à inventory dans SimpleProduct
  unit?: string;
  imageSrc?: string;
  stockByLocation?: {
    [locationId: string]: { quantity: number; locationName: string };
  };
  costPerItem?: number;
  distributorPrices?: Array<{
    distributorId: string;
    distributorName?: string;
    price: number;
    currency?: string;
  }>;
}

interface RetailerContact {
  name?: string;
  email?: string;
}

interface RetailerLite {
  id?: string;
  _id?: string;
  name?: string;
  contacts?: RetailerContact[];
  status?: string;
  crm?: { onboardingStage?: string };
}

interface SupplyNotification {
  id: string;
  subject: string;
  from: string;
  receivedDateTime?: string;
  category: "new_order" | "delivery_issue" | "delivery_followup" | "other";
  label: string;
  orderReference?: string | null;
  externalId?: string | null;
  confidence?: number;
  summary?: string;
  highlights?: string[];
  entityName?: string;
  addedAt: string;
}

interface Supplier extends supplierApi.Supplier {
  suppliedType?: "brands" | "references";
  suppliedItems?: string[];
}

interface DeliveryAddress {
  id: string;
  retailerId?: string;
  retailerName?: string;
  fulfillmentMode?: "hub" | "multi_hub" | "per_channel";
  channelKey?: string;
  channelType?: string;
  locationId?: string;
  name: string;
  streetAddress: string;
  postalCode: string;
  city: string;
  country: string;
  contactPerson?: string;
  phone?: string;
  deliveryInfo?: string;
  comment?: string;
  isDefault?: boolean;
}

// Utiliser l'interface du service pour PurchaseOrderItem
interface PurchaseOrderItem extends Omit<purchaseOrderService.PurchaseOrderItem, 'sku'> {
  productId: string;
  productName: string;
  vendor: string;
  sku?: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  totalPrice: number;
  unitWeightKg?: number;
  hsCode?: string;
  madeIn?: string;
  image?: string;
}

// Utiliser les mêmes statuts que le service pour compatibilité
// type PurchaseOrderStatus =
//   | "draft"
//   | "sent"
//   | "partially_received"
//   | "in_delivery"
//   | "noted_delivered"
//   | "received"
//   | "completed"
//   | "cancelled"
//   | "dispute";

// Utiliser l'interface PurchaseOrder du service pour uniformiser les types
interface PurchaseOrder extends purchaseOrderService.PurchaseOrder {
  // Champs supplémentaires pour la compatibilité avec le code existant
  id?: string;
  createdAt?: Date; // Aligné avec creationDate du service
  expectedDeliveryDate?: Date; // Aligné avec deliveryDate du service
  deliveryStreet?: string;
  deliveryCity?: string;
  deliveryCountry?: string;
  estimatedCost?: number;
}

// Interface pour le stock par emplacement (recommandations internes)
interface LocationStock {
  stock: number;
  locationId: string;
}

// Import des types depuis l'API des règles d'approvisionnement
import { RuleType, SupplyRule } from "../services/supplyRuleApi";

interface RecommendedProduct {
  productId: string;
  locationStocks: Array<{ stock: number; locationId: string }>;
}

// Composant principal
export const Supply = () => {
  const normalizeId = (value: any): string =>
    String(value?._id || value?.$oid || value || "");
  const isObjectId = (value: string): boolean => /^[a-fA-F0-9]{24}$/.test(value);
  const dispatch = useAppDispatch();

  // États pour la gestion des bons de commande
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [shippingEstimateByPoId, setShippingEstimateByPoId] = useState<
    Record<
      string,
      {
        loading: boolean;
        error: string | null;
      }
    >
  >({});
  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
  const [isImportingBatches, setIsImportingBatches] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [purchaseOrderForm] = Form.useForm();
  const [selectedOrder, setSelectedOrder] = useState<PurchaseOrder | null>(
    null
  );

  // État pour la modale du bon de commande conseillé
  const [showRecommendedOrderModal, setShowRecommendedOrderModal] =
    useState<boolean>(false);
  const [desiredRunway, setDesiredRunway] = useState<number>(30);
  const [calculatedItems, setCalculatedItems] = useState<any[]>([]);
  // State local pour les produits recommandés avec leurs quantités par emplacement
  const [recommendedProducts, setRecommendedProducts] = useState<
    RecommendedProduct[]
  >([]);

  // State pour le système de règles flexibles
  const [supplyRules, setSupplyRules] = useState<SupplyRule[]>([]);
  const [showRulesModal, setShowRulesModal] = useState<boolean>(false);
  const [currentRule, setCurrentRule] = useState<SupplyRule | null>(null);

  // États pour la gestion des entrepôts
  const [storages, setStorages] = useState<Storage[]>([]);
  const [showStorageModal, setShowStorageModal] = useState(false);
  const [showStorageFormModal, setShowStorageFormModal] = useState(false);
  const [storageForm] = Form.useForm();
  const [currentStorage, setCurrentStorage] = useState<Storage | null>(null);
  const [loadingStorages, setLoadingStorages] = useState(false);
  const [stockTemplateHeaders, setStockTemplateHeaders] = useState<string[]>([]);
  const [stockTemplateLoading, setStockTemplateLoading] = useState(false);

  // États pour les fournisseurs et adresses
  const [supplierList, setSupplierList] = useState<Supplier[]>([]);
  const [currentSupplier, setCurrentSupplier] = useState<Supplier | null>(null);
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [showSupplierFormModal, setShowSupplierFormModal] = useState(false);
  const [supplierForm] = Form.useForm();
  const [isSupplierAddressEditing, setIsSupplierAddressEditing] =
    useState<boolean>(false);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);

  // États pour la gestion du sélecteur marques/références dans le formulaire fournisseur
  const [supplierFormType, setSupplierFormType] = useState<
    "brands" | "references"
  >("brands");
  const [availableVendors, setAvailableVendors] = useState<string[]>([]);
  const [allProducts, setAllProducts] = useState<SimpleProduct[]>([]);
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const [loadingProducts, setLoadingProducts] = useState<boolean>(false);
  // Filtres de la sélection de produits (tiroir de création)
  const [productVendorFilter, setProductVendorFilter] = useState<string[]>([]);
  const [productSupplierFilterIds, setProductSupplierFilterIds] = useState<
    string[]
  >([]);

  const [addressList, setAddressList] = useState<DeliveryAddress[]>([]);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [showRetailerLogisticsModal, setShowRetailerLogisticsModal] = useState(false);
  const [editingRetailer, setEditingRetailer] = useState<Retailer | null>(null);
  const [showPennylaneModal, setShowPennylaneModal] = useState(false);
  const [pennylaneOrder, setPennylaneOrder] = useState<PurchaseOrder | null>(null);
  const pennylaneOpenRequestRef = useRef(0);
  const [pennylaneLoading, setPennylaneLoading] = useState(false);
  const [pennylanePrefetchLoading, setPennylanePrefetchLoading] = useState(false);
  const [reopenDetailsAfterPennylane, setReopenDetailsAfterPennylane] = useState(false);
  const [pennylaneCustomerQuery, setPennylaneCustomerQuery] = useState("");
  const [pennylaneSupplierQuery, setPennylaneSupplierQuery] = useState("");
  const [pennylaneCustomerInvoices, setPennylaneCustomerInvoices] = useState<purchaseOrderService.LinkedInvoice[]>([]);
  const [pennylaneSupplierInvoices, setPennylaneSupplierInvoices] = useState<purchaseOrderService.LinkedInvoice[]>([]);
  const [attachingPennylaneDirection, setAttachingPennylaneDirection] = useState<"incoming" | "outgoing" | null>(null);
  const [loadingAddresses, setLoadingAddresses] = useState(false);

  const [retailerList, setRetailerList] = useState<RetailerLite[]>([]);
  const [supplyRetailers, setSupplyRetailers] = useState<Retailer[]>([]);
  const supplyNotifications = useAppSelector(
    (state: RootState) => state.supplyNotifications.items
  );
  const [supplyInboxLoading, setSupplyInboxLoading] = useState(false);

  const [supplyProjects, setSupplyProjects] = useState<Project[]>([]);
  const [selectedSupplyProject, setSelectedSupplyProject] = useState<Project | null>(null);
  const [supplyProjectDrawerOpen, setSupplyProjectDrawerOpen] = useState(false);
  const [supplyDragOverColumn, setSupplyDragOverColumn] = useState<string | null>(null);
  const [supplyTabKey, setSupplyTabKey] = useState("history");

  // États pour la gestion des données
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>("");
  const [productSearchText, setProductSearchText] = useState<string>("");
  const [visibleProductCount, setVisibleProductCount] = useState<number>(50);

  // États pour les filtres des bons de commande
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [typeFilter, setTypeFilter] = useState<string>("");
  const [startDateFilter, setStartDateFilter] = useState<dayjs.Dayjs | null>(
    null
  );
  const [endDateFilter, setEndDateFilter] = useState<dayjs.Dayjs | null>(null);
  const [supplierFilter, setSupplierFilter] = useState<string>("");
  const [addressFilter, setAddressFilter] = useState<string>("");

  // Nous n'affichons pas les erreurs dans l'UI pour l'instant, mais nous les loggons via message.error


  // États pour la création de purchase order
  const [flowType, setFlowType] = useState<"inbound" | "outbound">("inbound");
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [createDrawerVisible, setCreateDrawerVisible] =
    useState<boolean>(false);
  const [selectedProducts, setSelectedProducts] = useState<{
    [key: string]: boolean;
  }>({});

  // État pour stocker les quantités par produit
  const [productQuantities, setProductQuantities] = useState<{
    [key: string]: number; // format: productId
  }>({});

  // Les adresses sont maintenant automatiquement associées aux emplacements

  // Récupération des données de réapprovisionnement depuis Redux pour tous les emplacements
  const allLocationReorderData = useAppSelector(
    (state: RootState) => state.reorder.locationReorderDataArray
  );

  // Import de lots (batches) depuis un fichier CSV simple
  const handleImportBatchesFile = (file: File) => {
    if (!selectedOrder) {
      message.error("Aucun bon de commande sélectionné");
      return;
    }

    const orderId = (selectedOrder._id || (selectedOrder as any).id) as
      | string
      | undefined;
    if (!orderId) {
      message.error("Impossible de déterminer l'ID du bon de commande");
      return;
    }

    setIsImportingBatches(true);

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const text = String(e.target?.result || "");
        const rawLines = text.split(/\r?\n/);
        const lines = rawLines
          .map((l) => l.trim())
          .filter((l) => l.length > 0);

        if (lines.length <= 1) {
          message.error("Le fichier doit contenir au moins une ligne de données");
          setIsImportingBatches(false);
          return;
        }

        // Header avec au moins: barcode,batchNumber,expirationDate,quantity
        const detectSeparator = (line: string): string => {
          if (line.includes(";")) return ";";
          if (line.includes(",")) return ",";
          return ";";
        };

        const headerLine = lines[0];
        const sep = detectSeparator(headerLine);
        const header = headerLine
          .split(sep)
          .map((h) => h.trim().toLowerCase());

        const getIndex = (name: string) =>
          header.findIndex((h) => h === name.toLowerCase());

        const idxBarcode = getIndex("barcode");
        const idxBatchNumber = getIndex("batchnumber");
        const idxExpirationDate = getIndex("expirationdate");
        const idxQuantity = getIndex("quantity");

        if (idxBarcode === -1 || idxBatchNumber === -1 || idxQuantity === -1) {
          message.error(
            "Le fichier doit contenir au minimum les colonnes: barcode, batchNumber, quantity"
          );
          setIsImportingBatches(false);
          return;
        }

        const byBarcode: {
          [barcode: string]: purchaseOrderService.PurchaseOrderBatchLine;
        } = {};

        for (let i = 1; i < lines.length; i++) {
          const row = lines[i].split(sep);
          const rawBarcode = (row[idxBarcode] || "").trim();
          const rawBatchNumber = (row[idxBatchNumber] || "")
            .trim()
            .toUpperCase();
          const rawQuantity = (row[idxQuantity] || "").replace(",", ".");
          const rawExpiration =
            idxExpirationDate >= 0 ? (row[idxExpirationDate] || "").trim() : "";

          if (!rawBarcode || !rawBatchNumber) {
            continue;
          }

          const quantity = Number(rawQuantity);
          if (!Number.isFinite(quantity) || quantity <= 0) {
            continue;
          }

          let expiration: string | undefined = undefined;
          if (rawExpiration) {
            expiration = rawExpiration;
          }

          if (!byBarcode[rawBarcode]) {
            byBarcode[rawBarcode] = {
              barcode: rawBarcode,
              batches: [],
            };
          }

          byBarcode[rawBarcode].batches.push({
            batchNumber: rawBatchNumber,
            expirationDate: expiration,
            quantity,
          });
        }

        const batchLines = Object.values(byBarcode);

        if (batchLines.length === 0) {
          message.error("Aucune ligne de lot valide détectée dans le fichier");
          setIsImportingBatches(false);
          return;
        }

        await purchaseOrderService.updatePurchaseOrderBatches(
          orderId,
          batchLines
        );

        message.success("Lots mis à jour à partir du fichier");

        // Recharger la commande détaillée pour afficher les batches
        const updated = await purchaseOrderService.getPurchaseOrderById(orderId);
        setSelectedOrder(updated);
        fetchPurchaseOrders();
      } catch (error: any) {
        console.error("Erreur lors de l'import des lots:", error);
        message.error(
          `Erreur lors de l'import des lots: ${error?.message || "inconnue"}`
        );
      } finally {
        setIsImportingBatches(false);
      }
    };

    reader.onerror = () => {
      message.error("Erreur lors de la lecture du fichier");
      setIsImportingBatches(false);
    };

    reader.readAsText(file, "utf-8");
  };

  // Créer un ensemble unique de produits à partir de tous les emplacements
  const uniqueProductsMap = useMemo(() => {
    const productsMap = new Map();

    allLocationReorderData.forEach((location: LocationReorderData) => {
      location.reorderItems.forEach((item: ReorderItem) => {
        if (!productsMap.has(item.productId)) {
          productsMap.set(item.productId, {
            ...item,
            locationData: {},
          });
        }

        // Ajouter les données spécifiques à l'emplacement pour ce produit
        productsMap.get(item.productId).locationData[location.locationId] = {
          recommendedQuantity: item.recommendedQuantity,
          dailySales: item.dailySales,
          inventory: item.inventory,
          locationName: location.sourceLocationName,
        };
      });
    });

    return productsMap;
  }, [allLocationReorderData]);

  // Convertir la map en tableau pour l'affichage
  const aggregatedReorderItems = useMemo(
    () => Array.from(uniqueProductsMap.values()),
    [uniqueProductsMap]
  );

  // Recalcule les quantités recommandées lorsque le runway change
  useEffect(() => {
    if (aggregatedReorderItems && aggregatedReorderItems.length > 0) {
      const newCalculatedItems = aggregatedReorderItems.map((item) => {
        // Créer une copie de l'item pour ne pas modifier l'original
        const newItem = { ...item };

        // Si l'item a des données par emplacement
        if (newItem.locationData) {
          Object.keys(newItem.locationData).forEach((locationId) => {
            const locationInfo = newItem.locationData[locationId];
            if (
              locationInfo &&
              typeof locationInfo.dailySales === "number" &&
              typeof locationInfo.currentStock === "number"
            ) {
              // Calculer la quantité recommandée basée sur le runway
              // Formule: max(0, (ventes quotidiennes * runway) - stock actuel)
              const newRecommendedQty = Math.max(
                0,
                Math.ceil(locationInfo.dailySales * desiredRunway) -
                  locationInfo.currentStock
              );

              // Mettre à jour la quantité recommandée
              newItem.locationData[locationId] = {
                ...locationInfo,
                recommendedQuantity: newRecommendedQty,
              };
            }
          });
        }

        return newItem;
      });

      setCalculatedItems(newCalculatedItems);
    }
  }, [desiredRunway, aggregatedReorderItems]);

  // Définition du bon de commande
  const [newPO, setNewPO] = useState<Partial<PurchaseOrder>>({
    items: [],
    status: "draft",
    totalAmount: 0,
  });

  // Formulaire pour la création de PO
  const [poForm] = Form.useForm();
  const [retailerLogisticsForm] = Form.useForm();
  const selectedRetailerId = Form.useWatch("retailerId", poForm);
  const editFulfillmentMode = Form.useWatch(
    ["logistics", "fulfillmentMode"],
    retailerLogisticsForm
  );
  const outboundDestinations = useMemo(
    () =>
      addressList.filter(
        (addr) =>
          addr.retailerId &&
          normalizeId(addr.retailerId) === normalizeId(selectedRetailerId)
      ),
    [addressList, selectedRetailerId]
  );

  useEffect(() => {
    if (flowType !== "outbound") return;
    if (!selectedRetailerId) {
      poForm.setFieldsValue({ deliveryAddressId: undefined, deliveryAddressName: undefined });
      return;
    }
    if (outboundDestinations.length === 1) {
      poForm.setFieldsValue({
        deliveryAddressId: normalizeId(outboundDestinations[0].id),
        deliveryAddressName: outboundDestinations[0].name,
      });
    } else if (outboundDestinations.length === 0) {
      poForm.setFieldsValue({ deliveryAddressId: undefined, deliveryAddressName: undefined });
    }
  }, [flowType, outboundDestinations, poForm, selectedRetailerId]);

  const openRetailerLogisticsModal = (retailerId?: string) => {
    if (!retailerId) return;
    const retailer = supplyRetailers.find(
      (r) => normalizeId(r.id || r._id) === normalizeId(retailerId)
    );
    if (!retailer) {
      message.error("Retailer introuvable");
      return;
    }
    setEditingRetailer(retailer);
    retailerLogisticsForm.setFieldsValue({
      logistics: retailer.logistics || { fulfillmentMode: "hub" },
      channelsDetails: retailer.channelsDetails || [],
    });
    setShowRetailerLogisticsModal(true);
  };

  const handleSaveRetailerLogistics = async () => {
    if (!editingRetailer) return;
    try {
      const values = await retailerLogisticsForm.validateFields();
      const payload: Partial<Retailer> = {
        logistics: values.logistics || { fulfillmentMode: "hub" },
      };
      if (values.logistics?.fulfillmentMode === "per_channel") {
        const existingChannels = editingRetailer.channelsDetails || [];
        payload.channelsDetails = (values.channelsDetails || []).map(
          (channel: any, index: number) => ({
            ...existingChannels[index],
            ...channel,
            logistics: channel.logistics || existingChannels[index]?.logistics,
          })
        );
      }

      const id = String(editingRetailer.id || editingRetailer._id || "");
      await updateRetailer(id, payload);
      message.success("Logistique retailer mise à jour");
      setShowRetailerLogisticsModal(false);
      setEditingRetailer(null);
      retailerLogisticsForm.resetFields();
      await fetchAddresses();
    } catch (error: any) {
      console.error("Erreur mise à jour logistique retailer:", error);
      if (error?.errorFields?.length) return;
      message.error("Impossible de mettre à jour la logistique");
    }
  };

  const refreshSelectedOrder = async (id: string) => {
    const updated = await purchaseOrderService.getPurchaseOrderById(id);
    setSelectedOrder((current) =>
      String(current?._id || current?.id || "") === id ? (updated as any) : current
    );
    setPennylaneOrder((current) =>
      String(current?._id || current?.id || "") === id ? (updated as any) : current
    );
    setPurchaseOrders((prev) =>
      prev.map((po) => (String(po._id || po.id) === id ? ({ ...po, ...updated } as any) : po))
    );
    return updated;
  };

  const fetchPennylaneInvoicesForOrder = async (
    order: PurchaseOrder,
    kind: "customer" | "supplier",
    queryOverride?: string
  ) => {
    const orderId = String(order._id || order.id || "").trim();
    if (!orderId) return [];
    const query =
      queryOverride ??
      (kind === "customer"
        ? pennylaneCustomerQuery || order.retailerName || order.reference || ""
        : pennylaneSupplierQuery || order.supplier || order.reference || "");
    const resp = await purchaseOrderService.getPennylaneInvoicesForPurchaseOrder(orderId, { kind, query });
    return resp.items || [];
  };

  const fetchPennylaneInvoices = async (kind: "customer" | "supplier", queryOverride?: string) => {
    if (!pennylaneOrder) return;
    setPennylaneLoading(true);
    try {
      const items = await fetchPennylaneInvoicesForOrder(pennylaneOrder, kind, queryOverride);
      if (kind === "customer") {
        setPennylaneCustomerInvoices(items);
      } else {
        setPennylaneSupplierInvoices(items);
      }
    } catch (error: any) {
      message.error(error?.message || "Erreur Pennylane");
    } finally {
      setPennylaneLoading(false);
    }
  };

  const openPennylaneModalForOrder = async (order: PurchaseOrder, opts?: { fromDetails?: boolean }) => {
    if (String(order.type || "").toLowerCase() !== "outbound") {
      message.info("Les factures Pennylane sont disponibles uniquement pour les expéditions.");
      return;
    }
    const orderId = String(order._id || order.id || "").trim();
    if (!orderId) {
      message.error("Bon de commande invalide");
      return;
    }
    const requestId = ++pennylaneOpenRequestRef.current;
    setPennylaneOrder(order);
    setSelectedOrder(order);
    if (opts?.fromDetails) {
      setReopenDetailsAfterPennylane(true);
      setShowDetailsModal(false);
      setIsEditMode(false);
    } else {
      setReopenDetailsAfterPennylane(false);
    }
    const customerSeed = String(order.retailerName || order.reference || "");
    const supplierSeed = String(order.supplier || order.reference || "");
    setPennylaneCustomerQuery(customerSeed);
    setPennylaneSupplierQuery(supplierSeed);
    setPennylaneCustomerInvoices([]);
    setPennylaneSupplierInvoices([]);
    setPennylanePrefetchLoading(true);
    const loadingKey = "pennylane-prefetch";
    message.loading({ content: "Chargement des factures Pennylane...", key: loadingKey, duration: 0 });
    try {
      const [customerItems, supplierItems] = await Promise.all([
        fetchPennylaneInvoicesForOrder(order, "customer", customerSeed),
        fetchPennylaneInvoicesForOrder(order, "supplier", supplierSeed),
      ]);
      if (requestId !== pennylaneOpenRequestRef.current) return;
      setPennylaneCustomerInvoices(customerItems);
      setPennylaneSupplierInvoices(supplierItems);
      setShowPennylaneModal(true);
      message.destroy(loadingKey);
    } catch (error: any) {
      message.destroy(loadingKey);
      message.error(error?.message || "Erreur Pennylane");
    } finally {
      setPennylanePrefetchLoading(false);
    }
  };

  const handleAttachPennylaneInvoice = async (
    direction: "incoming" | "outgoing",
    invoice: purchaseOrderService.LinkedInvoice
  ) => {
    if (!pennylaneOrder) return;
    const orderId = String(pennylaneOrder._id || pennylaneOrder.id || "").trim();
    if (!orderId) return;
    setAttachingPennylaneDirection(direction);
    try {
      await purchaseOrderService.linkPennylaneInvoiceToPurchaseOrderPayment(orderId, {
        direction,
        invoice,
        syncStatus: true,
      });
      await refreshSelectedOrder(orderId);
      message.success("Facture rattachée au bon de commande");
    } catch (error: any) {
      message.error(error?.message || "Erreur lors du rattachement");
    } finally {
      setAttachingPennylaneDirection(null);
    }
  };

  const handleClearPennylaneInvoice = async (direction: "incoming" | "outgoing") => {
    if (!pennylaneOrder) return;
    const orderId = String(pennylaneOrder._id || pennylaneOrder.id || "").trim();
    if (!orderId) return;
    setAttachingPennylaneDirection(direction);
    try {
      await purchaseOrderService.linkPennylaneInvoiceToPurchaseOrderPayment(orderId, {
        direction,
        clear: true,
      });
      await refreshSelectedOrder(orderId);
      message.success("Rattachement supprimé");
    } catch (error: any) {
      message.error(error?.message || "Erreur lors de la suppression");
    } finally {
      setAttachingPennylaneDirection(null);
    }
  };

  const handleManualPaymentStatus = async (
    direction: "incoming" | "outgoing",
    manualStatus: "waiting" | "pending" | "partial" | "paid"
  ) => {
    if (!pennylaneOrder) return;
    const orderId = String(pennylaneOrder._id || pennylaneOrder.id || "").trim();
    if (!orderId) return;
    setAttachingPennylaneDirection(direction);
    try {
      await purchaseOrderService.linkPennylaneInvoiceToPurchaseOrderPayment(orderId, {
        direction,
        manualStatus,
      });
      await refreshSelectedOrder(orderId);
      message.success("Statut de paiement mis à jour");
    } catch (error: any) {
      message.error(error?.message || "Erreur lors de la mise à jour du statut");
    } finally {
      setAttachingPennylaneDirection(null);
    }
  };

  const isSupplyProject = (project: Project) =>
    project.metadata?.supplyProject === "true";

  const fetchSupplyProjects = async () => {
    try {
      const data = await getProjects();
      const supply = (data || []).filter(isSupplyProject);
      setSupplyProjects(supply);
      return supply;
    } catch (error) {
      message.error("Impossible de charger les projets supply");
      return [] as Project[];
    }
  };

  const buildSupplyProjectTitle = (notification: SupplyNotification) => {
    const entity = notification.entityName ? ` - ${notification.entityName}` : "";
    if (notification.category === "new_order") return `Commande${entity}`;
    if (notification.category === "delivery_issue") return `Livraison en alerte${entity}`;
    if (notification.category === "delivery_followup") return `Suivi livraison${entity}`;
    return `${notification.label}${entity}`;
  };

  const ensureSupplyProjectsFromNotifications = async (
    notifications: SupplyNotification[],
    existingSupplyProjects: Project[] = []
  ) => {
    const normalizeMetaValue = (value?: string | null) =>
      String(value || "").trim().toLowerCase();
    const existingBySource = new Set(
      existingSupplyProjects
        .map((project) => project.metadata?.supplySourceId)
        .filter(Boolean) as string[]
    );
    const created: Project[] = [];

    for (const notification of notifications) {
      if (!notification.id || existingBySource.has(notification.id)) continue;

      const normalizedOrderReference = normalizeMetaValue(
        notification.orderReference || null
      );
      const normalizedExternalId = normalizeMetaValue(
        notification.externalId || null
      );
      const matchingProject = existingSupplyProjects.find((project) => {
        const projectOrderReference = normalizeMetaValue(
          project.metadata?.orderReference
        );
        const projectExternalId = normalizeMetaValue(
          project.metadata?.externalId
        );
        return (
          (normalizedOrderReference &&
            projectOrderReference === normalizedOrderReference) ||
          (normalizedExternalId && projectExternalId === normalizedExternalId)
        );
      });

      if (matchingProject && matchingProject._id) {
        const mergedEmailIds = Array.from(
          new Set([
            ...(matchingProject.attachedEmailIds || []),
            notification.id,
          ])
        );
        try {
          const updatedProject = await updateProjectGlobal(matchingProject._id, {
            attachedEmailIds: mergedEmailIds,
            lastEmailSubject: notification.subject,
            lastEmailPreview: notification.summary || notification.subject,
            lastEmailAt: notification.receivedDateTime,
            lastEmailFrom: notification.from,
            unreadEmailCount: (matchingProject.unreadEmailCount || 0) + 1,
            metadata: {
              ...(matchingProject.metadata || {}),
              category:
                matchingProject.metadata?.category || notification.category,
              orderReference: notification.orderReference
                ? String(notification.orderReference)
                : matchingProject.metadata?.orderReference || "",
              externalId: notification.externalId
                ? String(notification.externalId)
                : matchingProject.metadata?.externalId || "",
              supplySourceId:
                matchingProject.metadata?.supplySourceId || notification.id,
            },
          });
          setSupplyProjects((prev: Project[]) =>
            prev.map((project) =>
              project._id === updatedProject._id
                ? { ...project, ...updatedProject }
                : project
            )
          );
        } catch (error) {
          console.error("Erreur mise à jour projet supply:", error);
        }
        continue;
      }

      const payload: Partial<Project> = {
        title: buildSupplyProjectTitle(notification),
        description: notification.summary || notification.subject,
        status: "open",
        priority: notification.category === "delivery_issue" ? "high" : "medium",
        pushPullStatus: notification.category === "new_order" ? "push" : "pull",
        externalEntityName: notification.entityName,
        attachedEmailIds: notification.id ? [notification.id] : [],
        lastEmailSubject: notification.subject,
        lastEmailPreview: notification.summary || notification.subject,
        lastEmailAt: notification.receivedDateTime,
        lastEmailFrom: notification.from,
        unreadEmailCount: 1,
        metadata: {
          supplyProject: "true",
          supplySourceId: notification.id,
          category: notification.category,
          orderReference: notification.orderReference ? String(notification.orderReference) : "",
          externalId: notification.externalId ? String(notification.externalId) : "",
        },
      };

      try {
        const createdProject = await createProjectGlobal(payload);
        created.push(createdProject);
      } catch (error) {
        console.error("Erreur création projet supply:", error);
      }
    }

    if (created.length) {
      setSupplyProjects((prev: Project[]) => [...created, ...prev]);
    }
  };

  const handleSupplyProjectDrop = async (projectId: string, target: string) => {
    setSupplyDragOverColumn(null);
    const project = supplyProjects.find((p) => p._id === projectId);
    if (!project) return;

    let updates: Partial<Project> = {};
    if (target === "todo") {
      updates = { status: "open", pushPullStatus: "push" };
    } else if (target === "in_progress") {
      updates = { status: "open", pushPullStatus: "pull" };
    } else if (target === "on_hold") {
      updates = { status: "on_hold" };
    } else {
      updates = { status: "completed" };
    }

    try {
      const data = await updateProjectGlobal(projectId, updates);
      setSupplyProjects((prev: Project[]) =>
        prev.map((p) => (p._id === projectId ? { ...p, ...data } : p))
      );
      if (selectedSupplyProject?._id === projectId) {
        setSelectedSupplyProject((prev) => (prev ? { ...prev, ...data } : prev));
      }
    } catch (error) {
      message.error("Erreur lors de la mise à jour du statut");
    }
  };

  const handleSupplyProjectDelete = async (projectId: string) => {
    Modal.confirm({
      title: "Supprimer le projet supply",
      content: "Êtes-vous sûr de vouloir supprimer ce suivi ?",
      okText: "Supprimer",
      okType: "danger",
      cancelText: "Annuler",
      onOk: async () => {
        try {
          await deleteProjectGlobal(projectId);
          setSupplyProjects((prev: Project[]) => prev.filter((p) => p._id !== projectId));
          if (selectedSupplyProject?._id === projectId) {
            setSelectedSupplyProject(null);
            setSupplyProjectDrawerOpen(false);
          }
          message.success("Suivi supprimé");
        } catch (error) {
          message.error("Erreur lors de la suppression");
        }
      },
    });
  };

  const handleSupplyProjectClick = (projectId: string) => {
    const project = supplyProjects.find((p) => p._id === projectId);
    if (project) {
      setSelectedSupplyProject(project);
      setSupplyProjectDrawerOpen(true);
    }
  };

  const getSupplyContactDisplayName = (retailerId: string, email?: string) => {
    const retailer = supplyRetailers.find((r) => (r.id || r._id) === retailerId);
    if (!email) return retailer?.name || "—";
    return resolveEmailEntityName(email) || email.split("@")[0];
  };

  const getSupplyContactDisplayNameForDetails = (
    retailer: Retailer,
    email?: string
  ) => {
    if (!email) return retailer?.name || "—";
    return resolveEmailEntityName(email) || email.split("@")[0];
  };

  const getSupplyRetailerForProject = (project: Project) => {
    const retailer = supplyRetailers.find(
      (r) => (r.id || r._id) === project.retailerId
    );
    if (retailer) return retailer;
    return { name: project.externalEntityName || "Supply" } as Retailer;
  };

  const getSupplySnapshotPayload = (project: Project) => {
    try {
      return project.metadata?.snapshotPayload
        ? JSON.parse(project.metadata.snapshotPayload)
        : null;
    } catch {
      return null;
    }
  };

  const resolveOrderReferenceFromTracking = (reference?: string | null) => {
    if (!reference) return reference || null;
    const normalized = String(reference).trim();
    if (!normalized) return reference || null;
    const matched = purchaseOrders.find((po) => {
      const tracking = String(po.trackingNumber || "").trim();
      if (tracking && tracking === normalized) return true;
      const parcelTracking = (po.parcels || []).some(
        (parcel) => String(parcel.trackingNumber || "").trim() === normalized
      );
      return parcelTracking;
    });
    return matched?.reference || reference || null;
  };

  const resolveEmailEntityName = (rawEmail?: string): string | undefined => {
    const email = String(rawEmail || "").toLowerCase();
    if (!email) return undefined;
    const domain = email.split("@")[1] || "";

    const supplierMatch = supplierList.find((supplier) => {
      const emails = [supplier.email, supplier.address?.email]
        .filter(Boolean)
        .map((entry) => String(entry).toLowerCase());
      return emails.some((entry) => entry === email || entry.endsWith(`@${domain}`));
    });
    if (supplierMatch?.name) return supplierMatch.name;

    const storageMatch = storages.find((storage) => {
      const storageEmail = String(storage.email || "").toLowerCase();
      return storageEmail && (storageEmail === email || storageEmail.endsWith(`@${domain}`));
    });
    if (storageMatch?.name) return storageMatch.name;

    const retailerMatch = retailerList.find((retailer) => {
      const contacts = Array.isArray(retailer.contacts) ? retailer.contacts : [];
      const domains = new Set(
        contacts
          .map((contact) => String(contact?.email || "").toLowerCase().split("@")[1])
          .filter(Boolean)
      );
      return domains.has(domain);
    });
    return retailerMatch?.name;
  };

  const fetchSupplyInboxAlerts = async (existingSupplyProjects: Project[] = []) => {
    setSupplyInboxLoading(true);
    dispatch(pruneSupplyNotifications());
    try {
      const messages = await purchaseOrderService.fetchSupplyInboxMessages(50);
      const now = Date.now();
      const readIds = (messages || [])
        .filter((msg: any) => msg?.id && msg?.isRead === true)
        .map((msg: any) => String(msg.id));
      if (readIds.length) {
        dispatch(removeSupplyNotifications(readIds));
      }
      const indexedIds = new Set(supplyNotifications.map((item) => item.id));
      const unread = (messages || []).filter((msg: any) => msg && msg.isRead === false);
      const recentUnread = unread.filter((msg: any) => {
        if (!msg?.id) return false;
        if (indexedIds.has(String(msg.id))) return false;
        if (!msg?.receivedDateTime) return true;
        const receivedAt = new Date(msg.receivedDateTime).getTime();
        if (Number.isNaN(receivedAt)) return true;
        return now - receivedAt <= 14 * 24 * 60 * 60 * 1000;
      });

      if (!recentUnread.length) {
        return;
      }

      const classified = await purchaseOrderService.classifySupplyInboxMessages(recentUnread);
      const classifiedById = new Map(
        (classified || []).map((item: any) => [String(item.id), item])
      );
      const notifications = recentUnread.map((msg: any) => {
        const item = classifiedById.get(String(msg.id)) || {};
        const from = typeof msg?.from === "string"
          ? msg.from
          : msg?.from?.emailAddress?.address || msg?.from?.name || "-";
        const resolvedOrderReference = resolveOrderReferenceFromTracking(
          item?.orderReference || null
        );
        return {
          id: String(item.id || msg?.id || ""),
          subject: msg?.subject || "(Sans objet)",
          from,
          receivedDateTime: msg?.receivedDateTime,
          category: item?.category || "other",
          label: item?.label || "Notification",
          orderReference: resolvedOrderReference,
          externalId: item?.externalId || null,
          confidence: item?.confidence,
          summary: item?.summary,
          highlights: Array.isArray(item?.highlights) ? item.highlights : [],
          entityName: resolveEmailEntityName(from),
          addedAt: new Date().toISOString(),
        } as SupplyNotification;
      });

      dispatch(upsertSupplyNotifications(notifications));
      await ensureSupplyProjectsFromNotifications(notifications, existingSupplyProjects);
    } catch (error) {
      console.error("[Supply] Inbox classify error:", error);
    } finally {
      setSupplyInboxLoading(false);
    }
  };

  // Charger les règles d'approvisionnement au premier rendu
  useEffect(() => {
    const bootstrap = async () => {
      fetchSupplyRules();
      fetchPurchaseOrders();
      fetchSuppliers();
      fetchAddresses();
      fetchStorages();
      fetchAllProducts(); // Récupérer les produits pour la gestion des fournisseurs
      const supply = await fetchSupplyProjects();
      await fetchSupplyInboxAlerts(supply);
    };
    bootstrap();
  }, []);

  // Fonctions pour récupérer les entrepôts
  const fetchStorages = async () => {
    try {
      setLoadingStorages(true);
      const data = await getStorages();
      setStorages(data);
      // Après avoir chargé les entrepôts, on peut charger les adresses qui en dépendent
      await fetchAddresses(data);
    } catch (error) {
      console.error("Erreur lors de la récupération des entrepôts:", error);
      message.error("Impossible de charger la liste des entrepôts");
    } finally {
      setLoadingStorages(false);
    }
  };

  const handleSaveStorage = async (values: Partial<Storage>) => {
    try {
      setLoadingStorages(true);
      const mappedColumns = new Set(
        Object.values(values.stockImportTemplate?.mapping || {}).filter(Boolean)
      );
      const payload = values.stockImportTemplate
        ? {
            ...values,
            stockImportTemplate: {
              ...values.stockImportTemplate,
              ignoredColumns: (values.stockImportTemplate.ignoredColumns || []).filter(
                (column) => !mappedColumns.has(column)
              ),
            },
          }
        : values;
      if (currentStorage && (currentStorage.id || currentStorage._id)) {
        await updateStorage((currentStorage.id || currentStorage._id)!, payload);
        message.success("Entrepôt mis à jour");
      } else {
        await createStorage(payload);
        message.success("Entrepôt créé");
      }
      fetchStorages();
      setShowStorageFormModal(false);
      storageForm.resetFields();
    } catch (error) {
      console.error("Erreur lors de l'enregistrement de l'entrepôt:", error);
      message.error("Erreur lors de l'enregistrement");
    } finally {
      setLoadingStorages(false);
    }
  };

  const handleStorageTemplateFile = async (file: File) => {
    try {
      setStockTemplateLoading(true);
      const report = await parseStockReportFile(file);
      const preferredSheet =
        report.sheets.find((sheet) => sheet.name.toUpperCase() === "PRODUITS") ||
        report.sheets[0];
      if (!preferredSheet) throw new Error("Aucune feuille exploitable");

      const detectedHeaderRow = preferredSheet.rows.findIndex((row) =>
        row.some((value) => {
          const normalized = String(value || "")
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .toLowerCase();
          return normalized.includes("code article") || normalized.includes("qte en stock");
        })
      ) + 1;
      const headerRow = detectedHeaderRow > 0 ? detectedHeaderRow : 1;
      const headers = getStockReportHeaders(report, preferredSheet.name, headerRow);
      const currentTemplate = storageForm.getFieldValue("stockImportTemplate") || {};
      const mapping = currentTemplate.mapping || {};
      setStockTemplateHeaders(headers);
      storageForm.setFieldsValue({
        stockImportTemplate: {
          ...currentTemplate,
          sheetName: preferredSheet.name,
          headerRow,
          mapping,
          ignoredColumns: currentTemplate.ignoredColumns || [],
          sourceFileName: file.name,
          updatedAt: new Date().toISOString(),
        },
      });
      message.success("Colonnes détectées, configure maintenant chaque champ nécessaire");
    } catch (error: any) {
      message.error(error?.message || "Impossible de lire le fichier");
    } finally {
      setStockTemplateLoading(false);
    }
    return false;
  };

  const handleDeleteStorage = async (id: string) => {
    try {
      await deleteStorage(id);
      message.success("Entrepôt supprimé");
      fetchStorages();
    } catch (error) {
      console.error("Erreur lors de la suppression de l'entrepôt:", error);
      message.error("Erreur lors de la suppression");
    }
  };

  // Fonctions pour récupérer les fournisseurs et adresses
  const fetchSuppliers = async () => {
    try {
      setLoadingSuppliers(true);
      const apiSuppliers = await supplierApi.getAllSuppliers();
      // Mapper les fournisseurs de l'API vers l'interface locale
      const mappedSuppliers: Supplier[] = apiSuppliers.map((s) => ({
        id: s.id || s._id || "",
        name: s.name,
        country: s.country,
        contactPerson: s.contactPerson,
        email: s.email,
        phone: s.phone,
        address: (s as any).address,
        vendorBrands: s.vendorBrands || [],
        suppliedType:
          s.vendorBrands && s.vendorBrands.length > 0 ? "brands" : "references",
        suppliedItems: s.vendorBrands || [],
        paymentTerms: (s as any).paymentTerms || "",
        tvaRate: (s as any).tvaRate,
        paymentDelayDays: (s as any).paymentDelayDays,
        // compatibilité ascendante: accepter soit carrierId, soit un ancien champ carrier
        carrierId: (s as any).carrierId || (s as any).carrier || undefined,
      }));
      setSupplierList(mappedSuppliers);
    } catch (error) {
      console.error("Erreur lors de la récupération des fournisseurs:", error);
      message.error("Impossible de charger la liste des fournisseurs");
    } finally {
      setLoadingSuppliers(false);
    }
  };

  const fetchAddresses = async (providedStorages?: Storage[]) => {
    try {
      setLoadingAddresses(true);
      const retailers = await getRetailers();
      const destinations: DeliveryAddress[] = [];
      const currentStorages = providedStorages || storages;

      // Ajouter les entrepôts comme destinations possibles
      currentStorages.forEach((storage) => {
        const sId = normalizeId(storage.id || storage._id || "");
        destinations.push({
          id: sId,
          name: `[ENTREPÔT] ${storage.name}`,
          streetAddress: storage.address?.street || "",
          postalCode: storage.address?.postalCode || "",
          city: storage.address?.city || "",
          country: storage.address?.country || "France",
          contactPerson: storage.contactPerson,
          phone: storage.phone,
        });
      });

      // Filtrer pour ne garder que les retailers actifs (pas en onboarding)
      const activeRetailers = retailers.filter(
        (r) => r.crm?.onboardingStage !== "onboarding" && r.status === "active"
      );

      setRetailerList(activeRetailers);
      setSupplyRetailers(activeRetailers);

      activeRetailers.forEach((retailer) => {
        const mode = retailer.logistics?.fulfillmentMode || "hub";
        const retailerName = retailer.name;
        const rId = normalizeId(retailer.id || retailer._id || "");

        if (mode === "hub" && retailer.logistics?.hub) {
          const hub = retailer.logistics.hub;
          destinations.push({
            id: `${rId}-hub`,
            retailerId: rId,
            retailerName,
            fulfillmentMode: mode,
            name: `${retailerName} - ${hub.label || "Hub"}`,
            streetAddress: hub.street || "",
            postalCode: hub.postalCode || "",
            city: hub.city || "",
            country: hub.country || "",
          });
        }

        if (mode === "multi_hub" && retailer.logistics?.hubs) {
          retailer.logistics.hubs.forEach((hub, index) => {
            destinations.push({
              id: `${rId}-hub-${index}`,
              retailerId: rId,
              retailerName,
              fulfillmentMode: mode,
              name: hub.label || `${retailerName} - Hub ${index + 1}`,
              streetAddress: hub.address?.street || "",
              postalCode: hub.address?.postalCode || "",
              city: hub.address?.city || "",
              country: hub.address?.country || "",
              channelKey: hub.channelKeys?.[0],
            });
          });
        }

        if (mode === "per_channel" && retailer.channelsDetails) {
          retailer.channelsDetails.forEach((channel, index) => {
            const hub = channel.logistics?.hub;
            const location = hub || channel.location;
            if (!location) return;
            destinations.push({
              id: `${rId}-channel-${index}`,
              retailerId: rId,
              retailerName,
              fulfillmentMode: mode,
              channelKey: channel.key,
              channelType: channel.type,
              name: location.label || `${retailerName} - ${channel.name || channel.key || "Canal"}`,
              streetAddress: location.street || "",
              postalCode: location.postalCode || "",
              city: location.city || "",
              country: location.country || "",
            });
          });
        }
      });

      setAddressList(destinations);
    } catch (error) {
      console.error("Erreur lors de la récupération des destinations:", error);
      message.error("Impossible de charger les destinations");
    } finally {
      setLoadingAddresses(false);
    }
  };

  // Handlers pour les fournisseurs
  const handleSaveSupplier = async (values: Supplier) => {
    try {
      setLoadingSuppliers(true);

      // Récupérer tous les fournisseurs existants pour la gestion de l'unicité
      const existingSuppliers = await supplierApi.getAllSuppliers();

      // Collecter les IDs des produits à associer au supplier
      let productIdsToAssociate: string[] = [];

      if (values.suppliedType === "brands") {
        // Si on associe par marques, récupérer tous les produits de ces marques
        const selectedBrands = values.suppliedItems || [];

        // Pour chaque marque, trouver tous les produits correspondants
        selectedBrands.forEach((brand) => {
          const productsOfBrand = allProducts.filter(
            (product) => product.vendor === brand
          );

          // Ajouter les IDs de ces produits à la liste
          productIdsToAssociate = [
            ...productIdsToAssociate,
            ...productsOfBrand.map((product) => product.id || ""),
          ];
        });

        // Afficher une notification du nombre de produits associés
        if (productIdsToAssociate.length > 0) {
          message.info(
            `${productIdsToAssociate.length} produits de ${selectedBrands.length} marques seront associés à ce fournisseur`
          );
        }
      } else {
        // Si on associe directement par références, utiliser les IDs sélectionnés
        productIdsToAssociate = values.suppliedItems || [];
      }

      // Nettoyer les associations de produits dans tous les autres fournisseurs
      if (productIdsToAssociate.length > 0) {
        // Pour chaque fournisseur existant (sauf celui qu'on édite)
        for (const supplier of existingSuppliers) {
          // S'assurer que l'ID n'est jamais undefined pour la comparaison
          const supplierId = supplier.id || "";
          const currentSupplierId = currentSupplier?.id || "";

          if (supplierId === currentSupplierId || !supplier.vendorBrands) {
            continue; // Ignorer le fournisseur actuel ou ceux sans vendorBrands
          }

          // Filtrer les vendorBrands pour retirer tous les IDs de produits qu'on va associer
          const newVendorBrands = supplier.vendorBrands.filter(
            (item: string) => !productIdsToAssociate.some((id) => id === item)
          );

          // Si on a retiré des éléments, mettre à jour ce fournisseur
          if (newVendorBrands.length !== supplier.vendorBrands.length) {
            await supplierApi.updateSupplier(supplier.id || "", {
              ...supplier,
              vendorBrands: newVendorBrands,
            });
          }
        }
      }

      // Préparer les données du fournisseur avec les bonnes valeurs
      const supplierData: supplierApi.Supplier = {
        ...values,
        vendorBrands: productIdsToAssociate,
        carrierId: values.carrierId,
      };

      if (currentSupplier && currentSupplier.id) {
        // Mise à jour d'un fournisseur existant
        await supplierApi.updateSupplier(currentSupplier.id, supplierData);
        message.success("Fournisseur mis à jour avec succès");
      } else {
        // Création d'un nouveau fournisseur
        await supplierApi.createSupplier(supplierData);
        message.success("Fournisseur créé avec succès");
      }
      // Rafraîchir la liste des fournisseurs
      fetchSuppliers();
      // Réinitialiser le formulaire
      supplierForm.resetFields();
      setCurrentSupplier(null);
    } catch (error) {
      console.error("Erreur lors de l'enregistrement du fournisseur:", error);
      message.error("Impossible d'enregistrer le fournisseur");
    } finally {
      setLoadingSuppliers(false);
    }
  };

  const handleDeleteSupplier = async (id?: string) => {
    if (!id) return;

    try {
      setLoadingSuppliers(true);
      await supplierApi.deleteSupplier(id);
      message.success("Fournisseur supprimé avec succès");
      // Rafraîchir la liste des fournisseurs
      fetchSuppliers();
    } catch (error) {
      console.error("Erreur lors de la suppression du fournisseur:", error);
      message.error("Impossible de supprimer le fournisseur");
    } finally {
      setLoadingSuppliers(false);
    }
  };

  // Récupérer tous les produits et extraire les marques uniques
  const fetchAllProducts = async () => {
    setLoadingProducts(true);
    try {
      const productsData = await getCatalog();
      const formattedProducts = productsData.map((p) => ({
        ...p,
        id: p.id || p._id || p.handle || "",
        title: p.title || "",
        name: p.title || "",
        vendor: p.vendor || "",
        sku: p.Sku || "",
        barcode: p.Barcode ? String(p.Barcode) : "",
        price: typeof p.Price === "number" ? p.Price : Number(p.Price || 0),
        costPerItem: p.costPerItem,
        distributorPrices: p.distributorPrices,
        image: p.imageSrc || undefined,
      })) as Product[];

      const shouldUpdateProducts =
        products.length === 0 ||
        products.every(
          (prod) =>
            !prod.stockByLocation ||
            Object.keys(prod.stockByLocation || {}).length === 0
        );

      if (shouldUpdateProducts) {
        setProducts(formattedProducts);
      }
      setAllProducts(formattedProducts);

      const uniqueVendors = [
        ...new Set(formattedProducts.map((product) => product.vendor).filter(Boolean)),
      ] as string[];
      setAvailableVendors(uniqueVendors);

      // Précharger les images des produits pour améliorer l'UX
      formattedProducts.forEach((product) => {
        const imageSrc = product.image || product.imageSrc;
        if (imageSrc) {
          const img = document.createElement('img');
          img.src = imageSrc;
        }
      });

      return formattedProducts;
    } catch (error) {
      message.error("Impossible de charger les produits et marques");
      return [];
    } finally {
      setLoadingProducts(false);
    }
  };


  // Récupérer les règles d'approvisionnement depuis l'API
  const fetchSupplyRules = async () => {
    try {
      setLoading(true);
      const rules = await supplyRuleApi.getAllSupplyRules();
      setSupplyRules(rules);
    } catch (error) {
      console.error(
        "Erreur lors de la récupération des règles d'approvisionnement:",
        error
      );
      message.error("Impossible de charger les règles d'approvisionnement");
    } finally {
      setLoading(false);
    }
  };

  // Créer une nouvelle règle d'approvisionnement
  const handleCreateSupplyRule = async (rule: SupplyRule) => {
    try {
      setLoading(true);
      const newRule = await supplyRuleApi.createSupplyRule(rule);
      setSupplyRules([...supplyRules, newRule]);
      message.success("Règle créée avec succès");
      setCurrentRule(null); // Fermer le formulaire
      return newRule;
    } catch (error) {
      console.error("Erreur lors de la création de la règle:", error);
      message.error("Impossible de créer la règle d'approvisionnement");
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Mettre à jour une règle existante
  const handleUpdateSupplyRule = async (rule: SupplyRule) => {
    if (!rule.id && !rule._id) {
      message.error("ID de règle manquant");
      return null;
    }

    try {
      setLoading(true);
      const ruleId = rule.id || rule._id;
      const updatedRule = await supplyRuleApi.updateSupplyRule(
        ruleId as string,
        rule
      );

      // Mettre à jour la liste des règles
      setSupplyRules(
        supplyRules.map((r) =>
          r.id === ruleId || r._id === ruleId ? updatedRule : r
        )
      );

      message.success("Règle mise à jour avec succès");
      setCurrentRule(null); // Fermer le formulaire
      return updatedRule;
    } catch (error) {
      console.error("Erreur lors de la mise à jour de la règle:", error);
      message.error("Impossible de mettre à jour la règle d'approvisionnement");
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Activer ou désactiver une règle
  const handleToggleSupplyRule = async (rule: SupplyRule) => {
    if (!rule.id && !rule._id) {
      message.error("ID de règle manquant");
      return;
    }

    try {
      setLoading(true);
      const ruleId = rule.id || rule._id;
      const result = await supplyRuleApi.toggleSupplyRule(ruleId as string);

      // Mettre à jour la liste des règles
      setSupplyRules(
        supplyRules.map((r) => {
          if (r.id === ruleId || r._id === ruleId) {
            return { ...r, active: result.active };
          }
          return r;
        })
      );

      message.success(
        `Règle ${result.active ? "activée" : "désactivée"} avec succès`
      );
    } catch (error) {
      console.error(
        `Erreur lors de l'activation/désactivation de la règle:`,
        error
      );
      message.error("Impossible de modifier l'état de la règle");
    } finally {
      setLoading(false);
    }
  };

  // Supprimer une règle
  const handleDeleteSupplyRule = async (rule: SupplyRule) => {
    if (!rule.id && !rule._id) {
      message.error("ID de règle manquant");
      return;
    }

    try {
      setLoading(true);
      const ruleId = rule.id || rule._id;
      await supplyRuleApi.deleteSupplyRule(ruleId as string);

      // Retirer la règle supprimée de la liste
      setSupplyRules(
        supplyRules.filter((r) => r.id !== ruleId && r._id !== ruleId)
      );

      message.success("Règle supprimée avec succès");
    } catch (error) {
      console.error("Erreur lors de la suppression de la règle:", error);
      message.error("Impossible de supprimer la règle");
    } finally {
      setLoading(false);
    }
  };

  // Gérer l'ajout ou la modification d'une règle (pour le formulaire)
  const handleSaveSupplyRule = async (values: SupplyRule) => {
    // Normaliser les données du formulaire en règle d'approvisionnement
    const ruleToSave: SupplyRule = {
      ...values,
      // Ajouter d'autres champs ou transformations si nécessaire
    };

    if (currentRule && (currentRule.id || currentRule._id)) {
      // Mode édition
      await handleUpdateSupplyRule({
        ...ruleToSave,
        id: currentRule.id,
        _id: currentRule._id,
      });
    } else {
      // Mode création
      await handleCreateSupplyRule(ruleToSave);
    }
  };

  // Récupérer les produits du catalogue
  const fetchAllProductsWithLocationStock = async () => {
    try {
      setLoading(true);
      const productsData = await getCatalog();
      const formattedProducts = productsData.map((product) => ({
        ...product,
        id: product.id || product._id || product.handle || "",
        title: product.title || "",
        name: product.title || "",
        currentStock: 0,
        minStock: 5,
        unit: "pièce",
        supplier: product.vendor || "Fournisseur inconnu",
        category: product.type || "Non catégorisé",
        stockByLocation: {},
      })) as Product[];
      setProducts(formattedProducts);
    } catch (error: any) {
      const errorMessage =
        error instanceof Error ? error.message : "Erreur inconnue";
      message.error(`Erreur lors du chargement des produits: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour exporter un bon de commande au format CSV (template enrichi)

  // Récupérer les bons de commande depuis l'API
  const fetchPurchaseOrders = async () => {
    try {
      setLoading(true);

      // Utiliser le service pour récupérer les données depuis l'API
      const orders = await purchaseOrderService.getAllPurchaseOrders();

      // Transformer les données pour correspondre au format attendu par le composant
      const formattedOrders: PurchaseOrder[] = orders.map((order) => ({
        ...order,
        id: order._id, // Utiliser _id comme id pour la compatibilité avec le frontend
        createdAt: order.creationDate
          ? new Date(order.creationDate)
          : new Date(),
        expectedDeliveryDate: order.deliveryDate
          ? new Date(order.deliveryDate)
          : undefined,
      }));

      setPurchaseOrders(formattedOrders);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Erreur inconnue";
      message.error(`Erreur lors du chargement des commandes: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSendPO = async (id: string) => {
    try {
      const key = "sending-po";
      message.loading({ content: "Envoi du bon de commande...", key });

      const result = await purchaseOrderService.sendPurchaseOrderNotification(id);
      const sentCount = result.notifications.sent.length;
      const failedCount = result.notifications.failed.length;

      message.success({
        content:
          sentCount > 0
            ? `Bon de commande envoyé à ${sentCount} destinataire(s)${failedCount ? `, ${failedCount} échec(s)` : ""}`
            : "Statut mis à jour. Aucun destinataire email configuré.",
        key,
        duration: 3,
      });

      // Rafraîchir la liste
      fetchPurchaseOrders();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Erreur inconnue";
      message.error(`Erreur lors de l'envoi : ${errorMessage}`);
    }
  };

  // Mettre à jour le statut d'un bon de commande
  const updateOrderStatus = async (
    id: string | undefined,
    newStatus: string
  ) => {
    // Vérifier que l'ID est défini
    if (!id) {
      message.error("Impossible de mettre à jour le statut: ID manquant");
      return;
    }

    try {
      // Afficher un indicateur de chargement
      const key = "updating-status";
      message.loading({ content: "Mise à jour du statut...", key });

      // Appeler l'API pour mettre à jour le statut
      const validStatus = newStatus as
        | "draft"
        | "sent"
        | "in_delivery"
        | "partially_received"
        | "noted_delivered"
        | "cancelled";
      await purchaseOrderService.updatePurchaseOrderStatus(id, validStatus);

      // Afficher un message de succès
      message.success({
        content: "Statut mis à jour avec succès",
        key,
        duration: 2,
      });

      // Rafraîchir la liste des bons de commande
      fetchPurchaseOrders();
    } catch (error) {
      // Gérer les erreurs
      const errorMessage =
        error instanceof Error ? error.message : "Erreur inconnue";
      message.error(`Erreur lors de la mise à jour du statut: ${errorMessage}`);
    }
  };

  // Supprimer un bon de commande
  const handleDeletePurchaseOrder = async (id: string | undefined) => {
    // Vérifier que l'ID est défini
    if (!id) {
      message.error("Impossible de supprimer: ID manquant");
      return;
    }

    try {
      // Demander confirmation avant de supprimer
      Modal.confirm({
        title: "Supprimer ce bon de commande ?",
        content:
          "Cette action est irréversible. Le bon de commande sera définitivement supprimé.",
        okText: "Supprimer",
        okType: "danger",
        cancelText: "Annuler",
        onOk: async () => {
          // Afficher un indicateur de chargement
          const key = "deleting";
          message.loading({ content: "Suppression en cours...", key });

          // Appeler l'API pour supprimer le bon de commande
          await purchaseOrderService.deletePurchaseOrder(id);

          // Afficher un message de succès
          message.success({
            content: "Bon de commande supprimé avec succès",
            key,
            duration: 2,
          });

          // Rafraîchir la liste des bons de commande
          fetchPurchaseOrders();
        },
      });
    } catch (error) {
      // Gérer les erreurs
      const errorMessage =
        error instanceof Error ? error.message : "Erreur inconnue";
      message.error(`Erreur lors de la suppression: ${errorMessage}`);
    }
  };

  // Filtrer les purchase orders selon tous les critères
  const filteredPurchaseOrders = purchaseOrders.filter((po) => {
    // Filtre par texte de recherche
    const matchesSearch =
      po.reference?.toLowerCase().includes(searchText.toLowerCase()) ||
      po.supplier?.toLowerCase().includes(searchText.toLowerCase());

    // Filtre par statut
    const matchesStatus = statusFilter ? po.status === statusFilter : true;

    // Filtre par type (inbound/outbound)
    const matchesType = typeFilter ? po.type === typeFilter : true;

    // Filtre par fournisseur
    const matchesSupplier = !supplierFilter || po.supplier === supplierFilter;

    // Filtre par adresse
    const matchesAddress =
      !addressFilter || po.deliveryAddress?.name === addressFilter;

    // Normaliser la date pour éviter l'erreur TS sur instanceof
    const poDate = po.createdAt ? dayjs(po.createdAt as any).toDate() : null;

    // Filtre par date de début
    const matchesStartDate =
      !startDateFilter ||
      (poDate !== null && startDateFilter.toDate() <= poDate);

    // Filtre par date de fin
    const matchesEndDate =
      !endDateFilter || (poDate !== null && endDateFilter.toDate() >= poDate);

    // Tous les critères doivent être satisfaits
    return (
      matchesSearch &&
      matchesStatus &&
      matchesType &&
      matchesSupplier &&
      matchesAddress &&
      matchesStartDate &&
      matchesEndDate
    );
  });

  const notificationContent = (
    <div style={{ width: 380, maxHeight: 460, overflowY: "auto", padding: 12, background: "#f8fafc" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <Typography.Text strong style={{ fontSize: 13, letterSpacing: "0.05em" }}>
          CENTRE DE NOTIFICATIONS
        </Typography.Text>
        <Tag color="geekblue" style={{ borderRadius: 999, padding: "0 10px" }}>
          {supplyNotifications.length}
        </Tag>
      </div>
      {supplyInboxLoading ? (
        <div style={{ padding: 12 }}>
          <Spin size="small" />
        </div>
      ) : supplyNotifications.length === 0 ? (
        <Empty description="Aucune alerte supply" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <List
          dataSource={supplyNotifications}
          itemLayout="vertical"
          renderItem={(item) => (
            <List.Item
              style={{
                padding: "14px 12px",
                borderRadius: 14,
                background: "#ffffff",
                border: "1px solid #e2e8f0",
                marginBottom: 10,
                boxShadow: "0 8px 20px rgba(15, 23, 42, 0.06)",
              }}
            >
              <Space direction="vertical" size={8} style={{ width: "100%" }}>
                <Space size={8} wrap>
                  <Tag
                    color={
                      item.category === "new_order"
                        ? "green"
                        : item.category === "delivery_issue"
                          ? "volcano"
                          : item.category === "delivery_followup"
                            ? "geekblue"
                            : "blue"
                    }
                    style={{ borderRadius: 999, display: "inline-flex", alignItems: "center", gap: 6 }}
                  >
                    {item.category === "new_order" ? (
                      <ShoppingCartOutlined />
                    ) : item.category === "delivery_issue" ? (
                      <ClockCircleOutlined />
                    ) : item.category === "delivery_followup" ? (
                      <PlayCircleOutlined />
                    ) : (
                      <InfoCircleOutlined />
                    )}
                    {item.label}
                  </Tag>
                  {item.orderReference && (
                    <Tag color="gold" style={{ borderRadius: 999 }}>
                      {item.orderReference}
                    </Tag>
                  )}
                  {item.entityName && (
                    <Tag style={{ borderRadius: 999, background: "#f1f5f9", color: "#334155", border: "none" }}>
                      {item.entityName}
                    </Tag>
                  )}
                </Space>
                <Typography.Text strong style={{ fontSize: 14, color: "#0f172a" }}>
                  {item.subject}
                </Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {item.from} • {item.receivedDateTime ? dayjs(item.receivedDateTime).format("DD/MM/YYYY HH:mm") : ""}
                </Typography.Text>
                {item.summary && (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {item.summary}
                  </Typography.Text>
                )}
                {item.highlights && item.highlights.length > 0 && (
                  <Space direction="vertical" size={4} style={{ width: "100%" }}>
                    {item.highlights.slice(0, 3).map((highlight, index) => (
                      <Typography.Text key={`${item.id}-h-${index}`} style={{ fontSize: 11, color: "#475569" }}>
                        • {highlight}
                      </Typography.Text>
                    ))}
                  </Space>
                )}
              </Space>
            </List.Item>
          )}
        />
      )}
    </div>
  );

  // Filtrer les produits (recherche + filtres marque & fournisseur)
  const filteredProducts = products.filter((product) => {
    const searchLower = productSearchText.toLowerCase();
    const matchesSearch =
      product.name.toLowerCase().includes(searchLower) ||
      (product.sku || "").toLowerCase().includes(searchLower) ||
      (product.supplier || "").toLowerCase().includes(searchLower) ||
      (product.vendor || "").toLowerCase().includes(searchLower);

    if (!matchesSearch) return false;

    // Filtre par marque (vendor)
    if (productVendorFilter.length > 0) {
      const vendor = (product.vendor || "").trim();
      if (!productVendorFilter.includes(vendor)) return false;
    }

    // Filtre par fournisseur (par ID de fournisseur)
    if (productSupplierFilterIds.length > 0) {
      const includeBySupplier = productSupplierFilterIds.some((sid) => {
        const s = supplierList.find((x) => x.id === sid);
        if (!s) return false;
        // Correspondance directe par nom renseigné sur le produit
        if (product.supplier && product.supplier === s.name) return true;
        // Correspondance par marque ou références fournies
        const suppliedItems =
          s.suppliedItems && s.suppliedItems.length > 0
            ? s.suppliedItems
            : s.vendorBrands || [];
        if (s.suppliedType === "references") {
          return suppliedItems.includes(product.id);
        }
        // Par défaut: matching par marque
        return suppliedItems.includes(product.vendor || "");
      });
      if (!includeBySupplier) return false;
    }

    return true;
  });

  const sortedFilteredProducts = useMemo(() => {
    return [...filteredProducts].sort((a, b) => {
      if (selectedProducts[a.id] && !selectedProducts[b.id]) return -1;
      if (!selectedProducts[a.id] && selectedProducts[b.id]) return 1;
      return 0;
    });
  }, [filteredProducts, selectedProducts]);

  const visibleProducts = useMemo(
    () => sortedFilteredProducts.slice(0, visibleProductCount),
    [sortedFilteredProducts, visibleProductCount]
  );

  useEffect(() => {
    setVisibleProductCount(50);
  }, [productSearchText, productVendorFilter, productSupplierFilterIds]);

  // Vérifie que les produits sont enrichis avec stockByLocation
  const ensureProductsHaveLocationStock = async () => {
    try {
      // Toujours ré-enrichir pour garantir que les produits n'ont pas d'entrées 0 artificielles
      await fetchAllProductsWithLocationStock();
    } catch (e) {
      message.warning("Chargement des stocks par emplacement échoué");
    }
  };

  // Ouvrir le tiroir de création de PO
  const showCreateDrawer = async (
    preselectedProducts?: RecommendedProduct[]
  ) => {
    // Réinitialiser la recherche produit pour afficher toute la liste
    setProductSearchText("");
    setProductVendorFilter([]);
    setProductSupplierFilterIds([]);

    setCreateDrawerVisible(true);
    setFlowType("inbound");
    setCurrentStep(0);
    // S'assurer d'avoir les colonnes d'emplacements prêtes
    await ensureProductsHaveLocationStock();
    resetPOForm(preselectedProducts);
  };

  // Réinitialiser le formulaire de création de PO et présélectionner les produits recommandés
  const resetPOForm = (preselectedProducts?: RecommendedProduct[]) => {
    // Préselectionner les produits du bon de commande depuis recommendedProducts
    const newSelectedProducts: { [key: string]: boolean } = {};
    const newProductQuantities: { [key: string]: number } = {};

    // Priorité aux produits passés en paramètre (depuis la modale "Conseillé")
    const listToUse =
      preselectedProducts && preselectedProducts.length > 0
        ? preselectedProducts
        : recommendedProducts;

    if (listToUse.length > 0) {
      listToUse.forEach((product) => {
        // Présélectionner ce produit
        newSelectedProducts[product.productId] = true;

        const totalQty = product.locationStocks.reduce(
          (sum, locationStock) => sum + (locationStock.stock || 0),
          0
        );
        if (totalQty > 0) {
          newProductQuantities[product.productId] = totalQty;
        }
      });

      // Mettre à jour les sélections et quantités
      setSelectedProducts(newSelectedProducts);
      setProductQuantities(newProductQuantities);
    } else {
      // Si aucun produit recommandé, réinitialiser proprement
      setSelectedProducts({});
      setProductQuantities({});
    }

    // Réinitialiser le bon de commande avec les items vides
    // Les quantités seront appliquées automatiquement depuis locationQuantities
    setNewPO({
      items: [],
      status: "draft",
      totalAmount: 0,
    });

    poForm.resetFields();
  };

  // Fonction pour collecter toutes les quantités par emplacement lors de la création du bon de commande
  const collectOrderItems = () => {
    const orderItems: PurchaseOrderItem[] = [];
    const formData = poForm.getFieldsValue(true);
    const deliveryAddressId = formData.deliveryAddressId;
    const currentFlowType = formData.flowType || flowType; // Utiliser la valeur du form en priorité
    
    console.log("🔍 collectOrderItems called:");
    console.log("  - currentFlowType:", currentFlowType);
    console.log("  - deliveryAddressId:", deliveryAddressId);
    console.log("  - formData:", formData);
    
    // Pour l'expédition, déterminer le retailer choisi (ou fallback depuis l'adresse)
    let selectedRetailerId = normalizeId(formData.retailerId || "");
    let selectedRetailerName = supplyRetailers.find(
      (r) => normalizeId(r.id || r._id) === selectedRetailerId
    )?.name || "";
    let selectedHub: DeliveryAddress | undefined;
    if (currentFlowType === "outbound" && deliveryAddressId) {
      console.log("Looking for deliveryAddressId:", deliveryAddressId);
      console.log("AddressList content:", addressList.map(a => ({ id: a.id, name: a.name, retailerId: a.retailerId })));
      
      const addr = addressList.find(a => normalizeId(a.id) === normalizeId(deliveryAddressId));
      selectedHub = addr;
      selectedRetailerName = selectedRetailerName || addr?.retailerName || "";
      console.log("Found address:", addr);
      console.log("Outbound hub selected:", {
        hubId: addr?.id,
        hubName: addr?.name,
        fulfillmentMode: addr?.fulfillmentMode,
        channelKey: addr?.channelKey,
      });
      console.log("Outbound hub retailer:", {
        retailerId: addr?.retailerId,
        retailerName: addr?.retailerName,
      });
      
      if (!selectedRetailerId && addr?.retailerId) {
        selectedRetailerId = normalizeId(addr.retailerId);
        console.log("✓ Extracted retailerId:", selectedRetailerId);
      } else if (!selectedRetailerId && !addr?.retailerId) {
        console.error("✗ No retailerId found in address:", addr);
      }
    } else {
      console.warn("⚠️ Condition not met for retailerId extraction:", {
        currentFlowType,
        deliveryAddressId,
        isOutbound: currentFlowType === "outbound",
        hasDeliveryAddress: !!deliveryAddressId
      });
    }

    // Parcourir tous les produits sélectionnés
    Object.keys(selectedProducts)
      .filter((id) => selectedProducts[id])
      .forEach((productId) => {
        const product = products.find((p) => p.id === productId);
        if (!product) return;

        const quantity = productQuantities[productId] || 0;
        if (quantity <= 0) return;

        // Déterminer le prix unitaire selon le flux
        let unitPrice = 0;
        if (currentFlowType === "inbound") {
          // Arrivage : Utiliser le coût (costPerItem)
          unitPrice = product.costPerItem || 0;
          console.log(`Inbound mode - Using cost: ${unitPrice}€`);
        } else {
          // Expédition : Utiliser le prix distributeur spécifique ou le prix catalogue par défaut
          if (selectedRetailerId && product.distributorPrices) {
            console.log(`Product ${product.name} - Looking for retailer:`, selectedRetailerId);
            console.log("Available distributorPrices:", product.distributorPrices.map(dp => ({
              id: normalizeId(dp.distributorId),
              name: dp.distributorName,
              price: dp.price
            })));
            console.log("Outbound pricing context:", {
              hubId: selectedHub?.id,
              hubName: selectedHub?.name,
              retailerId: selectedRetailerId,
              retailerName: selectedRetailerName,
              productId: product.id,
              productName: product.name,
            });
            
            const distPriceById = product.distributorPrices.find(
              (dp) => normalizeId(dp.distributorId) === selectedRetailerId
            );
            const distPriceByName =
              !distPriceById && selectedRetailerName
                ? product.distributorPrices.find(
                    (dp) =>
                      String(dp.distributorName || "").trim().toLowerCase() ===
                      selectedRetailerName.trim().toLowerCase()
                  )
                : undefined;
            const distPrice = distPriceById || distPriceByName;
            console.log("Distributor price match:", {
              matchedBy: distPriceById ? "id" : distPriceByName ? "name" : "none",
              price: distPrice?.price,
            });
            
            if (distPrice) {
              unitPrice = distPrice.price;
              console.log(`✓ Found distributor price: ${distPrice.price}€ for ${distPrice.distributorName}`);
            } else {
              unitPrice = product.price || 0;
              console.log(`✗ No distributor price found, using retail: ${unitPrice}€`);
            }
          } else {
            unitPrice = product.price || 0;
            console.log(`Using default retail price: ${unitPrice}€ (selectedRetailerId: ${selectedRetailerId}, has distributorPrices: ${!!product.distributorPrices})`);
          }
        }

        // Trouver le fournisseur associé au produit
        let supplierName = "N/A";

        // 1. Vérifier si l'ID produit est dans vendorBrands d'un fournisseur
        if (product.id) {
          const supplierByProductId = supplierList.find(
            (s) => s.vendorBrands && product.id && s.vendorBrands.includes(product.id)
          );
          if (supplierByProductId) {
            supplierName = supplierByProductId.name;
          } else {
            const supplierByVendor = supplierList.find(
              (s) =>
                product.vendor &&
                s.vendorBrands &&
                s.vendorBrands.some(
                  (brand) => brand.toUpperCase() === product.vendor?.toUpperCase()
                )
            );
            if (supplierByVendor) {
              supplierName = supplierByVendor.name;
            } else {
              supplierName = product.vendor || product.supplier || "N/A";
            }
          }
        }

        const supplierUnitPrice = product.costPerItem || 0;
        orderItems.push({
          productId: product.id || "",
          productName: product.name,
          vendor: supplierName,
          sku: product.sku || "",
          quantity,
          unit: product.unit || "qte.",
          unitPrice: unitPrice,
          totalPrice: quantity * unitPrice,
          supplierUnitPrice,
          supplierTotalPrice: quantity * supplierUnitPrice,
          unitWeightKg: (product as any).weightKg,
          hsCode: (product as any).hsCode,
          madeIn: (product as any).originCountry,
          image: (product as any).image || product.imageSrc,
        });
      });

    return orderItems;
  };

  // Helper pour résoudre une adresse à partir d'un ID
  const resolveAddress = (id: string): DeliveryAddress | undefined => {
    const sId = normalizeId(id);
    if (!sId) return undefined;

    // 1. Chercher dans addressList (contient retailers ET storages)
    const fromList = addressList.find(a => normalizeId(a.id) === sId);
    if (fromList) {
      if (fromList.name?.startsWith("[ENTREPÔT] ")) {
        return {
          ...fromList,
          name: fromList.name.replace(/^\[ENTREPÔT\]\s*/, ""),
        };
      }
      return fromList;
    }

    // 2. Fallback direct dans storages si non trouvé dans addressList
    const storage = storages.find(s => normalizeId(s.id || s._id) === sId);
    if (storage) {
      return {
        id: normalizeId(storage.id || storage._id || ""),
        name: storage.name,
        streetAddress: storage.address?.street || "",
        postalCode: storage.address?.postalCode || "",
        city: storage.address?.city || "",
        country: storage.address?.country || "France",
      };
    }

    return undefined;
  };

  const buildOutboundFallbackAddress = (
    retailerId?: string
  ): DeliveryAddress | undefined => {
    if (!retailerId) return undefined;
    const retailer = supplyRetailers.find(
      (r) => normalizeId(r.id || r._id) === normalizeId(retailerId)
    );
    if (!retailer) return undefined;

    const mode = retailer.logistics?.fulfillmentMode || "hub";
    const retailerName = retailer.name;
    const rId = normalizeId(retailer.id || retailer._id || "");

    if (mode === "hub" && retailer.logistics?.hub) {
      const hub = retailer.logistics.hub;
      return {
        id: `${rId}-hub`,
        retailerId: rId,
        retailerName,
        fulfillmentMode: mode,
        name: `${retailerName} - ${hub.label || "Hub"}`,
        streetAddress: hub.street || "",
        postalCode: hub.postalCode || "",
        city: hub.city || "",
        country: hub.country || "",
      };
    }

    if (mode === "multi_hub" && retailer.logistics?.hubs?.length) {
      const hub = retailer.logistics.hubs[0];
      return {
        id: `${rId}-hub-0`,
        retailerId: rId,
        retailerName,
        fulfillmentMode: mode,
        name: hub.label || `${retailerName} - Hub 1`,
        streetAddress: hub.address?.street || "",
        postalCode: hub.address?.postalCode || "",
        city: hub.address?.city || "",
        country: hub.address?.country || "",
        channelKey: hub.channelKeys?.[0],
      };
    }

    if (mode === "per_channel" && retailer.channelsDetails?.length) {
      const channel = retailer.channelsDetails.find(
        (entry) => entry.logistics?.hub || entry.location
      );
      if (!channel) return undefined;
      const location = channel.logistics?.hub || channel.location;
      if (!location) return undefined;
      return {
        id: `${rId}-channel-${channel.key || 0}`,
        retailerId: rId,
        retailerName,
        fulfillmentMode: mode,
        channelKey: channel.key,
        channelType: channel.type,
        name: location.label || `${retailerName} - ${channel.name || channel.key || "Canal"}`,
        streetAddress: location.street || "",
        postalCode: location.postalCode || "",
        city: location.city || "",
        country: location.country || "",
      };
    }

    return undefined;
  };

  // Soumettre les nouvelles purchase orders
  const handleCreatePO = async () => {
    try {
      const fieldsValue = poForm.getFieldsValue(true);
      console.log("All form fields:", fieldsValue);
      
      // Valider tous les champs requis
      await poForm.validateFields();
      
      const formData = fieldsValue;
      const fallbackDeliveryAddressId =
        (formData.flowType || flowType) === "outbound" && outboundDestinations.length === 1
          ? String(outboundDestinations[0]?.id || "")
          : "";
      const fallbackDeliveryAddressName =
        (formData.flowType || flowType) === "outbound" && outboundDestinations.length === 1
          ? String(outboundDestinations[0]?.name || "")
          : "";
      let deliveryAddressId = String(
        formData.deliveryAddressId || fallbackDeliveryAddressId || ""
      ).trim();
      if (!formData.deliveryAddressName && fallbackDeliveryAddressName) {
        formData.deliveryAddressName = fallbackDeliveryAddressName;
      }

      const fallbackOutboundAddress =
        !deliveryAddressId && (formData.flowType || flowType) === "outbound"
          ? buildOutboundFallbackAddress(formData.retailerId)
          : undefined;

      if (fallbackOutboundAddress) {
        deliveryAddressId = fallbackOutboundAddress.id;
        formData.deliveryAddressId = deliveryAddressId;
        formData.deliveryAddressName = fallbackOutboundAddress.name;
        poForm.setFieldsValue({
          deliveryAddressId,
          deliveryAddressName: fallbackOutboundAddress.name,
        });
      }

      if (deliveryAddressId && formData.deliveryAddressId !== deliveryAddressId) {
        formData.deliveryAddressId = deliveryAddressId;
      }

      if (!deliveryAddressId || deliveryAddressId === "") {
        message.warning("Destination manquante, tentative de création pour diagnostic.");
        console.error("deliveryAddressId manquant:", { formData, deliveryAddressId });
      }

      const items = collectOrderItems();
      if (!items || items.length === 0) {
        message.error("Veuillez sélectionner au moins un produit.");
        return;
      }

      const totalAmount = roundTo2(
        items.reduce((sum, item) => sum + (item.totalPrice || 0), 0)
      );
      setNewPO((prev) => ({
        ...prev,
        items,
        totalAmount,
      }));
      
      let selectedAddress = resolveAddress(deliveryAddressId) || fallbackOutboundAddress;

      if (!selectedAddress) {
        message.warning("Destination non résolue, envoi en cours avec l'ID sélectionné.");
        selectedAddress = {
          id: deliveryAddressId,
          name: formData.deliveryAddressName || "Destination",
          streetAddress: "",
          postalCode: "",
          city: "",
          country: "France",
        };
      }

      await executeCreatePOs(formData, selectedAddress, items);
    } catch (error: any) {
      if (error?.errorFields?.length) {
        message.error("Veuillez remplir tous les champs obligatoires.");
        return;
      }
      console.error("Erreur lors de la création du bon de commande:", error);
      message.error("Erreur lors de la création du bon de commande");
    }
  };

  const executeCreatePOs = async (
    formData: any,
    selectedAddress: DeliveryAddress,
    items: PurchaseOrderItem[]
  ) => {
    try {
      const effectiveFlowType = formData.flowType || flowType;
      const groupedBySupplier: Record<string, PurchaseOrderItem[]> = {};

      (items || []).forEach((item) => {
        // Toujours grouper par fournisseur: un PO = un fournisseur.
        const groupKey = item.vendor || "N/A";
        if (!groupedBySupplier[groupKey]) {
          groupedBySupplier[groupKey] = [];
        }
        groupedBySupplier[groupKey].push(item);
      });

      const createdPOsList: PurchaseOrder[] = [];
      let poCount = purchaseOrders.length;
      Object.entries(groupedBySupplier).forEach(([groupName, items]) => {
        if (!items.length) return;
        poCount += 1;
        const totalAmountRaw = items.reduce(
          (sum, item) => sum + (item.totalPrice || 0),
          0
        );
        const totalAmount = roundTo2(totalAmountRaw);

        // Déterminer la source (fournisseur pour arrivage, entrepôt pour expédition)
        let resolvedSourceId = "";
        let resolvedSourceName = "";

        if (effectiveFlowType === "inbound") {
          const normalizedGroupName = String(groupName || "").trim().toLowerCase();
          const supplierFromGroup = supplierList.find(
            (x) => String(x.name || "").trim().toLowerCase() === normalizedGroupName
          );
          const supplierFromForm = supplierList.find(
            (x) => String(x.id) === String(formData.supplierId)
          );
          const resolvedSupplier = supplierFromGroup || supplierFromForm;
          resolvedSourceId = String(resolvedSupplier?.id || "");
          resolvedSourceName = resolvedSupplier?.name || groupName;
        } else {
          const normalizedGroupName = String(groupName || "").trim().toLowerCase();
          const supplierFromGroup = supplierList.find(
            (x) => String(x.name || "").trim().toLowerCase() === normalizedGroupName
          );
          const s = storages.find(x => String(x.id || x._id) === String(formData.sourceLocationId));
          resolvedSourceId = String(supplierFromGroup?.id || "");
          resolvedSourceName = supplierFromGroup?.name || groupName;
          if (!resolvedSourceId) {
            message.error(`Fournisseur introuvable pour le groupe "${groupName}". Vérifiez l'association produit/fournisseur.`);
            return;
          }
        }

        const resolvedLocationName =
          effectiveFlowType === "inbound"
            ? selectedAddress.name
            : selectedAddress.name ||
              storages.find(
                (storage) =>
                  normalizeId(storage.id || storage._id) ===
                  normalizeId(formData.sourceLocationId)
              )?.name || "";

        const locationId =
          effectiveFlowType === "inbound"
            ? normalizeId(selectedAddress.id)
            : normalizeId(formData.deliveryAddressId || selectedAddress.id);
        const deliveryAddressObjectId = isObjectId(normalizeId(selectedAddress.id))
          ? normalizeId(selectedAddress.id)
          : undefined;

        createdPOsList.push({
          id: `po${poCount}`,
          reference: `PO-2025-${String(poCount).padStart(3, "0")}`,
          supplier: resolvedSourceName,
          supplierId: resolvedSourceId || undefined,
          warehouseId: effectiveFlowType === "outbound" ? normalizeId(formData.sourceLocationId) : undefined,
          type: effectiveFlowType,
          sourceLocationId:
            effectiveFlowType === "outbound"
              ? normalizeId(formData.sourceLocationId)
              : undefined,
          sourceLocationName:
            effectiveFlowType === "outbound"
              ? storages.find(x => String(x.id || x._id) === String(formData.sourceLocationId))?.name || "Entrepôt Inconnu"
              : undefined,
          createdAt: new Date(),
          status: "draft",
          orderDate: formData.orderDate ? dayjs(formData.orderDate).toDate() : undefined,
          expectedDeliveryDate: formData.expectedDeliveryDate ? dayjs(formData.expectedDeliveryDate).toDate() : undefined,
          items,
          totalAmount,
          notes: formData.notes,
          retailerId: formData.retailerId || selectedAddress.retailerId,
          retailerName: supplyRetailers.find(r => (r.id || r._id) === (formData.retailerId || selectedAddress.retailerId))?.name,
          locationName: resolvedLocationName || "",
          locationId: locationId || "",
          deliveryAddressId: deliveryAddressObjectId,
          deliveryAddress: {
            name: selectedAddress.name || "",
            street: selectedAddress.streetAddress || "",
            city: selectedAddress.city || "",
            postalCode: selectedAddress.postalCode || "",
            country: selectedAddress.country || "",
            deliveryInfo: selectedAddress.deliveryInfo || "",
            comment: selectedAddress.comment || "",
          },
          deliveryStreet: selectedAddress.streetAddress,
          deliveryCity: `${selectedAddress.postalCode} ${selectedAddress.city}`,
          deliveryCountry: selectedAddress.country || "",
        });
      });

      // Envoyer les bons de commande à l'API
      if (createdPOsList.length > 0) {
        const loadingKey = "loading-po";
        message.loading({ content: "Enregistrement des bons de commande...", key: loadingKey, duration: 0 });

        let successCount = 0;
        let errorCount = 0;

        const savePromises = createdPOsList.map(async (po) => {
          try {
            const apiPurchaseOrder: purchaseOrderService.PurchaseOrder = {
              supplier: po.supplier,
              supplierId: po.supplierId,
              warehouseId: po.warehouseId,
              type: po.type,
              sourceLocationId:
                po.type === "outbound" ? normalizeId(formData.sourceLocationId) : po.sourceLocationId,
              sourceLocationName: po.sourceLocationName,
              items: po.items.map((item) => {
                const productInfo = allProducts.find((p) => String(p.id) === String(item.productId));
                return {
                  ...item,
                  productName: item.productName || productInfo?.title || `Produit ${item.productId}`,
                  vendor: item.vendor || productInfo?.vendor || po.supplier,
                  unit: item.unit || "pièce",
                  barcode: productInfo?.barcode || "",
                };
              }),
              totalAmount: roundTo2(po.totalAmount),
              status: "draft" as "draft",
              orderDate: po.orderDate ? new Date(po.orderDate.toString()) : undefined,
              deliveryDate: po.expectedDeliveryDate ? new Date(po.expectedDeliveryDate.toString()) : undefined,
              expectedDeliveryDate: po.expectedDeliveryDate ? new Date(po.expectedDeliveryDate.toString()) : undefined,
              creationDate: new Date(),
              locationName: po.locationName,
              locationId: po.locationId,
              retailerId: po.retailerId,
              retailerName: po.retailerName,
              deliveryAddressId: po.deliveryAddressId,
              deliveryAddress: po.deliveryAddress,
              notes: po.notes || "",
            };

            const savedPO = await purchaseOrderService.createPurchaseOrder(apiPurchaseOrder);
            successCount++;
            return savedPO;
          } catch (error) {
            console.error(`Erreur lors de l'enregistrement du bon de commande:`, error);
            errorCount++;
            throw error;
          }
        });

        const results = await Promise.allSettled(savePromises);
        message.destroy(loadingKey);

        const savedPOs = results
          .filter((r): r is PromiseFulfilledResult<purchaseOrderService.PurchaseOrder> => r.status === "fulfilled")
          .map((r) => r.value);

        if (savedPOs.length > 0) {
          setPurchaseOrders([...savedPOs, ...purchaseOrders]);
          message.success(`${successCount} bon(s) de commande créé(s) avec succès`);
          setCreateDrawerVisible(false);
          resetPOForm();
        }

        if (errorCount > 0) {
          message.error(`${errorCount} bon(s) de commande n'a pas pu être créé`);
        }
      } else {
        message.warning("Aucun bon de commande créé. Vérifiez votre sélection.");
      }
    } catch (error) {
      console.error("Error in executeCreatePOs:", error);
      message.error("Une erreur est survenue lors de la création.");
    }
  };

  // Annuler la création de PO
  const handleCancel = () => {
    setCreateDrawerVisible(false);
    resetPOForm();
  };

  // Étapes du processus de création
  const steps = [
    {
      title: "Flux & Logistique",
      icon: <SettingOutlined />,
      content: () => (
        <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
          <div className="neo-form-section" style={{ marginBottom: 20 }}>
            <Typography.Text strong className="section-title" style={{ display: 'block', marginBottom: 12, color: '#64748b', fontSize: 12, textTransform: 'uppercase' }}>TYPE DE FLUX</Typography.Text>
            <Form.Item name="flowType" initialValue="inbound" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
              <Radio.Group 
                onChange={(e) => {
                  const newType = e.target.value;
                  setFlowType(newType);
                  poForm.setFieldsValue({ 
                    deliveryAddressId: undefined,
                    supplierId: undefined,
                    sourceLocationId: undefined,
                    retailerId: undefined,
                  });
                }} 
                value={flowType} 
                className="neo-radio-group"
                style={{ width: '100%', display: 'flex', gap: 12 }}
              >
                <Radio.Button value="inbound" style={{ flex: 1, height: 'auto', padding: '12px', borderRadius: 12 }}>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: '#eff6ff', color: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><ShoppingCartOutlined /></div>
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>Arrivage</div>
                      <div style={{ fontSize: 11, color: '#64748b' }}>Fournisseur → Entrepôt</div>
                    </div>
                  </div>
                </Radio.Button>
                <Radio.Button value="outbound" style={{ flex: 1, height: 'auto', padding: '12px', borderRadius: 12 }}>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: '#fff7ed', color: '#f97316', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><PlayCircleOutlined /></div>
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>Expédition</div>
                      <div style={{ fontSize: 11, color: '#64748b' }}>Entrepôt → Client/Magasin</div>
                    </div>
                  </div>
                </Radio.Button>
              </Radio.Group>
            </Form.Item>
          </div>

          <Row gutter={20} style={{ marginBottom: 20 }}>
            <Col span={12}>
              <div className="neo-form-section">
                <Typography.Text strong className="section-title" style={{ display: 'block', marginBottom: 12, color: '#64748b', fontSize: 12, textTransform: 'uppercase' }}>ORIGINE</Typography.Text>
                {flowType === "inbound" ? (
                  <Form.Item name="supplierId" style={{ marginBottom: 0 }}>
                    <Select 
                      placeholder="Filtrer sur un fournisseur (optionnel)" 
                      className="neo-select"
                      size="large"
                      style={{ borderRadius: 10 }}
                      onChange={(val) => {
                        setProductSupplierFilterIds(val ? [String(val)] : []);
                      }}
                    >
                      {supplierList.map(s => (
                        <Select.Option key={String(s.id)} value={String(s.id)}>{s.name}</Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                ) : (
                  <Form.Item name="sourceLocationId" rules={[{ required: true, message: "Sélectionnez un entrepôt de départ" }]} style={{ marginBottom: 0 }}>
                    <Select placeholder="Choisir l'entrepôt source" className="neo-select" size="large" style={{ borderRadius: 10 }}>
                      {storages.map(s => (
                        <Select.Option key={normalizeId(s.id || s._id)} value={normalizeId(s.id || s._id)}>{s.name}</Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                )}
              </div>
            </Col>
            <Col span={12}>
              <div className="neo-form-section">
                <Typography.Text strong className="section-title" style={{ display: 'block', marginBottom: 12, color: '#64748b', fontSize: 12, textTransform: 'uppercase' }}>DESTINATION</Typography.Text>
                {flowType === "outbound" && (
                  <Form.Item
                    name="retailerId"
                    rules={[{ required: true, message: "Sélectionnez un distributeur" }]}
                    style={{ marginBottom: 12 }}
                  >
                    <Select
                      placeholder="Choisir un distributeur"
                      showSearch
                      className="neo-select"
                      size="large"
                      style={{ borderRadius: 10 }}
                      options={supplyRetailers.map(r => ({
                        label: r.name,
                        value: r.id || r._id,
                      }))}
                      filterOption={(input, option) =>
                        (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      onChange={() => {
                        poForm.setFieldsValue({ deliveryAddressId: undefined, deliveryAddressName: undefined });
                      }}
                    />
                  </Form.Item>
                )}
                <Form.Item name="deliveryAddressName" hidden>
                  <Input />
                </Form.Item>
                {flowType === "inbound" || outboundDestinations.length > 1 ? (
                  <Form.Item
                    name="deliveryAddressId"
                    rules={[{ required: true, message: "Sélectionnez une destination" }]}
                    style={{ marginBottom: 0 }}
                  >
                    <Select
                      placeholder="Choisir une destination"
                      className="neo-select"
                      size="large"
                      style={{ borderRadius: 10 }}
                      disabled={flowType === "outbound" && !selectedRetailerId}
                      onChange={(value) => {
                        const selectedDestination =
                          flowType === "inbound"
                            ? storages.find((storage) => normalizeId(storage.id || storage._id) === normalizeId(value))
                            : outboundDestinations.find((addr) => normalizeId(addr.id) === normalizeId(value));
                        poForm.setFieldsValue({
                          deliveryAddressId: normalizeId(value),
                          deliveryAddressName: selectedDestination?.name,
                        });
                      }}
                    >
                      {flowType === "inbound"
                        ? storages.map(s => (
                            <Select.Option key={normalizeId(s.id || s._id)} value={normalizeId(s.id || s._id)}>{s.name}</Select.Option>
                          ))
                        : outboundDestinations.map(addr => (
                            <Select.Option key={normalizeId(addr.id)} value={normalizeId(addr.id)}>{addr.name}</Select.Option>
                          ))
                      }
                    </Select>
                  </Form.Item>
                ) : (
                  <>
                    <Form.Item name="deliveryAddressId" style={{ display: "none" }}>
                      <Input />
                    </Form.Item>
                    <Input
                      disabled
                      value={outboundDestinations[0]?.name || "Aucune destination"}
                      placeholder="Destination"
                    />
                  </>
                )}
              </div>
            </Col>
          </Row>

          <Row gutter={20} style={{ marginBottom: 20 }}>
            <Col span={12}>
              <div className="neo-form-section">
                <Typography.Text strong className="section-title" style={{ display: 'block', marginBottom: 12, color: '#64748b', fontSize: 12, textTransform: 'uppercase' }}>PLANIFICATION</Typography.Text>
                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item label={<span style={{ fontSize: 11, color: '#64748b' }}>Date du bon</span>} name="orderDate" style={{ marginBottom: 0 }}>
                      <DatePicker style={{ width: "100%", borderRadius: 8 }} className="neo-datepicker" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label={<span style={{ fontSize: 11, color: '#64748b' }}>Livraison estimée</span>} name="expectedDeliveryDate" style={{ marginBottom: 0 }}>
                      <DatePicker style={{ width: "100%", borderRadius: 8 }} className="neo-datepicker" />
                    </Form.Item>
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>
        </div>
      ),
    },
    {
      title: "Catalogue Produits",
      icon: <ShoppingCartOutlined />,
      content: () => (
        <div
          className="neo-product-selection"
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden',
            padding: '20px 28px',
            gap: 14,
            background: '#f8fafc',
          }}
        >
          <div
            className="selection-header"
            style={{
              padding: '16px 18px',
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              borderRadius: 14,
              flexShrink: 0,
              boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
            }}
          >
            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
              <div className="search-bar-wrapper" style={{ flex: 1 }}>
                <Input
                  placeholder="Rechercher des produits (SKU, nom...)"
                  prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
                  className="neo-search-input"
                  size="large"
                  style={{ borderRadius: 10 }}
                  value={productSearchText}
                  onChange={(e) => setProductSearchText(e.target.value)}
                  suffix={
                    productSearchText ? (
                      <CloseCircleOutlined
                        onClick={() => setProductSearchText("")}
                        style={{ cursor: "pointer", color: '#94a3b8' }}
                      />
                    ) : null
                  }
                />
              </div>
              <div className="filters-wrapper" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <Space size={12}>
                  <Select
                    mode="multiple"
                    allowClear
                    placeholder="Marques"
                    className="neo-select-compact"
                    style={{ minWidth: 160, borderRadius: 8 }}
                    value={productVendorFilter.length ? productVendorFilter : undefined}
                    onChange={(vals) => setProductVendorFilter(vals)}
                  >
                    {availableVendors.map((vendor) => (
                      <Select.Option key={vendor} value={vendor}>{vendor}</Select.Option>
                    ))}
                  </Select>

                  <Select
                    mode="multiple"
                    allowClear
                    placeholder="Fournisseurs"
                    className="neo-select-compact"
                    style={{ minWidth: 200, borderRadius: 8 }}
                    value={productSupplierFilterIds.length ? productSupplierFilterIds : undefined}
                    onChange={(vals) => setProductSupplierFilterIds(vals)}
                  >
                    {supplierList.map((s) => (
                      <Select.Option key={s.id} value={s.id}>{s.name}</Select.Option>
                    ))}
                  </Select>

                  <Button
                    icon={<ReloadOutlined />}
                    className="neo-btn-icon"
                    onClick={() => {
                      setProductVendorFilter([]);
                      setProductSupplierFilterIds([]);
                    }}
                  />
                </Space>
              </div>
            </div>
          </div>

          <div
            className="neo-table-wrapper"
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: 0,
              border: '1px solid #e2e8f0',
              borderRadius: 14,
              background: '#ffffff',
              boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
            }}
            onScroll={(event) => {
              const target = event.currentTarget;
              const isNearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 120;
              if (isNearBottom && visibleProductCount < sortedFilteredProducts.length) {
                setVisibleProductCount((count) => Math.min(count + 50, sortedFilteredProducts.length));
              }
            }}
          >
            <Table
              dataSource={visibleProducts}
              rowKey="id"
              className="neo-table-product-selection"
              sticky
              rowSelection={{
                type: "checkbox",
                selectedRowKeys: Object.keys(selectedProducts).filter(
                  (id) => selectedProducts[id]
                ),
                onSelect: (record, selected) => {
                  setSelectedProducts((prev) => ({
                    ...prev,
                    [record.id]: selected,
                  }));
                },
                onSelectAll: (selected, _rows, changeRows) => {
                  setSelectedProducts((prev) => {
                    const updated = { ...prev };
                    changeRows.forEach((r) => {
                      if (selected) updated[r.id] = true;
                      else delete updated[r.id];
                    });
                    return updated;
                  });
                },
              }}
              columns={[
                {
                  title: "Produit",
                  key: "product",
                  render: (_, product) => (
                    <Space size={12}>
                      {product.image || product.imageSrc || (product as any).image ? (
                        <img
                          src={product.image || product.imageSrc || (product as any).image}
                          alt={product.title}
                          width={40}
                          height={40}
                          loading="lazy"
                          style={{ borderRadius: 6, objectFit: "cover", border: "1px solid #e2e8f0" }}
                        />
                      ) : (
                        <div className="product-avatar" style={{ width: 40, height: 40, borderRadius: 6, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: '#64748b', border: '1px solid #e2e8f0' }}>
                          {product.title.charAt(0)}
                        </div>
                      )}
                      <div className="product-info">
                        <Typography.Text strong style={{ fontSize: 13, display: 'block', lineHeight: 1.2 }}>{product.title}</Typography.Text>
                        <div className="product-meta" style={{ marginTop: 2, display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 11, color: '#64748b' }}>{product.vendor}</span>
                          {product.sku && <span style={{ fontSize: 10, color: '#94a3b8', background: '#f8fafc', padding: '1px 4px', borderRadius: 4 }}>{product.sku}</span>}
                        </div>
                      </div>
                    </Space>
                  ),
                },
                {
                  title: "Quantité",
                  key: "quantity",
                  width: 140,
                  render: (_, product) => (
                    <InputNumber
                      min={0}
                      className="neo-input-number"
                      size="middle"
                      value={productQuantities[product.id] || 0}
                      placeholder="0"
                      onChange={(value) => {
                        setProductQuantities((prev) => ({
                          ...prev,
                          [product.id]: (value as number) || 0,
                        }));
                        if (value && value > 0 && !selectedProducts[product.id]) {
                          setSelectedProducts(prev => ({ ...prev, [product.id]: true }));
                        }
                      }}
                      style={{ width: "100%", borderRadius: 8 }}
                    />
                  ),
                },
              ]}
              pagination={false}
            />
          </div>
        </div>
      ),
    },
    {
      title: "Récapitulatif",
      icon: <CheckCircleOutlined />,
      content: () => {
        const items = collectOrderItems();
        const formData = poForm.getFieldsValue(true);
        const totalAmount = items.reduce((sum, item) => sum + item.totalPrice, 0);
        const effectiveFlowType = formData.flowType || flowType;
        const selectedRetailerName =
          supplyRetailers.find((retailer) => normalizeId(retailer.id || retailer._id) === normalizeId(formData.retailerId))?.name ||
          formData.retailerName;
        const selectedDestinationName =
          effectiveFlowType === "inbound"
            ? storages.find((storage) => normalizeId(storage.id || storage._id) === normalizeId(formData.deliveryAddressId))?.name ||
              formData.deliveryAddressName ||
              formData.deliveryAddressId
            : selectedRetailerName ||
              formData.deliveryAddressName ||
              outboundDestinations.find((addr) => normalizeId(addr.id) === normalizeId(formData.deliveryAddressId))?.name ||
              formData.deliveryAddressId;

        return (
          <div className="neo-recap-section" style={{ padding: '16px 24px', flex: 1, background: '#f8fafc', display: 'flex', flexDirection: 'column', overflow: 'hidden', height: '100%' }}>
            <div className="recap-header" style={{ marginBottom: 12, flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography.Title level={4} style={{ margin: 0, fontWeight: 700, fontSize: 16 }}>Résumé du Bon</Typography.Title>
              <Tag color={formData.flowType === "inbound" ? "blue" : "orange"} style={{ margin: 0, fontSize: 10, fontWeight: 700, borderRadius: 4 }}>
                {formData.flowType === "inbound" ? "ARRIVAGE" : "EXPÉDITION"}
              </Tag>
            </div>

            <div className="recap-info-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 16, flexShrink: 0 }}>
              <div className="recap-card" style={{ padding: '8px 12px', background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                <Typography.Text type="secondary" style={{ fontSize: 9, textTransform: 'uppercase', display: 'block', marginBottom: 2, fontWeight: 600 }}>Origine</Typography.Text>
                <Typography.Text strong style={{ fontSize: 11, display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {effectiveFlowType === "inbound"
                    ? supplierList.find(s => String(s.id) === String(formData.supplierId))?.name || formData.supplierId || "Non défini"
                    : storages.find(s => normalizeId(s.id || s._id) === normalizeId(formData.sourceLocationId))?.name || formData.sourceLocationId || "Non défini"
                  }
                </Typography.Text>
              </div>
              <div className="recap-card" style={{ padding: '8px 12px', background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                <Typography.Text type="secondary" style={{ fontSize: 9, textTransform: 'uppercase', display: 'block', marginBottom: 2, fontWeight: 600 }}>Destination</Typography.Text>
                <Typography.Text strong style={{ fontSize: 11, display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {selectedDestinationName || "Non défini"}
                </Typography.Text>
              </div>
              <div className="recap-card" style={{ padding: '8px 12px', background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                <Typography.Text type="secondary" style={{ fontSize: 9, textTransform: 'uppercase', display: 'block', marginBottom: 2, fontWeight: 600 }}>Date Prévue</Typography.Text>
                <Typography.Text strong style={{ fontSize: 11, display: 'block' }}>
                  {formData.expectedDeliveryDate ? dayjs(formData.expectedDeliveryDate).format('DD/MM/YYYY') : "À confirmer"}
                </Typography.Text>
              </div>
            </div>

            <div className="recap-products" style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
              <div style={{ padding: '8px 16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                <Typography.Text strong style={{ fontSize: 12, color: '#475569' }}>PRODUITS ({items.length})</Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>{items.reduce((s, i) => s + i.quantity, 0)} unités</Typography.Text>
              </div>
              <div className="recap-products-list" style={{ flex: 1, overflowY: 'auto' }}>
                {items.length > 0 ? (
                  items.map((item, idx) => (
                    <div key={idx} className="recap-product-item" style={{ padding: '8px 16px', borderBottom: idx === items.length - 1 ? 'none' : '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 12 }}>
                      {item.image ? (
                        <Image 
                          src={item.image} 
                          alt={item.productName} 
                          width={32} 
                          height={32} 
                          style={{ borderRadius: 4, objectFit: 'cover' }}
                          preview={false}
                        />
                      ) : (
                        <div className="product-avatar" style={{ width: 32, height: 32, borderRadius: 4, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, border: '1px solid #e2e8f0', color: '#64748b' }}>
                          {item.productName.charAt(0)}
                        </div>
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Typography.Text strong style={{ fontSize: 11, display: 'block', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.productName}</Typography.Text>
                        <div style={{ fontSize: 9, color: '#94a3b8' }}>
                          SKU: {item.sku || "N/A"} • {item.vendor}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <div style={{ fontSize: 10, color: '#64748b' }}>{item.quantity} × {item.unitPrice.toFixed(2)}€</div>
                        <Typography.Text strong style={{ fontSize: 12, color: '#1e293b' }}>
                          {item.totalPrice.toFixed(2)}€
                        </Typography.Text>
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={{ padding: 40, textAlign: 'center' }}>
                    <Empty description="Aucun produit sélectionné" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                  </div>
                )}
              </div>
            </div>

            <div className="recap-total" style={{ marginTop: 24, padding: '16px 24px', background: '#0f172a', borderRadius: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#fff', flexShrink: 0 }}>
              <Typography.Text strong style={{ fontSize: 16, color: '#fff' }}>Montant Total</Typography.Text>
              <Typography.Text strong style={{ fontSize: 24, color: '#fff' }}>
                {totalAmount.toFixed(2)}€
              </Typography.Text>
            </div>
          </div>
        );
      },
    },
  ];

  const handleRemoveItemFromOrder = (productId: string) => {
    if (!selectedOrder) return;
    
    const newItems = selectedOrder.items.filter(item => item.productId !== productId);
    const itemsTotal = newItems.reduce((total, item) => total + (item.totalPrice || 0), 0);
    const importCost = selectedOrder.importCost || selectedOrder.shippingCost || 0;
    
    setSelectedOrder({
      ...selectedOrder,
      items: newItems,
      totalAmount: roundTo2(itemsTotal + importCost)
    });
    
    message.success("Produit retiré du bon de commande");
  };

  // Fonctions de navigation entre les étapes
  const next = () => {
    if (currentStep === 1) {
      // Vérifier qu'au moins un produit est sélectionné avec une quantité > 0
      const hasProductsWithQuantities = Object.keys(selectedProducts)
        .filter((id) => selectedProducts[id])
        .some((productId) => (productQuantities[productId] || 0) > 0);

      if (!hasProductsWithQuantities) {
        message.error(
          "Veuillez sélectionner au moins un produit et spécifier une quantité"
        );
        return;
      }

      // Mettre à jour les éléments du bon de commande avec les quantités actuelles
      const items = collectOrderItems();
      const totalAmountRaw = items.reduce(
        (sum, item) => sum + item.totalPrice,
        0
      );
      const totalAmount = roundTo2(totalAmountRaw);

      setNewPO((prev) => ({
        ...prev,
        items,
        totalAmount,
      }));
    }
    // Réinitialiser la barre de recherche si on arrive à l'étape 1 (sélection des produits)
    if (currentStep + 1 === 1) {
      setSearchText("");
    }

    setCurrentStep(currentStep + 1);
  };

  const prev = () => {
    // Réinitialiser la barre de recherche si on arrive à l'étape 1 (sélection des produits)
    if (currentStep - 1 === 1) {
      setSearchText("");
    }

    setCurrentStep(currentStep - 1);
  };

  // Colonnes pour la table des purchase orders
  const getRetrocessionBase = (po: PurchaseOrder) => {
    const supplierTotals = (po.items || []).reduce((sum, item) => {
      if (Number.isFinite(item.supplierTotalPrice)) return sum + (item.supplierTotalPrice || 0);
      if (Number.isFinite(item.supplierUnitPrice)) {
        return sum + (item.supplierUnitPrice || 0) * (item.quantity || 0);
      }
      return sum;
    }, 0);
    return supplierTotals > 0 ? supplierTotals : 0;
  };

  const getRetrocessionAmount = (po: PurchaseOrder) => {
    const base = getRetrocessionBase(po);
    const rate = po.retrocessionRate || 0;
    if (rate > 0) return (base * rate) / 100;
    return po.retrocessionAmount || 0;
  };

  const purchaseOrderColumns = [
    {
      title: <Typography.Text type="secondary" style={{ fontSize: 10, fontWeight: 500, letterSpacing: '0.05em' }}>RÉFÉRENCE</Typography.Text>,
      dataIndex: "reference",
      key: "reference",
      width: 130,
      render: (text: string, record: PurchaseOrder) => (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <Typography.Text 
            strong 
            style={{ 
              color: '#0f172a', 
              fontSize: 13, 
              cursor: 'pointer' 
            }}
            onClick={() => {
              setSelectedOrder(record);
              setIsEditMode(false);
              setShowDetailsModal(true);
            }}
          >
            {text}
          </Typography.Text>
          <div style={{ marginTop: 2 }}>
            <Tag 
              color={record.type === "outbound" ? "orange" : "blue"} 
              style={{ 
                fontSize: 9, 
                lineHeight: "14px", 
                borderRadius: 4,
                margin: 0,
                fontWeight: 600,
                border: 'none',
                padding: '0 4px'
              }}
            >
              {record.type === "outbound" ? "EXPÉDITION" : "ARRIVAGE"}
            </Tag>
          </div>
        </div>
      ),
    },
    {
      title: <Typography.Text type="secondary" style={{ fontSize: 10, fontWeight: 500, letterSpacing: '0.05em' }}>DATE</Typography.Text>,
      dataIndex: "createdAt",
      key: "createdAt",
      width: 100,
      render: (date: any) => (
        <Typography.Text style={{ fontSize: 12, color: '#64748b' }}>
          {dayjs(date).format("DD/MM/YYYY")}
        </Typography.Text>
      ),
    },
    {
      title: <Typography.Text type="secondary" style={{ fontSize: 10, fontWeight: 500, letterSpacing: '0.05em' }}>STATUT</Typography.Text>,
      dataIndex: "status",
      key: "status",
      width: 130,
      render: (status: string) => (
        <Tag 
          color={getStatusColor(status)} 
          style={{ 
            borderRadius: 6, 
            padding: '1px 8px', 
            fontWeight: 600,
            fontSize: 10,
            textTransform: 'uppercase'
          }}
        >
          {getStatusLabel(status)}
        </Tag>
      ),
    },
    {
      title: <Typography.Text type="secondary" style={{ fontSize: 10, fontWeight: 500, letterSpacing: '0.05em' }}>PARCOURS</Typography.Text>,
      key: "routing",
      render: (_: any, record: PurchaseOrder) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
              {record.type === "outbound" ? <HomeOutlined style={{ color: "#94a3b8", fontSize: 11 }} /> : <TeamOutlined style={{ color: "#94a3b8", fontSize: 11 }} />}
              <Typography.Text strong style={{ fontSize: 12, color: '#334155' }}>
                {record.type === "outbound" ? record.sourceLocationName : record.supplier}
              </Typography.Text>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {record.type === "inbound" ? <HomeOutlined style={{ color: "#94a3b8", fontSize: 11 }} /> : <EnvironmentOutlined style={{ color: "#94a3b8", fontSize: 11 }} />}
              <Typography.Text style={{ fontSize: 11, color: '#64748b' }}>
                {record.deliveryAddress?.name || "-"}
              </Typography.Text>
            </div>
          </div>
        </div>
      ),
    },
    {
      title: <Typography.Text type="secondary" style={{ fontSize: 10, fontWeight: 500, letterSpacing: '0.05em' }}>DÉTAILS</Typography.Text>,
      key: "details",
      width: 110,
      render: (_: any, record: PurchaseOrder) => (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <Typography.Text style={{ fontSize: 12, color: '#334155' }}>
            <strong>{record.items?.length || 0}</strong> SKU
          </Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 10 }}>
            {record.items?.reduce((sum, item) => sum + (item.quantity || 0), 0)} unités
          </Typography.Text>
        </div>
      ),
    },
    {
      title: <Typography.Text type="secondary" style={{ fontSize: 10, fontWeight: 500, letterSpacing: '0.05em' }}>MONTANT</Typography.Text>,
      dataIndex: "totalAmount",
      key: "totalAmount",
      align: "right" as const,
      width: 150,
      render: (amount: number) => (
        <Typography.Text strong style={{ fontSize: 14, color: '#0f172a' }}>
          {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0)}
        </Typography.Text>
      ),
    },
    {
      title: <Typography.Text type="secondary" style={{ fontSize: 10, fontWeight: 500, letterSpacing: '0.05em' }}>LOGISTIQUE</Typography.Text>,
      key: "logistics",
      width: 140,
      render: (_: any, record: PurchaseOrder) => {
        const cost = record.importCost || record.shippingCost || 0;
        const estimated = (record as any).estimatedCost || 0;
        
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {cost > 0 ? (
              <Tag color="green" style={{ margin: 0, fontSize: 9, padding: '0 4px' }}>Frais: €{cost.toFixed(2)}</Tag>
            ) : estimated > 0 ? (
              <Tag color="default" style={{ margin: 0, fontSize: 9, padding: '0 4px' }}>Est: €{estimated.toFixed(2)}</Tag>
            ) : null}
            {record.trackingNumber && (
              <Typography.Text copyable={{ text: record.trackingNumber }} style={{ fontSize: 10, color: '#64748b' }}>
                {record.trackingNumber}
              </Typography.Text>
            )}
          </div>
        );
      },
    },
    {
      title: "",
      key: "actions",
      align: "right" as const,
      width: 140,
      render: (_: any, record: PurchaseOrder) => {
        const menuItems: any[] = [
          {
            key: 'edit',
            label: 'Modifier',
            icon: <EditOutlined />,
          },
          {
            key: 'export',
            label: 'Exporter XLSX',
            icon: <FileTextOutlined />,
          },
          {
            key: 'export-pdf',
            label: 'Exporter PDF',
            icon: <FileTextOutlined />,
          },
          {
            key: 'invoices',
            label: 'Factures (Pennylane)',
            icon: <FileTextOutlined />,
            disabled: String(record.type || "").toLowerCase() !== "outbound",
          },
          {
            type: 'divider',
          },
          {
            key: 'delete',
            label: 'Supprimer',
            icon: <DeleteOutlined />,
            danger: true,
            disabled: record.status !== "draft",
          }
        ];

        const handleMenuAction = (key: string) => {
          if (key === "edit") {
            purchaseOrderService
              .getPurchaseOrderById(record.id || record._id || "")
              .then((orderDetails) => {
                setSelectedOrder(orderDetails);
                purchaseOrderForm.setFieldsValue({
                  reference: orderDetails.reference,
                  supplier: orderDetails.supplier,
                  deliveryAddress: orderDetails.deliveryAddress?.name || "",
                  expectedDeliveryDate: orderDetails.expectedDeliveryDate ? dayjs(orderDetails.expectedDeliveryDate) : undefined,
                  importCost: orderDetails.importCost || 0,
                  deliveryInfo: orderDetails.deliveryAddress?.deliveryInfo || "",
                  deliveryComment: orderDetails.deliveryAddress?.comment || "",
                  carrierId: orderDetails.carrierId,
                  trackingNumber: orderDetails.trackingNumber,
                  retailerId: orderDetails.retailerId,
                  retrocessionRate: orderDetails.retrocessionRate,
                  parcelCount: orderDetails.parcelCount || 1,
                  parcels: orderDetails.parcels || [],
                  items: orderDetails.items || [],
                });
                setIsEditMode(true);
                setShowDetailsModal(true);
              })
              .catch(() => message.error("Erreur de chargement"));
            return;
          }
          if (key === "export") {
            exportPOXLSX(record);
            return;
          }
          if (key === "export-pdf") {
            exportPurchaseOrderToPdf(record);
            return;
          }
          if (key === "invoices") {
            purchaseOrderService
              .getPurchaseOrderById(record.id || record._id || "")
              .then((orderDetails) => {
                setSelectedOrder(orderDetails);
                setIsEditMode(false);
                setShowDetailsModal(false);
                openPennylaneModalForOrder(orderDetails as any);
              })
              .catch(() => message.error("Erreur de chargement"));
            return;
          }
          if (key === "delete") {
            handleDeletePurchaseOrder(record._id || record.id);
          }
        };

        return (
          <Space size={4} className="po-row-actions">
            {record.status === "draft" ? (
              <Tooltip title="Notifier l'entrepôt">
                <Button
                  icon={<PlayCircleOutlined />}
                  size="small"
                  type="primary"
                  className="!h-6"
                  style={{ borderRadius: 6, fontSize: 11, height: 26, padding: '0 10px' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    const id = record._id || record.id;
                    if (id) handleSendPO(id);
                  }}
                >
                  Envoyer
                </Button>
              </Tooltip>
            ) : (
              <Select
                value={record.status}
                size="small"
                style={{ width: 100 }}
                className="neo-status-select"
                onClick={(e) => e.stopPropagation()}
                onChange={(value) => updateOrderStatus(record._id || record.id, value)}
                options={[
                  { value: "sent", label: "Commandé" },
                  { value: "in_delivery", label: "En livraison" },
                  { value: "partially_received", label: "Partiel" },
                  { value: "noted_delivered", label: "Livré" },
                  { value: "completed", label: "Terminé" },
                  { value: "dispute", label: "Litige" },
                  { value: "cancelled", label: "Annuler" },
                ]}
              />
            )}
            
            <Dropdown
              menu={{
                items: menuItems,
                onClick: ({ key, domEvent }) => {
                  domEvent.stopPropagation();
                  handleMenuAction(String(key));
                },
              }}
              trigger={['click']}
            >
              <Button 
                icon={<EllipsisOutlined />} 
                size="small" 
                type="text" 
                style={{ borderRadius: 6, border: '1px solid #cbd5e1', color: '#64748b', width: 28, height: 26 }}
                onClick={(e) => e.stopPropagation()}
              />
            </Dropdown>
          </Space>
        );
      },
    },
  ];

  // Interface utilisateur principale
  return (
    <div className="supply-cockpit">
      <div className="display-flex">
        <div>
          <Typography.Title level={2}>Supply Chain</Typography.Title>
          <Typography.Text type="secondary" style={{ marginBottom: 24, display: "block" }}>
            Pilotage des approvisionnements, gestion multi-entrepôts et suivi des flux fournisseurs.
          </Typography.Text>
        </div>
        <Popover content={notificationContent} trigger="click" placement="bottomRight">
          <Badge count={supplyNotifications.length} size="small" overflowCount={99}>
            <Button
              shape="circle"
              icon={<BellOutlined />}
              style={{
                background: "#0f172a",
                color: "#f8fafc",
                border: "none",
                boxShadow: "0 10px 20px rgba(15, 23, 42, 0.2)",
              }}
            />
          </Badge>
        </Popover>
      </div>

      <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card className="neo-card" styles={{ body: { padding: '24px' } }}>
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography.Text type="secondary" strong style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Commandes Actives</Typography.Text>
                <ShoppingCartOutlined style={{ color: '#64748b' }} />
              </div>
              <Typography.Title level={2} style={{ margin: 0, fontWeight: 800 }}>
                {purchaseOrders.filter(
                  (po) =>
                    po.status === "sent" ||
                    po.status === "in_delivery" ||
                    po.status === "partially_received" ||
                    po.status === "noted_delivered" ||
                    po.status === "dispute"
                ).length}
              </Typography.Title>
              <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
                <Tag color="blue" style={{ margin: 0 }}>
                  {purchaseOrders.filter(po => po.type !== "outbound" && (po.status === "sent" || po.status === "in_delivery")).length} Arrivages
                </Tag>
                <Tag color="orange" style={{ margin: 0 }}>
                  {purchaseOrders.filter(po => po.type === "outbound" && (po.status === "sent" || po.status === "in_delivery")).length} Expéditions
                </Tag>
              </div>
            </Space>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="neo-card" bodyStyle={{ padding: '24px' }}>
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography.Text type="secondary" strong style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Livraisons 7j</Typography.Text>
                <ClockCircleOutlined style={{ color: '#faad14' }} />
              </div>
              <Typography.Title level={2} style={{ margin: 0, fontWeight: 800 }}>
                {purchaseOrders.filter(
                  (po) =>
                    (po.status === "sent" || po.status === "in_delivery") &&
                    po.expectedDeliveryDate &&
                    dayjs(po.expectedDeliveryDate).isBefore(dayjs().add(7, "day"))
                ).length}
              </Typography.Title>
              <div className="trend-warning">Arrivages & Départs prévus</div>
            </Space>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="neo-card" bodyStyle={{ padding: '24px' }}>
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography.Text type="secondary" strong style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Ruptures / Stock bas</Typography.Text>
                <ReloadOutlined style={{ color: '#ef4444' }} />
              </div>
              <Typography.Title level={2} style={{ margin: 0, fontWeight: 800 }}>
                {products.filter(
                  (p) =>
                    p.currentStock !== undefined &&
                    p.minStock !== undefined &&
                    p.currentStock < p.minStock
                ).length}
              </Typography.Title>
              <div className="trend-danger">Alertes réappro.</div>
            </Space>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="neo-card" bodyStyle={{ padding: '24px' }}>
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography.Text type="secondary" strong style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Volume Commandé</Typography.Text>
                <Typography.Text strong style={{ color: '#64748b' }}>€</Typography.Text>
              </div>
              <Typography.Title level={2} style={{ margin: 0, fontWeight: 800 }}>
                {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(
                  purchaseOrders.reduce((sum, po) => sum + po.totalAmount, 0)
                )}
              </Typography.Title>
              <div className="trend-up">Total historique</div>
            </Space>
          </Card>
        </Col>
      </Row>

      <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
        <Col xs={24} md={12} lg={6}>
          <Card
            hoverable
            className="neo-card card-compact"
            onClick={() => setShowSupplierModal(true)}
            styles={{ body: { padding: "24px" } }}
            loading={loadingSuppliers}
          >
            <div className="card-icon" style={{ background: '#e0f2fe', color: '#0369a1' }}>
              <TeamOutlined />
            </div>
            <Typography.Title level={4} style={{ margin: "0 0 8px", fontWeight: 700 }}>Fournisseurs</Typography.Title>
            <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
              {supplierList?.length || 0} contacts actifs
            </Typography.Text>
            <Button type="text" style={{ color: '#0369a1', fontWeight: 600 }}>Configurer</Button>
          </Card>
        </Col>
        <Col xs={24} md={12} lg={6}>
          <Card
            hoverable
            className="neo-card card-compact"
            onClick={() => setShowAddressModal(true)}
            styles={{ body: { padding: "24px" } }}
            loading={loadingAddresses}
          >
            <div className="card-icon" style={{ background: '#f0fdf4', color: '#15803d' }}>
              <EnvironmentOutlined />
            </div>
            <Typography.Title level={4} style={{ margin: "0 0 8px", fontWeight: 700 }}>Destinations</Typography.Title>
            <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
              {addressList?.length || 0} adresses configurées
            </Typography.Text>
            <Button type="text" style={{ color: '#15803d', fontWeight: 600 }}>Gérer</Button>
          </Card>
        </Col>
        <Col xs={24} md={12} lg={6}>
          <Card
            hoverable
            className="neo-card card-compact"
            onClick={() => setShowRulesModal(true)}
            styles={{ body: { padding: "24px" } }}
          >
            <div className="card-icon" style={{ background: '#fffbeb', color: '#a16207' }}>
              <SettingOutlined />
            </div>
            <Typography.Title level={4} style={{ margin: "0 0 8px", fontWeight: 700 }}>Automatisations</Typography.Title>
            <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
              {supplyRules.length} règles de calcul
            </Typography.Text>
            <Button type="text" style={{ color: '#a16207', fontWeight: 600 }}>Optimiser</Button>
          </Card>
        </Col>
        <Col xs={24} md={12} lg={6}>
          <Card
            hoverable
            className="neo-card card-compact"
            onClick={() => setShowStorageModal(true)}
            styles={{ body: { padding: "24px" } }}
            loading={loadingStorages}
          >
            <div className="card-icon" style={{ background: '#fef2f2', color: '#991b1b' }}>
              <HomeOutlined />
            </div>
            <Typography.Title level={4} style={{ margin: "0 0 8px", fontWeight: 700 }}>Entrepôt</Typography.Title>
            <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
              {storages.length} sites actifs
            </Typography.Text>
            <Button type="text" style={{ color: '#991b1b', fontWeight: 600 }}>Accéder</Button>
          </Card>
        </Col>
      </Row>

      {/* Modale pour la liste des entrepôts */}
      <Modal
        title="Gestion des entrepôts"
        open={showStorageModal}
        onCancel={() => setShowStorageModal(false)}
        width={800}
        footer={[
          <Button key="close" onClick={() => setShowStorageModal(false)}>
            Fermer
          </Button>,
          <Button
            key="add"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setCurrentStorage(null);
              storageForm.resetFields();
              setStockTemplateHeaders([]);
              setShowStorageFormModal(true);
            }}
          >
            Nouvel entrepôt
          </Button>,
        ]}
      >
        <Table
          dataSource={storages}
          rowKey={(s) => s.id || s._id || ''}
          columns={[
            { title: "Nom", dataIndex: "name", key: "name" },
            { 
              title: "Type", 
              dataIndex: "type", 
              key: "type",
              render: (t) => <Tag color={t === 'warehouse' ? 'blue' : 'orange'}>{t}</Tag>
            },
            { 
              title: "Ville", 
              dataIndex: ["address", "city"], 
              key: "city",
              render: (_, r) => r.address?.city || "-"
            },
            {
              title: "Actions",
              key: "actions",
              render: (_, record) => (
                <Space>
                  <Button
                    icon={<EditOutlined />}
                    size="small"
                    onClick={() => {
                      setCurrentStorage(record);
                      storageForm.setFieldsValue({
                        ...record,
                        address: record.address || {},
                      });
                      setStockTemplateHeaders(
                        Array.from(new Set([
                          ...Object.values(record.stockImportTemplate?.mapping || {}).filter(Boolean),
                          ...(record.stockImportTemplate?.ignoredColumns || []),
                        ])) as string[]
                      );
                      setShowStorageFormModal(true);
                    }}
                  >
                    Modifier
                  </Button>
                  <Button
                    icon={<DeleteOutlined />}
                    danger
                    size="small"
                    onClick={() => {
                      Modal.confirm({
                        title: 'Supprimer cet entrepôt ?',
                        content: 'Cette action est irréversible.',
                        onOk: () => handleDeleteStorage(record.id || record._id || ''),
                      });
                    }}
                  >
                    Supprimer
                  </Button>
                </Space>
              ),
            },
          ]}
          pagination={{ pageSize: 5 }}
        />
      </Modal>

      <Modal
        title={editingRetailer ? `Logistique - ${editingRetailer.name}` : "Logistique retailer"}
        open={showRetailerLogisticsModal}
        onCancel={() => {
          setShowRetailerLogisticsModal(false);
          setEditingRetailer(null);
          retailerLogisticsForm.resetFields();
        }}
        onOk={handleSaveRetailerLogistics}
        width={900}
        okText="Enregistrer"
      >
        <Form form={retailerLogisticsForm} layout="vertical">
          <Form.Item name={["logistics", "fulfillmentMode"]} label="Mode de fulfillment">
            <Select
              options={[
                { value: "hub", label: "Hub unique" },
                { value: "multi_hub", label: "Multi-hubs" },
                { value: "per_channel", label: "Par canal" },
              ]}
            />
          </Form.Item>

          {editFulfillmentMode === "hub" && (
            <Card style={{ background: "#fbfcff", marginBottom: 16 }}>
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item name={["logistics", "hub", "label"]} label="Hub principal">
                    <Input placeholder="Hub central" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name={["logistics", "hub", "street"]} label="Adresse hub">
                    <Input placeholder="12 Rue du Hub" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col span={8}>
                  <Form.Item name={["logistics", "hub", "city"]} label="Ville">
                    <Input placeholder="Paris" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name={["logistics", "hub", "postalCode"]} label="Code postal">
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name={["logistics", "hub", "country"]} label="Pays">
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          )}

          {editFulfillmentMode === "multi_hub" && (
            <Form.List name={["logistics", "hubs"]}>
              {(fields, { add, remove }) => (
                <Space direction="vertical" style={{ width: "100%" }}>
                  {fields.map(({ key, name, ...restField }) => (
                    <Row gutter={8} key={key}>
                      <Col span={6}>
                        <Form.Item {...restField} name={[name, "label"]} label="Hub">
                          <Input placeholder="HUB Nord" />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item {...restField} name={[name, "address", "street"]} label="Adresse">
                          <Input placeholder="12 Rue du Hub" />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item {...restField} name={[name, "address", "city"]} label="Ville">
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col span={3}>
                        <Form.Item {...restField} name={[name, "address", "postalCode"]} label="CP">
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col span={3}>
                        <Form.Item {...restField} name={[name, "address", "country"]} label="Pays">
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col span={24} style={{ textAlign: "right" }}>
                        <Button danger onClick={() => remove(name)}>
                          Supprimer
                        </Button>
                      </Col>
                    </Row>
                  ))}
                  <Button type="dashed" onClick={() => add()}>
                    Ajouter un hub
                  </Button>
                </Space>
              )}
            </Form.List>
          )}

          {editFulfillmentMode === "per_channel" && (
            <Form.List name={["channelsDetails"]}>
              {(fields) => (
                <Space direction="vertical" style={{ width: "100%" }}>
                  {fields.map(({ key, name, ...restField }) => (
                    <Card key={key} style={{ background: "#fbfbfb" }}>
                      <Row gutter={12}>
                        <Col span={12}>
                          <Form.Item {...restField} name={[name, "name"]} label="Canal">
                            <Input disabled />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item {...restField} name={[name, "key"]} label="Code">
                            <Input disabled />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={12}>
                        <Col span={12}>
                          <Form.Item {...restField} name={[name, "logistics", "hub", "label"]} label="Hub canal">
                            <Input placeholder="Hub canal" />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item {...restField} name={[name, "logistics", "hub", "street"]} label="Adresse">
                            <Input />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={12}>
                        <Col span={8}>
                          <Form.Item {...restField} name={[name, "logistics", "hub", "city"]} label="Ville">
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item {...restField} name={[name, "logistics", "hub", "postalCode"]} label="CP">
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item {...restField} name={[name, "logistics", "hub", "country"]} label="Pays">
                            <Input />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Card>
                  ))}
                </Space>
              )}
            </Form.List>
          )}
        </Form>
      </Modal>

      {/* Modale de formulaire pour entrepôt */}
      <Modal
        title={currentStorage ? "Modifier l'entrepôt" : "Créer un entrepôt"}
        open={showStorageFormModal}
        onCancel={() => setShowStorageFormModal(false)}
        onOk={() => storageForm.submit()}
        confirmLoading={loadingStorages}
        destroyOnClose
      >
        <Form
          form={storageForm}
          layout="vertical"
          onFinish={handleSaveStorage}
          initialValues={{ type: 'warehouse' }}
        >
          <Form.Item name="name" label="Nom de l'entrepôt" rules={[{ required: true }]}>
            <Input placeholder="Ex: Entrepôt Nord" />
          </Form.Item>
          <Form.Item name="type" label="Type">
            <Select options={[
              { label: 'Entrepôt', value: 'warehouse' },
              { label: 'Boutique', value: 'store' },
              { label: 'Autre', value: 'other' },
            ]} />
          </Form.Item>
          <Divider orientation="left">Adresse</Divider>
          <Form.Item name={["address", "street"]} label="Rue"><Input /></Form.Item>
          <Row gutter={8}>
            <Col span={12}><Form.Item name={["address", "postalCode"]} label="CP"><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name={["address", "city"]} label="Ville"><Input /></Form.Item></Col>
          </Row>
          <Form.Item name={["address", "country"]} label="Pays"><Input /></Form.Item>
          <Divider orientation="left">Contact</Divider>
          <Form.Item name="contactPerson" label="Contact"><Input /></Form.Item>
          <Row gutter={8}>
            <Col span={12}><Form.Item name="email" label="Email"><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="phone" label="Téléphone"><Input /></Form.Item></Col>
          </Row>
          <Divider orientation="left">Template d'import de stock</Divider>
          <Upload
            accept=".xlsx,.xls,.csv"
            maxCount={1}
            showUploadList={false}
            beforeUpload={handleStorageTemplateFile}
          >
            <Button icon={<UploadOutlined />} loading={stockTemplateLoading}>
              Analyser un fichier XLS ou CSV
            </Button>
          </Upload>
          <Typography.Text type="secondary" style={{ display: "block", marginTop: 8, marginBottom: 12 }}>
            Associe chaque information métier à la colonne correspondante du fichier. Ce mapping est conservé pour cet entrepôt.
          </Typography.Text>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name={["stockImportTemplate", "sheetName"]} label="Feuille">
                <Input placeholder="PRODUITS" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name={["stockImportTemplate", "headerRow"]} label="Ligne d'en-tête">
                <InputNumber min={1} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
          {[
            ["productCode", "SKU"],
            ["barcode", "EAN"],
            ["quantity", "Quantité en stock"],
            ["status", "État du stock"],
            ["batchNumber", "Numéro de lot"],
            ["expiryDate", "DLUO / expiration"],
          ].map(([field, label]) => (
            <Form.Item
              key={field}
              name={["stockImportTemplate", "mapping", field]}
              label={label}
              rules={["quantity", "status"].includes(field) ? [{ required: true }] : undefined}
            >
              <Select
                allowClear
                showSearch
                options={stockTemplateHeaders.map((header) => ({
                  value: header,
                  label: header,
                }))}
                placeholder="Choisir une colonne du fichier"
              />
            </Form.Item>
          ))}
          <Divider orientation="left">Correspondance des états</Divider>
          <Typography.Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
            Exemple : la valeur externe 01 peut correspondre à Commercialisable, et 08 à Défectueux.
          </Typography.Text>
          <Form.List name={["stockImportTemplate", "statusMappings"]}>
            {(fields, { add, remove }) => (
              <Space direction="vertical" size={8} style={{ width: "100%" }}>
                {fields.map(({ key, name, ...restField }) => (
                  <Row key={key} gutter={8} align="middle">
                    <Col span={10}>
                      <Form.Item
                        {...restField}
                        name={[name, "externalValue"]}
                        rules={[{ required: true, message: "Valeur requise" }]}
                        style={{ marginBottom: 0 }}
                      >
                        <Input placeholder="Valeur du fichier, ex. 01" />
                      </Form.Item>
                    </Col>
                    <Col span={11}>
                      <Form.Item
                        {...restField}
                        name={[name, "internalStatus"]}
                        rules={[{ required: true, message: "État requis" }]}
                        style={{ marginBottom: 0 }}
                      >
                        <Select
                          placeholder="Notre état"
                          options={[
                            { value: "marketable", label: "Commercialisable" },
                            { value: "reserved", label: "Réservé" },
                            { value: "defective", label: "Défectueux" },
                          ]}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={3}>
                      <Button
                        danger
                        type="text"
                        icon={<DeleteOutlined />}
                        onClick={() => remove(name)}
                      />
                    </Col>
                  </Row>
                ))}
                <Button type="dashed" icon={<PlusOutlined />} onClick={() => add()} block>
                  Ajouter une correspondance d'état
                </Button>
              </Space>
            )}
          </Form.List>
          <Form.Item
            name={["stockImportTemplate", "ignoredColumns"]}
            label="Colonnes mises de côté pour cet entrepôt"
          >
            <Select
              mode="multiple"
              allowClear
              options={stockTemplateHeaders.map((header) => ({
                value: header,
                label: header,
              }))}
            />
          </Form.Item>
          <Form.Item name={["stockImportTemplate", "sourceFileName"]} hidden>
            <Input />
          </Form.Item>
          <Form.Item name={["stockImportTemplate", "updatedAt"]} hidden>
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      {supplyTabKey === "history" ? (
        <Card className="neo-card" style={{ marginBottom: 24 }}>
          <div className="supply-card-header">
            <Tabs
              activeKey={supplyTabKey}
              onChange={setSupplyTabKey}
              className="neo-main-tabs"
              items={[
                { key: "history", label: "Historique des flux" },
                { key: "tracking", label: "Suivi" },
              ]}
            />
            <Button icon={<PlusOutlined />} type="primary" onClick={() => showCreateDrawer()}>
              Nouveau bon de commande
            </Button>
          </div>
          <div className="filter-section neo-filters">
            <Row gutter={[16, 16]} style={{ width: "100%" }} align="middle">
              <Col flex="auto">
                <Input
                  placeholder="Rechercher par référence, fournisseur..."
                  prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
                  className="neo-search-input"
                  allowClear
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </Col>
              <Col>
                <Select
                  placeholder="Flux"
                  style={{ width: 140 }}
                  allowClear
                  className="neo-select"
                  onChange={(val) => setTypeFilter(val || "")}
                  value={typeFilter || undefined}
                >
                  <Select.Option value="inbound">Arrivages</Select.Option>
                  <Select.Option value="outbound">Expéditions</Select.Option>
                </Select>
              </Col>
              <Col>
                <Select
                  placeholder="Statut"
                  style={{ width: 160 }}
                  allowClear
                  className="neo-select"
                  onChange={(value) => setStatusFilter(value || "")}
                  value={statusFilter || undefined}
                >
                  <Select.Option value="draft">Brouillon</Select.Option>
                  <Select.Option value="sent">Envoyé</Select.Option>
                  <Select.Option value="in_delivery">En livraison</Select.Option>
                  <Select.Option value="partially_received">Partiel</Select.Option>
                  <Select.Option value="noted_delivered">Livré</Select.Option>
                  <Select.Option value="completed">Terminé</Select.Option>
                  <Select.Option value="cancelled">Annulé</Select.Option>
                </Select>
              </Col>
              <Col>
                <Select
                  placeholder="Fournisseur"
                  style={{ width: 180 }}
                  allowClear
                  className="neo-select"
                  onChange={(value) => setSupplierFilter(value || "")}
                  value={supplierFilter || undefined}
                >
                  {supplierList.map((s) => (
                    <Select.Option key={s.id || s._id || s.name} value={s.name}>
                      {s.name}
                    </Select.Option>
                  ))}
                </Select>
              </Col>
              <Col>
                <Select
                  placeholder="Adresse"
                  style={{ width: 180 }}
                  allowClear
                  className="neo-select"
                  onChange={(value) => setAddressFilter(value || "")}
                  value={addressFilter || undefined}
                >
                  {addressList.map((a) => (
                    <Select.Option key={a.id} value={a.name}>
                      {a.name}
                    </Select.Option>
                  ))}
                </Select>
              </Col>
              <Col>
                <DatePicker.RangePicker
                  onChange={(dates) => {
                    setStartDateFilter(dates ? dates[0] : null);
                    setEndDateFilter(dates ? dates[1] : null);
                  }}
                  className="neo-datepicker"
                  style={{ width: 260 }}
                />
              </Col>
              <Col>
                <Button
                  icon={<ReloadOutlined />}
                  className="neo-btn-icon"
                  onClick={() => {
                    setStatusFilter("");
                    setTypeFilter("");
                    setStartDateFilter(null);
                    setEndDateFilter(null);
                    setSupplierFilter("");
                    setAddressFilter("");
                    setSearchText("");
                  }}
                />
              </Col>
            </Row>
          </div>

          <Table
            dataSource={filteredPurchaseOrders}
            columns={purchaseOrderColumns}
            rowKey="id"
            loading={loading}
            pagination={{
              pageSize: 12,
              showTotal: (total) => (
                <span style={{ color: "#64748b", fontSize: 12 }}>
                  Total <strong>{total}</strong> commandes
                </span>
              ),
              className: "neo-pagination-modern",
            }}
            className="neo-table-main"
            scroll={{ x: 1200 }}
            onRow={(record) => ({
              onClick: (event: any) => {
                const target = event?.target as HTMLElement | null;
                if (target?.closest(".po-row-actions")) return;
                setSelectedOrder(record);
                setIsEditMode(false);
                setShowDetailsModal(true);
              },
              style: { cursor: "pointer" },
            })}
          />
        </Card>
      ) : (
        <Card className="neo-card" style={{ marginBottom: 24 }}>
          <div className="supply-card-header">
            <Tabs
              activeKey={supplyTabKey}
              onChange={setSupplyTabKey}
              className="neo-main-tabs"
              items={[
                { key: "history", label: "Historique des flux" },
                { key: "tracking", label: "Suivi" },
              ]}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={async () => {
                const created = await createProjectGlobal({
                  title: "Nouveau suivi supply",
                  description: "Suivi logistique",
                  status: "open",
                  priority: "medium",
                  pushPullStatus: "pull",
                  attachedEmailIds: [],
                  metadata: { supplyProject: "true" },
                });
                setSupplyProjects((prev: Project[]) => [created, ...prev]);
              }}
            >
              Nouveau suivi
            </Button>
          </div>
          <Typography.Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
            Livraisons, réclamations et exécution rapide via IA.
          </Typography.Text>
          <ProjectKanban
            projects={supplyProjects}
            onCardClick={handleSupplyProjectClick}
            onDeleteProject={handleSupplyProjectDelete}
            onDrop={handleSupplyProjectDrop}
            onDragOver={setSupplyDragOverColumn}
            dragOverColumn={supplyDragOverColumn}
            activeProjectId={selectedSupplyProject?._id}
            getContactDisplayName={(retailerId, email) =>
              getSupplyContactDisplayName(retailerId, email)
            }
          />
        </Card>
      )}

      <Drawer
        open={supplyProjectDrawerOpen}
        onClose={() => setSupplyProjectDrawerOpen(false)}
        width={960}
        styles={{ body: { padding: 0 } }}
      >
        {selectedSupplyProject && (
          <ProjectDetails
            project={selectedSupplyProject}
            retailer={getSupplyRetailerForProject(selectedSupplyProject)}
            retailers={supplyRetailers}
            suppliers={supplierList}
            storages={storages}
            users={[]}
            retailerEmails={[]}
            showBackButton
            onBack={() => setSupplyProjectDrawerOpen(false)}
            onAddTodo={async (stage, task) => {
              const currentBoard = (selectedSupplyProject.taskBoard as any) || {};
              const stageTasks = [...(currentBoard[stage] || [])];
              stageTasks.push({
                title: task,
                status: "active",
                source: "manual",
                createdAt: new Date().toISOString(),
              });
              const data = await updateProjectGlobal(selectedSupplyProject._id!, {
                taskBoard: { ...currentBoard, [stage]: stageTasks },
              });
              setSupplyProjects((prev: Project[]) =>
                prev.map((p) => (p._id === selectedSupplyProject._id ? { ...p, ...data } : p))
              );
              setSelectedSupplyProject((prev) => (prev ? { ...prev, ...data } : prev));
            }}
            onToggleTodo={async (stage, idx) => {
              const currentBoard = (selectedSupplyProject.taskBoard as any) || {};
              const stageTasks = [...(currentBoard[stage] || [])];
              const task = stageTasks[idx];
              if (!task) return;
              stageTasks[idx] = { ...task, status: task.status === "archived" ? "active" : "archived" };
              const data = await updateProjectGlobal(selectedSupplyProject._id!, {
                taskBoard: { ...currentBoard, [stage]: stageTasks },
              });
              setSupplyProjects((prev: Project[]) =>
                prev.map((p) => (p._id === selectedSupplyProject._id ? { ...p, ...data } : p))
              );
              setSelectedSupplyProject((prev) => (prev ? { ...prev, ...data } : prev));
            }}
            onDeleteTodo={async (stage, idx) => {
              const currentBoard = (selectedSupplyProject.taskBoard as any) || {};
              const stageTasks = [...(currentBoard[stage] || [])];
              stageTasks.splice(idx, 1);
              const data = await updateProjectGlobal(selectedSupplyProject._id!, {
                taskBoard: { ...currentBoard, [stage]: stageTasks },
              });
              setSupplyProjects((prev: Project[]) =>
                prev.map((p) => (p._id === selectedSupplyProject._id ? { ...p, ...data } : p))
              );
              setSelectedSupplyProject((prev) => (prev ? { ...prev, ...data } : prev));
            }}
            getContactDisplayName={getSupplyContactDisplayNameForDetails}
            aiWarningsByAction={{}}
            getSnapshotPayload={getSupplySnapshotPayload}
            onUpdateProject={async (updates) => {
              const data = await updateProjectGlobal(selectedSupplyProject._id!, updates);
              setSupplyProjects((prev: Project[]) =>
                prev.map((p) => (p._id === selectedSupplyProject._id ? { ...p, ...data } : p))
              );
              setSelectedSupplyProject((prev) => (prev ? { ...prev, ...data } : prev));
            }}
          />
        )}
      </Drawer>

      <Drawer
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div className="drawer-header-icon" style={{ 
              width: 40, 
              height: 40, 
              borderRadius: 10, 
              background: '#eff6ff', 
              color: '#3b82f6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 20
            }}>
              <PlusOutlined />
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, lineHeight: 1.2 }}>Nouveau Bon de Commande</div>
              <div style={{ fontSize: 12, fontWeight: 400, color: '#64748b' }}>Configurez votre flux logistique et sélectionnez vos produits</div>
            </div>
          </div>
        }
        width={920}
        onClose={handleCancel}
        open={createDrawerVisible}
        className="neo-drawer"
        styles={{
          body: { padding: 0, overflow: 'hidden' },
          footer: { padding: '12px 24px' }
        }}
        footer={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button onClick={handleCancel} type="text" danger>
              Annuler
            </Button>
            <Space size={12}>
              {currentStep > 0 && (
                <Button onClick={prev} className="neo-btn-secondary">
                  Précédent
                </Button>
              )}
              {currentStep < steps.length - 1 ? (
                <Button type="primary" onClick={next} className="neo-btn-primary">
                  Continuer
                </Button>
              ) : (
                <Button
                  type="primary"
                  onClick={() => {
                    const key = "finalize-po";
                    message.loading({ content: "Validation en cours...", key });
                    handleCreatePO().finally(() => message.destroy(key));
                  }}
                  className="neo-btn-primary"
                  icon={<CheckCircleOutlined />}
                >
                  Finaliser et Créer
                </Button>
              )}
            </Space>
          </div>
        }
      >
        <Form 
          form={poForm} 
          layout="vertical" 
          preserve={true} 
          style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
        >
          <div className="drawer-content-inner" style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div className="steps-wrapper" style={{ padding: '20px 24px 0', flexShrink: 0 }}>
              <Steps
                current={currentStep}
                items={steps.map((item) => ({ 
                  title: item.title,
                  icon: item.icon
                }))}
                className="neo-steps"
                size="small"
              />
            </div>
            <div className="step-content-container" style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              {steps[currentStep]?.content?.()}
            </div>
          </div>
        </Form>
      </Drawer>

      {/* Modale pour la liste des fournisseurs */}
      <Modal
        title="Gestion des fournisseurs"
        open={showSupplierModal}
        onCancel={() => setShowSupplierModal(false)}
        width={800}
        footer={[
          <Button key="close" onClick={() => setShowSupplierModal(false)}>
            Fermer
          </Button>,
          <Button
            key="add"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setCurrentSupplier(null);
              setIsSupplierAddressEditing(true);
              setSupplierFormType("brands");
              setSelectedProductIds([]);
              supplierForm.resetFields();
              supplierForm.setFieldsValue({
                suppliedType: "brands",
                suppliedItems: [],
                carrierId: undefined,
                address: {},
              });
              setShowSupplierFormModal(true);
            }}
          >
            Nouveau fournisseur
          </Button>,
        ]}
      >
        {supplierList && supplierList.length > 0 ? (
          <Table
            dataSource={supplierList}
            rowKey="id"
            columns={[
              {
                title: "Nom",
                dataIndex: "name",
                key: "name",
                sorter: (a, b) => a.name.localeCompare(b.name),
              },
              {
                title: "Pays",
                dataIndex: "country",
                key: "country",
                render: (country) => country || "Non spécifié",
              },
              {
                title: "Marques / Références",
                key: "suppliedItems",
                render: (_, record) => {
                  const items =
                    record.suppliedItems || record.vendorBrands || [];
                  if (!items || items.length === 0)
                    return "Aucun élément associé";

                  return (
                    <div
                      style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}
                    >
                      {items.map((item: string) => {
                        const isProductId =
                          record.suppliedType === "references";
                        const color = isProductId ? "green" : "blue";
                        let displayText = item;

                        return (
                          <Tag key={item} color={color}>
                            {displayText}
                          </Tag>
                        );
                      })}
                    </div>
                  );
                },
              },
              {
                title: "Actions",
                key: "actions",
                render: (_: any, record: Supplier) => (
                  <Space>
                    <Button
                      icon={<EditOutlined />}
                      onClick={() => {
                        setCurrentSupplier(record);
                        setIsSupplierAddressEditing(false);
                        supplierForm.resetFields();
                        setSupplierFormType(record.suppliedType || "brands");
                        setSelectedProductIds(record.suppliedItems || []);
                        supplierForm.setFieldsValue({
                          ...record,
                          name: record.name || "",
                          country: record.country || "",
                          contactPerson: record.contactPerson || "",
                          email: record.email || "",
                          phone: record.phone || "",
                          address: record.address || {},
                          suppliedType: record.suppliedType || "brands",
                          suppliedItems: record.suppliedItems || [],
                        });

                        const a: any = record.address || {};
                        const hasAnyAddress =
                          !!a.name ||
                          !!a.street1 ||
                          !!a.street2 ||
                          !!a.city ||
                          !!a.state ||
                          !!a.postalCode ||
                          !!a.country ||
                          !!a.countryCode ||
                          !!a.phone ||
                          !!a.email;
                        if (!hasAnyAddress) {
                          setIsSupplierAddressEditing(true);
                        }

                        setShowSupplierFormModal(true);
                      }}
                    >
                      Modifier
                    </Button>
                    <Button
                      icon={<DeleteOutlined />}
                      danger
                      onClick={() => handleDeleteSupplier(record.id)}
                    >
                      Supprimer
                    </Button>
                  </Space>
                ),
              },
            ]}
            pagination={{ pageSize: 5 }}
          />
        ) : (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <Typography.Title
              level={4}
              type="secondary"
              style={{ fontWeight: "normal" }}
            >
              Aucun fournisseur configuré
            </Typography.Title>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              style={{ marginTop: 16 }}
              onClick={() => {
                setCurrentSupplier(null);
                setIsSupplierAddressEditing(true);
                setSupplierFormType("brands");
                setSelectedProductIds([]);
                supplierForm.resetFields();
                supplierForm.setFieldsValue({
                  suppliedType: "brands",
                  suppliedItems: [],
                  carrierId: undefined,
                  address: {},
                });
                setShowSupplierFormModal(true);
              }}
            >
              Ajouter un fournisseur
            </Button>
          </div>
        )}
      </Modal>

      {/* Modale de formulaire pour ajouter/modifier un fournisseur */}
      <Modal
        title={
          currentSupplier ? "Modifier un fournisseur" : "Ajouter un fournisseur"
        }
        open={showSupplierFormModal}
        onCancel={() => {
          setShowSupplierFormModal(false);
          setIsSupplierAddressEditing(false);
        }}
        footer={null}
        style={{ top: 100 }}
        styles={{ body: { maxHeight: "75vh", overflowY: "auto" } }}
      >
        <Form
          layout="vertical"
          form={supplierForm}
          onFinish={(values) => {
            handleSaveSupplier(values);
            setShowSupplierFormModal(false);
            setIsSupplierAddressEditing(false);
          }}
          initialValues={{ suppliedType: "brands", suppliedItems: [], carrierId: undefined }}
        >
          <Form.Item
            name="name"
            label="Nom du fournisseur"
            rules={[
              {
                required: true,
                message: "Veuillez saisir le nom du fournisseur",
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="country" label="Pays">
            <Input />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            rules={[{ type: "email", message: "Format email invalide" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="phone" label="Téléphone">
            <Input />
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prev: any, cur: any) => {
              const pa = prev?.address || {};
              const ca = cur?.address || {};
              return (
                pa.name !== ca.name ||
                pa.street1 !== ca.street1 ||
                pa.street2 !== ca.street2 ||
                pa.city !== ca.city ||
                pa.state !== ca.state ||
                pa.postalCode !== ca.postalCode ||
                pa.country !== ca.country ||
                pa.countryCode !== ca.countryCode ||
                pa.phone !== ca.phone ||
                pa.email !== ca.email
              );
            }}
          >
            {({ getFieldValue }: any) => {
              const a: any = getFieldValue(["address"]) || {};
              const hasAnyAddress =
                !!a.name ||
                !!a.street1 ||
                !!a.street2 ||
                !!a.city ||
                !!a.state ||
                !!a.postalCode ||
                !!a.country ||
                !!a.countryCode ||
                !!a.phone ||
                !!a.email;

              if (hasAnyAddress && !isSupplierAddressEditing) {
                const line1 = [a.street1, a.street2].filter(Boolean).join(" ");
                const line2 = [a.postalCode, a.city].filter(Boolean).join(" ");
                const line3 = [a.country, a.countryCode].filter(Boolean).join(" ");
                const contact = [a.name, a.phone, a.email]
                  .filter(Boolean)
                  .join(" · ");

                return (
                  <Card size="small" style={{ marginBottom: 12 }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 12,
                        alignItems: "flex-start",
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <Typography.Text strong>
                          Adresse (fournisseur)
                        </Typography.Text>
                        <div style={{ marginTop: 6 }}>
                          <div>{line1 || "-"}</div>
                          <div>{line2 || "-"}</div>
                          <div>{line3 || "-"}</div>
                          {contact ? (
                            <Typography.Text type="secondary">
                              {contact}
                            </Typography.Text>
                          ) : null}
                        </div>
                      </div>

                      <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => setIsSupplierAddressEditing(true)}
                      />
                    </div>
                  </Card>
                );
              }

              return (
                <Form.Item label="Adresse (fournisseur)" style={{ marginBottom: 8 }}>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 8,
                    }}
                  >
                    <Form.Item
                      name={["address", "name"]}
                      label="Nom / Contact"
                      style={{ marginBottom: 8 }}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      name={["address", "countryCode"]}
                      label="Code pays"
                      style={{ marginBottom: 8 }}
                    >
                      <Input placeholder="Ex: FR" />
                    </Form.Item>

                    <Form.Item
                      name={["address", "street1"]}
                      label="Adresse"
                      style={{ marginBottom: 8 }}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      name={["address", "street2"]}
                      label="Complément"
                      style={{ marginBottom: 8 }}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      name={["address", "postalCode"]}
                      label="Code postal"
                      style={{ marginBottom: 8 }}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      name={["address", "city"]}
                      label="Ville"
                      style={{ marginBottom: 8 }}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      name={["address", "state"]}
                      label="État / Région"
                      style={{ marginBottom: 8 }}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      name={["address", "country"]}
                      label="Pays"
                      style={{ marginBottom: 8 }}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      name={["address", "phone"]}
                      label="Téléphone (adresse)"
                      style={{ marginBottom: 8 }}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      name={["address", "email"]}
                      label="Email (adresse)"
                      rules={[{ type: "email", message: "Format email invalide" }]}
                      style={{ marginBottom: 8 }}
                    >
                      <Input />
                    </Form.Item>
                  </div>
                </Form.Item>
              );
            }}
          </Form.Item>
          <Form.Item name="paymentTerms" label="Délais de paiement">
            <Input placeholder="Ex: 30 jours" />
          </Form.Item>
          <Form.Item name="tvaRate" label="TVA (%)">
            <Select
              allowClear
              placeholder="Sélectionner"
              options={[0, 5.5, 10, 20].map((r) => ({ label: `${r}%`, value: r }))}
            />
          </Form.Item>
          <Form.Item name="paymentDelayDays" label="Délai de paiement (jours)">
            <InputNumber min={0} style={{ width: "100%" }} placeholder="Ex: 30" />
          </Form.Item>
          {/* Sélecteur de transporteur */}
          <Form.Item name="carrierId" label="Transporteur">
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
              }}
            >
              <Form.Item
                noStyle
                shouldUpdate={(prev: any, cur: any) => prev?.carrierId !== cur?.carrierId}
              >
                {({ getFieldValue, setFieldsValue }: any) => {
                  const carrierId = getFieldValue("carrierId");
                  return CARRIERS.map((carrier) => {
                    const selected = carrierId === carrier.id;
                    return (
                      <div
                        key={carrier.id}
                        className={`carrier-card${selected ? " selected" : ""}`}
                        onClick={() => setFieldsValue({ carrierId: carrier.id })}
                        style={{
                          minWidth: 140,
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <img
                          src={carrier.logoPath}
                          alt={carrier.name}
                          style={{
                            height: 24,
                            objectFit: "contain",
                          }}
                        />
                        <span style={{ fontSize: 13 }}>{carrier.name}</span>
                      </div>
                    );
                  });
                }}
              </Form.Item>
            </div>
          </Form.Item>
          <Form.Item label="Fourni" style={{ marginBottom: 8 }}>
            <Radio.Group
              value={supplierFormType}
              onChange={(e: any) => {
                setSupplierFormType(e.target.value);
                // Réinitialiser les sélections lors du changement
                supplierForm.setFieldsValue({ suppliedItems: [] });
                setSelectedProductIds([]);
              }}
              style={{ marginBottom: 16 }}
            >
              <Radio.Button value="brands">Marques</Radio.Button>
              <Radio.Button value="references">Références</Radio.Button>
            </Radio.Group>

            {supplierFormType === "brands" ? (
              <Form.Item name="suppliedItems" noStyle>
                <Select
                  mode="multiple"
                  style={{ width: "100%" }}
                  placeholder="Sélectionner des marques fournies"
                  loading={loadingProducts}
                  allowClear
                >
                  {availableVendors.map((vendor) => (
                    <Select.Option key={vendor} value={vendor}>
                      {vendor}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            ) : (
              <div>
                <Form.Item name="suppliedItems" hidden>
                  <Input />
                </Form.Item>

                {loadingProducts ? (
                  <Spin tip="Chargement des produits..." />
                ) : (
                  <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                    <Checkbox.Group
                      style={{ display: "flex", flexDirection: "column" }}
                      value={selectedProductIds}
                      onChange={(checkedValues: any) => {
                        const selectedIds = checkedValues as string[];
                        setSelectedProductIds(selectedIds);
                        supplierForm.setFieldsValue({
                          suppliedItems: selectedIds,
                        });
                      }}
                    >
                      {allProducts.map((product) => (
                        <Checkbox key={product.id} value={product.id}>
                          {product.title}{" "}
                          {product.vendor ? `(${product.vendor})` : ""}
                        </Checkbox>
                      ))}
                    </Checkbox.Group>
                  </div>
                )}
              </div>
            )}
          </Form.Item>
          <Form.Item>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "8px",
              }}
            >
              <Button onClick={() => setShowSupplierFormModal(false)}>
                Annuler
              </Button>
              <Button type="primary" htmlType="submit">
                {currentSupplier ? "Modifier" : "Ajouter"}
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modale pour la liste des destinations */}
      <Modal
        title="Destinations"
        open={showAddressModal}
        onCancel={() => setShowAddressModal(false)}
        width={900}
        footer={[
          <Button key="close" onClick={() => setShowAddressModal(false)}>
            Fermer
          </Button>,
        ]}
      >
        {addressList && addressList.length > 0 ? (
          <Table
            dataSource={addressList}
            rowKey="id"
            columns={[
              {
                title: "Distributeur",
                dataIndex: "retailerName",
                key: "retailerName",
              },
              {
                title: "Destination",
                dataIndex: "name",
                key: "name",
              },
              {
                title: "Logistique",
                dataIndex: "fulfillmentMode",
                key: "fulfillmentMode",
                render: (mode) => {
                  const label =
                    mode === "multi_hub"
                      ? "Multi-hubs"
                      : mode === "per_channel"
                      ? "Par canal"
                      : "Hub unique";
                  return <Tag color="blue">{label}</Tag>;
                },
              },
              {
                title: "Canal",
                key: "channel",
                render: (_, record) =>
                  record.channelKey || record.channelType ? (
                    <Space>
                      {record.channelKey && <Tag color="geekblue">{record.channelKey}</Tag>}
                      {record.channelType && <Tag color="purple">{record.channelType}</Tag>}
                    </Space>
                  ) : (
                    "-"
                  ),
              },
              {
                title: "Adresse",
                key: "address",
                render: (_, record) => (
                  <span>
                    {record.streetAddress}, {record.postalCode} {record.city} {record.country}
                  </span>
                ),
              },
              {
                title: "Actions",
                key: "actions",
                render: (_, record) => (
                  <Button
                    size="small"
                    disabled={!record.retailerId}
                    onClick={() => openRetailerLogisticsModal(record.retailerId)}
                  >
                    Modifier logistique
                  </Button>
                ),
              },
            ]}
            pagination={{ pageSize: 6 }}
          />
        ) : (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <Typography.Title
              level={4}
              type="secondary"
              style={{ fontWeight: "normal" }}
            >
              Aucune destination configurée dans les distributeurs
            </Typography.Title>
          </div>
        )}
      </Modal>

      <style>{`
        .supply-cockpit {
          padding: 24px;
          min-height: 100vh;
          background: #f8fafc;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
        }
        .display-flex {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 32px;
        }
        .supply-card-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          margin-bottom: 16px;
        }
        .display-flex h2 {
          margin: 0 !important;
          font-weight: 800;
          letter-spacing: -0.02em;
          color: #0f172a;
        }
        .neo-card {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
          transition: all 0.2s ease;
        }
        .neo-card:hover {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
        }
        .trend-up { color: #10b981; font-weight: 600; font-size: 13px; }
        .trend-warning { color: #faad14; font-weight: 600; font-size: 13px; }
        .trend-danger { color: #ef4444; font-weight: 600; font-size: 13px; }
        
        .card-compact {
          text-align: center;
          cursor: pointer;
        }
        .card-icon {
          width: 48px;
          height: 48px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 16px;
          font-size: 24px;
        }

        .neo-main-tabs .ant-tabs-nav {
          margin-bottom: 0 !important;
        }
        .neo-main-tabs .ant-tabs-tab {
          padding: 12px 16px !important;
          font-weight: 600 !important;
        }
        .neo-main-tabs .ant-tabs-tab-active {
          color: #0f172a !important;
        }
        .neo-main-tabs .ant-tabs-tab-btn:hover {
          color: #0f172a70 !important;
        }
        .neo-main-tabs .ant-tabs-tab-btn {
          color: #0f172a70 !important;
        }
        .neo-main-tabs .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
          color: #0f172a !important;
        }
        .neo-main-tabs .ant-tabs-ink-bar {
          background: #0f172a !important;
          height: 3px !important;
        }
        
        /* Table Styles */
        .high-tech-table .ant-table {
          background: transparent !important;
        }
        .high-tech-table .ant-table-thead > tr > th {
          background: #f8fafc !important;
          color: #64748b !important;
          font-size: 12px !important;
          text-transform: uppercase !important;
          letter-spacing: 0.05em !important;
          border-bottom: 1px solid #e2e8f0 !important;
          padding: 16px !important;
        }
        .high-tech-table .ant-table-tbody > tr > td {
          border-bottom: 1px solid #f1f5f9 !important;
          padding: 16px !important;
        }
        
        /* Premium Modal Styles */
        .premium-modal .ant-modal-content {
          border-radius: 16px !important;
          padding: 0 !important;
          overflow: hidden;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
        }
        .premium-modal .ant-modal-header {
          margin: 0 !important;
          padding: 24px 32px !important;
          border-bottom: 1px solid #f1f5f9;
        }
        .edit-form-header {
          display: flex;
          justify-content: space-between;
          align-items: baseline;
          margin-bottom: 16px;
        }
        .premium-modal .ant-modal-title {
          font-size: 20px !important;
          font-weight: 700 !important;
          color: #0f172a !important;
          letter-spacing: -0.02em;
        }
        .premium-modal .ant-modal-body {
          padding: 0 !important;
        }
        .premium-modal .ant-modal-footer {
          padding: 16px 32px !important;
          border-top: 1px solid #f1f5f9;
          margin: 0 !important;
          background: #f8fafc;
        }
        
        .invoice-container {
          padding: 32px;
          background: #fff;
        }
        .invoice-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 40px;
        }
        .invoice-brand {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .invoice-title {
          font-size: 12px;
          font-weight: 800;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.1em;
        }
        .invoice-ref {
          font-size: 28px;
          font-weight: 800;
          color: #0f172a;
          letter-spacing: -0.03em;
        }
        .invoice-status-badge {
          padding: 6px 12px;
          border-radius: 9999px;
          font-size: 12px;
          font-weight: 700;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        
        .invoice-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 40px;
          margin-bottom: 40px;
          padding: 24px;
          background: #f8fafc;
          border-radius: 16px;
        }
        .invoice-info-block {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .invoice-info-label {
          font-size: 11px;
          font-weight: 700;
          color: #94a3b8;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
        .invoice-info-value {
          font-size: 14px;
          font-weight: 600;
          color: #334155;
        }
        
        .invoice-table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0;
          margin-bottom: 32px;
        }
        .invoice-table th {
          text-align: left;
          padding: 12px 16px;
          font-size: 11px;
          font-weight: 700;
          color: #94a3b8;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          border-bottom: 1px solid #f1f5f9;
        }
        .invoice-table td {
          padding: 16px;
          font-size: 14px;
          border-bottom: 1px solid #f1f5f9;
          color: #475569;
        }
        .invoice-table tr:last-child td {
          border-bottom: none;
        }
        
        .invoice-summary {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 12px;
          padding-top: 24px;
          border-top: 2px solid #0f172a;
        }
        .summary-row {
          display: flex;
          justify-content: space-between;
          width: 240px;
          font-size: 14px;
        }
        .summary-row.total {
          font-size: 20px;
          font-weight: 800;
          color: #0f172a;
          margin-top: 8px;
        }
        
        .neo-edit-form .neo-form-section {
          background: #fff;
          padding: 24px;
          border-radius: 16px;
          border: 1px solid #f1f5f9;
          margin-bottom: 24px;
        }
        .neo-edit-form .section-title {
          display: block;
          font-size: 12px;
          font-weight: 800;
          color: #94a3b8;
          letter-spacing: 0.05em;
          margin-bottom: 20px;
          text-transform: uppercase;
        }
        .neo-input, .neo-select, .neo-datepicker, .neo-input-number {
          border-radius: 10px !important;
          border: 1px solid #e2e8f0 !important;
          padding: 6px 12px !important;
          width: 100% !important;
        }
        .neo-input:focus, .neo-select:focus {
          border-color: #3b82f6 !important;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
        }
          border: none !important;
          border-radius: 8px !important;
          font-weight: 600 !important;
          height: 40px;
        }
        .ant-btn-primary:hover {
          background: #1e293b !important;
          transform: translateY(-1px);
        }
        
        /* Filters */
        .filter-section {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
          padding: 16px;
          background: #f8fafc;
          border-radius: 8px;
          margin-bottom: 16px;
          border: 1px solid #e2e8f0;
        }
        .neo-filters {
          background: #ffffff;
          padding: 12px 16px;
          margin-bottom: 20px;
          border-radius: 12px;
        }

        /* Neo Form Styles */
        .neo-form-section {
          background: #ffffff;
          padding: 24px;
          border-radius: 12px;
          border: 1px solid #e2e8f0;
          margin-bottom: 24px;
        }
        .section-title {
          display: block;
          margin-bottom: 16px;
          font-size: 11px;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          font-weight: 700;
        }
        .neo-radio-group {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
          width: 100%;
        }
        .neo-radio-button {
          height: auto !important;
          padding: 16px !important;
          border-radius: 12px !important;
          border: 1px solid #e2e8f0 !important;
          transition: all 0.2s ease !important;
        }
        .neo-radio-button .button-content {
          display: flex;
          align-items: center;
          gap: 16px;
          text-align: left;
          white-space: normal;
        }
        .icon-wrapper {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
        }
        .icon-wrapper.inbound { background: #eff6ff; color: #3b82f6; }
        .icon-wrapper.outbound { background: #fff7ed; color: #f59e0b; }
        .text-wrapper .title { display: block; font-weight: 700; color: #0f172a; font-size: 15px; }
        .text-wrapper .description { font-size: 12px; color: #64748b; }

        .neo-select .ant-select-selector,
        .neo-datepicker,
        .neo-textarea,
        .neo-search-input {
        }
        .neo-select .ant-select-selector:hover,
        .neo-datepicker:hover,
        .neo-textarea:hover,
        .neo-search-input:hover {
          border-color: #94a3b8 !important;
        }

        /* Destination dropdown logic improvement */
        .ant-select-single.ant-select-sm:not(.ant-select-customize-input) .ant-select-selector {
          border-radius: 6px;
        }
        .ant-form-item-label > label {
          font-size: 11px !important;
          color: #64748b !important;
          height: auto !important;
        }
        .neo-form-section:last-of-type {
          border-bottom: none;
        }
        .neo-product-selection {
          display: flex;
          flex-direction: column;
          gap: 16px;
          height: 100%;
          min-height: 0;
        }
        .selection-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: #ffffff;
          padding: 16px;
          border-radius: 12px;
          border: 1px solid #e2e8f0;
          flex-shrink: 0;
        }
        .search-bar-wrapper { flex: 1; max-width: 400px; }
        .neo-table-wrapper {
          background: #ffffff;
          border-radius: 12px;
          border: 1px solid #e2e8f0;
          overflow: hidden;
          flex: 1;
          display: flex;
          flex-direction: column;
          min-height: 0;
        }
        .neo-table-wrapper .ant-table-wrapper {
          flex: 1;
          display: flex;
          flex-direction: column;
          min-height: 0;
        }
        .neo-table-wrapper .ant-spin-nested-loading,
        .neo-table-wrapper .ant-spin-container {
          flex: 1;
          display: flex;
          flex-direction: column;
          min-height: 0;
        }
        .neo-table-wrapper .ant-table {
          flex: 1;
          display: flex;
          flex-direction: column;
          min-height: 0;
        }
        .neo-table-wrapper .ant-table-container {
          flex: 1;
          min-height: 0;
        }
        .neo-product-selection .ant-table-thead > tr > th {
          position: sticky !important;
          top: 0 !important;
          z-index: 5 !important;
          background: #f8fafc !important;
          box-shadow: inset 0 -1px 0 #e2e8f0;
        }
        .neo-product-selection .ant-table-container {
          border-radius: 14px !important;
        }
        .neo-product-selection .ant-table-tbody > tr > td:first-child,
        .neo-product-selection .ant-table-thead > tr > th:first-child {
          padding-left: 18px !important;
        }
        .neo-product-selection .ant-table-tbody > tr > td:last-child,
        .neo-product-selection .ant-table-thead > tr > th:last-child {
          padding-right: 18px !important;
        }
        .po-edit-form {
          padding: 18px 24px 22px !important;
          background: #f8fafc;
        }
        .po-edit-form .neo-form-section {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 14px;
          padding: 16px;
          margin-bottom: 14px;
          box-shadow: 0 1px 3px rgba(15, 23, 42, 0.05);
        }
        .po-edit-form .section-title {
          margin-bottom: 12px;
          color: #0f172a;
          font-size: 12px;
          font-weight: 800;
          letter-spacing: 0.06em;
          text-transform: uppercase;
        }
        .po-edit-form .ant-form-item {
          margin-bottom: 12px;
        }
        .po-edit-form .ant-form-item-label {
          padding-bottom: 4px;
        }
        .po-edit-form .ant-form-item-label > label {
          color: #64748b !important;
          font-size: 11px !important;
          font-weight: 700 !important;
        }
        .po-edit-form .ant-input,
        .po-edit-form .ant-input-number,
        .po-edit-form .ant-picker,
        .po-edit-form .ant-select-selector {
          width: 100% !important;
          min-height: 36px !important;
          border-radius: 9px !important;
        }
        .po-edit-form .ant-select-selection-item,
        .po-edit-form .ant-select-selection-placeholder {
          overflow: hidden !important;
          text-overflow: ellipsis !important;
          white-space: nowrap !important;
          max-width: 100% !important;
        }
        .po-edit-form .ant-input,
        .po-edit-form .ant-input-number-input {
          height: 34px !important;
          font-size: 13px !important;
        }
        .po-edit-form textarea.ant-input {
          min-height: auto !important;
        }
        .po-edit-form .invoice-table {
          margin-top: 0;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          overflow: hidden;
        }
        .po-edit-form .invoice-table th {
          padding: 9px 10px !important;
          font-size: 10px !important;
          background: #f8fafc !important;
        }
        .po-edit-form .invoice-table td {
          padding: 8px 10px !important;
          vertical-align: top;
        }
        .neo-table-wrapper .ant-table-body {
          overflow-y: auto !important;
          max-height: none !important;
        }
        .neo-table-wrapper .ant-pagination {
          flex-shrink: 0;
          margin: 16px !important;
        }
        .product-avatar {
          width: 32px;
          height: 32px;
          background: #f1f5f9;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #475569;
          font-weight: 700;
          font-size: 14px;
        }
        .product-meta { display: flex; align-items: center; gap: 8px; margin-top: 4px; }
        .sku { font-size: 11px; }
        .neo-input-number { border-radius: 6px !important; }
        .parcels-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
          margin-top: 16px;
          padding: 16px;
          background: #f8fafc;
          border-radius: 8px;
          border: 1px dashed #e2e8f0;
        }
        .parcel-item { margin-bottom: 0 !important; }

        /* Drawer & Steps Styles */
        .neo-drawer .ant-drawer-header {
          border-bottom: 1px solid #f1f5f9;
          padding: 20px 24px;
        }
        .drawer-header-icon {
          width: 40px;
          height: 40px;
          background: #0f172a;
          color: white;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
        }
        .neo-steps {
          padding: 16px 24px;
          background: #f8fafc;
          border-radius: 12px;
          margin-bottom: 20px !important;
          border: 1px solid #f1f5f9;
        }
        .neo-steps .ant-steps-item-title {
          font-weight: 600 !important;
          color: #64748b !important;
        }
        .neo-steps .ant-steps-item-active .ant-steps-item-title {
          color: #0f172a !important;
        }
        
        /* Buttons */
        .neo-btn-primary {
          background: #0f172a !important;
          border: none !important;
          height: 44px !important;
          padding: 0 24px !important;
          border-radius: 10px !important;
          font-weight: 600 !important;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1) !important;
        }
        .neo-btn-secondary {
          height: 44px !important;
          padding: 0 24px !important;
          border-radius: 10px !important;
          font-weight: 600 !important;
          border: 1px solid #e2e8f0 !important;
          color: #475569 !important;
        }
        .neo-btn-icon {
          width: 40px !important;
          height: 40px !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          border-radius: 10px !important;
          border: 1px solid #e2e8f0 !important;
          color: #64748b !important;
        }

        .drawer-content-inner {
          display: flex;
          flex-direction: column;
          height: calc(100vh - 200px);
          overflow: hidden;
        }
        
        .steps-wrapper {
          flex-shrink: 0;
        }
        
        .step-content-container {
          padding: 0 4px;
          flex: 1;
          overflow-y: auto;
          min-height: 0;
        }
        
        .neo-pagination .ant-pagination-item-active {
          border-color: #0f172a !important;
        }
        .neo-pagination .ant-pagination-item-active a {
          color: #0f172a !important;
        }
        
        /* Recap Step Styles */
        .neo-recap-section {
          display: flex;
          flex-direction: column;
          gap: 24px;
        }
        .recap-header {
          text-align: center;
          padding: 24px;
          background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
          border-radius: 12px;
        }
        .recap-info-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
        }
        .recap-card {
          background: #ffffff;
          padding: 16px;
          border-radius: 12px;
          border: 1px solid #e2e8f0;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .recap-products {
          background: #ffffff;
          padding: 20px;
          border-radius: 12px;
          border: 1px solid #e2e8f0;
        }
        .recap-products-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
          max-height: 400px;
          overflow-y: auto;
        }
        .recap-product-item {
          padding: 16px;
          background: #f8fafc;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
        }
        .recap-total {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 24px;
          background: #0f172a;
          color: white;
          border-radius: 12px;
        }
        .recap-total .ant-typography {
          color: white !important;
        }

        .premium-modal .ant-modal-content {
          border-radius: 16px;
          padding: 0;
          overflow: hidden;
          border: 1px solid #e2e8f0;
          box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1);
        }
        .premium-modal .ant-modal-body {
          padding: 0;
          overflow: hidden;
        }
        .premium-modal .ant-modal-header {
          margin: 0;
          padding: 20px 24px;
          border-bottom: 1px solid #f1f5f9;
        }
        .premium-modal .ant-modal-footer {
          margin: 0;
          padding: 16px 24px;
          border-top: 1px solid #f1f5f9;
          background: #f8fafc;
        }
        .premium-input {
          border-radius: 8px !important;
          border: 1px solid #e2e8f0 !important;
          font-weight: 500 !important;
          height: 38px !important;
          transition: all 0.2s;
        }
        .premium-input:hover {
          border-color: #94a3b8 !important;
          background: #f1f5f9 !important;
        }
        .premium-input-primary {
          background: #0f172a !important;
          border-color: #0f172a !important;
          color: white !important;
          height: 38px !important;
          border-radius: 8px !important;
        }
        .premium-input-primary:hover {
          background: #1e293b !important;
          opacity: 0.9;
        }
        
        /* Invoice Styling */
        .invoice-container {
          background: white;
          padding: 28px 32px 32px;
          color: #1e293b;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        }
        .invoice-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 28px;
        }
        .invoice-brand .invoice-title {
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: #64748b;
          display: block;
          margin-bottom: 4px;
        }
        .invoice-brand .invoice-ref {
          font-size: 22px;
          font-weight: 800;
          color: #0f172a;
          display: block;
          letter-spacing: -0.02em;
        }
        .invoice-status-badge {
          padding: 6px 14px;
          border-radius: 9999px;
          font-size: 12px;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .invoice-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 20px;
          margin-bottom: 28px;
          padding: 16px 18px;
          background: #f8fafc;
          border-radius: 12px;
          border: 1px solid #f1f5f9;
        }
        .invoice-info-block {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .invoice-info-label {
          font-size: 10px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: #94a3b8;
        }
        .invoice-info-value {
          font-size: 14px;
          font-weight: 600;
          color: #1e293b;
        }
        .invoice-table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 24px;
        }
        .invoice-table th {
          text-align: left;
          padding: 12px 16px;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: #64748b;
          border-bottom: 2px solid #f1f5f9;
        }
        .invoice-table td {
          padding: 14px 12px;
          border-bottom: 1px solid #f1f5f9;
          vertical-align: top;
        }
        .invoice-table tr:last-child td {
          border-bottom: none;
        }
        .invoice-summary {
          margin-left: auto;
          width: 300px;
          display: flex;
          flex-direction: column;
          gap: 10px;
          padding: 18px 20px;
          background: #f8fafc;
          border-radius: 12px;
        }
        .summary-row {
          display: flex;
          justify-content: space-between;
          font-size: 14px;
          color: #64748b;
        }
        .summary-row.total {
          margin-top: 6px;
          padding-top: 12px;
          border-top: 1px solid #e2e8f0;
          color: #0f172a;
          font-weight: 700;
          font-size: 16px;
        }
        
        /* Edit Form Styling */
        .neo-edit-form .neo-form-section {
          background: white;
          padding: 18px 20px;
          border-radius: 12px;
          border: 1px solid #f1f5f9;
          margin-bottom: 16px;
        }
        .neo-edit-form .section-title {
          font-size: 12px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: #64748b;
          display: block;
          margin-bottom: 14px;
          padding-bottom: 8px;
          border-bottom: 1px solid #f1f5f9;
        }
        .neo-input, .neo-select .ant-select-selector, .ant-picker.neo-datepicker, .ant-input-number.neo-input-number {
          border-radius: 8px !important;
          padding: 8px 12px !important;
          height: auto !important;
          transition: all 0.2s !important;
        }
        .neo-input:focus, .neo-select-focused .ant-select-selector {
          border-color: #0f172a !important;
          box-shadow: 0 0 0 2px rgb(15 23 42 / 0.1) !important;
          background: white !important;
        }
        .parcels-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 16px;
          margin-top: 16px;
          padding: 16px;
          background: #f8fafc;
          border-radius: 8px;
        }
      `}</style>

      {/* Modal pour afficher les détails complets d'un bon de commande en style facture */}
      <Modal
        title={isEditMode ? "Modifier le bon de commande" : null}
        open={showDetailsModal}
        onCancel={() => {
          setShowDetailsModal(false);
          setIsEditMode(false);
        }}
        width={1000}
        className="premium-modal"
        styles={{ body: { padding: 0, maxHeight: "84vh", overflowY: "auto" } }}
        footer={[
          <div key="footer-actions" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <div>
              <Button
                onClick={() => {
                  setShowDetailsModal(false);
                  setIsEditMode(false);
                }}
                className="premium-input"
              >
                Fermer
              </Button>
            </div>
            <Space>
              {!isEditMode && selectedOrder && (
                <>
                  {pennylanePrefetchLoading && (
                    <Button loading className="premium-input">
                      Chargement factures...
                    </Button>
                  )}
                  <Button
                    icon={<ReloadOutlined />}
                    loading={!!shippingEstimateByPoId[String(selectedOrder._id || selectedOrder.id || '')]?.loading}
                    className="premium-input"
                    onClick={async () => {
                      const poId = String(selectedOrder._id || selectedOrder.id || '').trim();
                      if (!poId) {
                        message.error('Impossible de faire un devis: bon de commande sans ID');
                        return;
                      }
                      setShippingEstimateByPoId(prev => ({ ...prev, [poId]: { loading: true, error: null } }));
                      try {
                        const resp = await purchaseOrderService.getPurchaseOrderShippingEstimate(poId);
                        if (resp?.purchaseOrder) {
                          setSelectedOrder(prev => prev ? ({ ...prev, ...resp.purchaseOrder } as any) : prev);
                          setPurchaseOrders(prev => prev.map(po => String(po._id || po.id) === poId ? ({ ...po, ...resp.purchaseOrder } as any) : po));
                        }
                        message.success('Estimation mise à jour');
                      } catch (e: any) {
                        message.error(e?.message || "Erreur d'estimation");
                      } finally {
                        setShippingEstimateByPoId(prev => ({ ...prev, [poId]: { loading: false, error: null } }));
                      }
                    }}
                  >
                    Estimer livraison
                  </Button>
                  <Button
                    icon={<PlusOutlined />}
                    className="premium-input"
                    onClick={() => {
                      const cost = prompt("Coût d'importation/expédition (€)", selectedOrder.importCost?.toString() || "0");
                      const val = parseFloat(cost || "");
                      if (!isNaN(val) && val >= 0) {
                        purchaseOrderService.updatePurchaseOrderImportCost((selectedOrder._id || selectedOrder.id)!, val)
                          .then(() => {
                            message.success("Frais mis à jour");
                            fetchPurchaseOrders();
                            purchaseOrderService.getPurchaseOrderById((selectedOrder._id || selectedOrder.id)!).then(setSelectedOrder);
                          })
                          .catch(err => message.error(err.message));
                      }
                    }}
                  >
                    Frais
                  </Button>
                  <Button
                    icon={<FileTextOutlined />}
                    loading={isImportingBatches}
                    className="premium-input"
                    onClick={() => {
                      const input = document.createElement("input");
                      input.type = "file";
                      input.accept = ".csv";
                      input.onchange = () => {
                        const file = input.files?.[0];
                        if (file) handleImportBatchesFile(file);
                      };
                      input.click();
                    }}
                  >
                    Import CSV
                  </Button>
                  {String(selectedOrder.type || "").toLowerCase() === "outbound" && (
                    <Button
                      icon={<FileTextOutlined />}
                      className="premium-input"
                      loading={pennylanePrefetchLoading}
                      onClick={() => {
                        if (selectedOrder) openPennylaneModalForOrder(selectedOrder, { fromDetails: true });
                      }}
                    >
                      Factures
                    </Button>
                  )}
                </>
              )}
              {selectedOrder && !isEditMode && (
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  className="premium-input-primary"
                  onClick={() => {
                    purchaseOrderForm.setFieldsValue({
                      reference: selectedOrder.reference,
                      supplier: selectedOrder.supplier,
                      deliveryAddress: selectedOrder.deliveryAddress?.name || "",
                      expectedDeliveryDate: selectedOrder.expectedDeliveryDate ? dayjs(selectedOrder.expectedDeliveryDate) : undefined,
                      importCost: selectedOrder.importCost || 0,
                      deliveryInfo: selectedOrder.deliveryAddress?.deliveryInfo || "",
                      deliveryComment: selectedOrder.deliveryAddress?.comment || "",
                      carrierId: selectedOrder.carrierId,
                      trackingNumber: selectedOrder.trackingNumber,
                      retailerId: selectedOrder.retailerId,
                      retrocessionRate: selectedOrder.retrocessionRate,
                      parcelCount: selectedOrder.parcelCount || 1,
                      parcels: selectedOrder.parcels || [],
                      items: selectedOrder.items || [],
                    });
                    setIsEditMode(true);
                  }}
                >
                  Modifier
                </Button>
              )}
              {isEditMode && (
                <>
                  <Button className="premium-input" onClick={() => setIsEditMode(false)}>Annuler</Button>
                  <Button
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    className="premium-input-primary"
                    onClick={() => {
                      purchaseOrderForm.validateFields().then(values => {
                        if (!selectedOrder) return;
                        const parcelCount = Number(values.parcelCount || 1);
                        const normalizedParcels = Array.from({ length: parcelCount }).map((_, i) => ({
                          trackingNumber: values.parcels?.[i]?.trackingNumber || values.trackingNumber || ""
                        }));
                        const matchedAddr = addressList.find(a => a.name === values.deliveryAddress);
                        const normalizedItems = (values.items || selectedOrder.items || []).map((item: any, index: number) => {
                          const originalItem = selectedOrder.items?.[index] || {};
                          const quantity = Number(item.quantity ?? originalItem.quantity ?? 0);
                          const unitPrice = Number(item.unitPrice ?? originalItem.unitPrice ?? 0);
                          const supplierUnitPrice = Number(item.supplierUnitPrice ?? originalItem.supplierUnitPrice ?? 0);
                          return {
                            ...originalItem,
                            ...item,
                            quantity,
                            unitPrice,
                            totalPrice: roundTo2(quantity * unitPrice),
                            supplierUnitPrice,
                            supplierTotalPrice: roundTo2(quantity * supplierUnitPrice),
                          };
                        });
                        const totalAmount = roundTo2(
                          normalizedItems.reduce((sum: number, item: any) => sum + Number(item.totalPrice || 0), 0)
                        );
                        const updated = {
                          ...values,
                          expectedDeliveryDate: values.expectedDeliveryDate?.toDate(),
                          items: normalizedItems,
                          totalAmount,
                          deliveryAddress: matchedAddr ? {
                            name: matchedAddr.name,
                            street: matchedAddr.streetAddress,
                            city: matchedAddr.city,
                            postalCode: matchedAddr.postalCode,
                            country: matchedAddr.country,
                            locationId: matchedAddr.id,
                            deliveryInfo: values.deliveryInfo || matchedAddr.deliveryInfo || "",
                            comment: values.deliveryComment || matchedAddr.comment || "",
                          } : {
                            name: values.deliveryAddress,
                            locationId: selectedOrder.locationId,
                            deliveryInfo: values.deliveryInfo || "",
                            comment: values.deliveryComment || "",
                          },
                          parcels: normalizedParcels,
                        };
                        const id = selectedOrder._id || selectedOrder.id;
                        if (id) {
                          purchaseOrderService.updatePurchaseOrder(id, updated)
                            .then(() => {
                              message.success("Bon de commande mis à jour");
                              setIsEditMode(false);
                              fetchPurchaseOrders();
                              purchaseOrderService.getPurchaseOrderById(id).then(setSelectedOrder);
                            })
                            .catch(err => message.error(err.message));
                        }
                      });
                    }}
                  >
                    Enregistrer
                  </Button>
                </>
              )}
            </Space>
          </div>
        ]}
      >
        {selectedOrder ? (
          <div>
            {isEditMode ? (
              <Form
                form={purchaseOrderForm}
                layout="vertical"
                className="po-edit-form neo-edit-form"
                requiredMark={false}
              >
                <div className="neo-form-section">
                  <div className="section-title">Informations Générales</div>
                  <Row gutter={24}>
                    <Col span={12}>
                      <Form.Item name="reference" label="Référence" rules={[{ required: true, message: 'Requis' }]}>
                        <Input className="neo-input" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="supplier" label="Fournisseur" rules={[{ required: true, message: 'Requis' }]}>
                        <Input className="neo-input" />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={24}>
                    <Col span={12}>
                      <Form.Item name="retailerId" label="Distributeur">
                        <Select
                          showSearch
                          placeholder="Lier à un distributeur"
                          className="neo-select"
                          options={supplyRetailers.map(r => ({ label: r.name, value: r.id || r._id }))}
                          filterOption={(input, option) => (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="retrocessionRate" label="Taux Rétrocession (%)">
                        <InputNumber min={0} max={100} style={{ width: '100%' }} className="neo-input-number" placeholder="Défaut fournisseur" />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={24}>
                    <Col span={12}>
                      <Form.Item name="expectedDeliveryDate" label="Livraison prévue">
                        <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} className="neo-datepicker" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="importCost" label="Frais d'importation (€)">
                        <InputNumber precision={2} style={{ width: '100%' }} className="neo-input-number" />
                      </Form.Item>
                    </Col>
                  </Row>
                </div>

                <div className="neo-form-section">
                  <div className="section-title">Produits commandés</div>
                  <Form.List name="items">
                    {(fields) => (
                      <div style={{ overflowX: 'auto' }}>
                        <table className="invoice-table" style={{ minWidth: 760 }}>
                          <thead>
                            <tr>
                              <th>Produit</th>
                              <th>Référence / SKU</th>
                              <th>Qté</th>
                              <th>Prix unitaire</th>
                              <th>Prix fournisseur</th>
                              <th>Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {fields.map((field) => {
                              const item = purchaseOrderForm.getFieldValue(['items', field.name]) || selectedOrder.items?.[field.name] || {};
                              return (
                                <tr key={field.key}>
                                  <td>
                                    <Typography.Text strong ellipsis style={{ maxWidth: 220, display: 'block' }}>
                                      {item.productName || 'Produit'}
                                    </Typography.Text>
                                  </td>
                                  <td>
                                    <Space direction="vertical" size={2} style={{ width: '100%' }}>
                                      <Typography.Text ellipsis style={{ maxWidth: 180, display: 'block', fontSize: 12 }}>
                                        SKU: {item.sku || 'N/A'}
                                      </Typography.Text>
                                      <Typography.Text type="secondary" ellipsis style={{ maxWidth: 180, display: 'block', fontSize: 11 }}>
                                        EAN: {item.barcode || 'N/A'}
                                      </Typography.Text>
                                    </Space>
                                  </td>
                                  <td>
                                    <Form.Item name={[field.name, 'quantity']} style={{ margin: 0 }}>
                                      <InputNumber min={0} precision={0} className="neo-input-number" style={{ width: 82 }} />
                                    </Form.Item>
                                  </td>
                                  <td style={{ textAlign: 'right' }}>
                                    {Number(item.unitPrice || 0).toFixed(2)}
                                  </td>
                                  <td style={{ textAlign: 'right' }}>
                                    {Number(item.supplierUnitPrice || 0).toFixed(2)}
                                  </td>
                                  <td style={{ textAlign: 'right', fontWeight: 700 }}>
                                    <Form.Item noStyle shouldUpdate>
                                      {() => {
                                        const currentItem = purchaseOrderForm.getFieldValue(['items', field.name]) || item;
                                        return `${roundTo2(Number(currentItem.quantity || 0) * Number(item.unitPrice || 0)).toLocaleString('fr-FR')} €`;
                                      }}
                                    </Form.Item>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </Form.List>
                </div>

                <div className="neo-form-section">
                  <div className="section-title">Logistique & Suivi</div>
                  <Row gutter={12}>
                    <Col span={24}>
                      <Form.Item name="deliveryAddress" label="Adresse de livraison">
                        <Select
                          showSearch
                          className="neo-select"
                          style={{ width: '100%' }}
                          optionLabelProp="label"
                          options={addressList.map(a => ({ label: a.name, value: a.name }))}
                          filterOption={(input, option) => (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())}
                          onChange={(val) => {
                            const addr = addressList.find(a => a.name === val);
                            if (addr) {
                              purchaseOrderForm.setFieldsValue({
                                deliveryInfo: addr.deliveryInfo || purchaseOrderForm.getFieldValue('deliveryInfo'),
                                deliveryComment: addr.comment || purchaseOrderForm.getFieldValue('deliveryComment')
                              });
                            }
                          }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={12}>
                    <Col span={8}>
                      <Form.Item name="carrierId" label="Transporteur">
                        <Select className="neo-select" style={{ width: '100%' }} options={CARRIERS.map(c => ({ label: c.name, value: c.id }))} />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item name="trackingNumber" label="N° Suivi Principal">
                        <Input className="neo-input" />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item name="parcelCount" label="Nombre de colis">
                        <InputNumber min={1} style={{ width: '100%' }} className="neo-input-number" />
                      </Form.Item>
                    </Col>
                  </Row>
                  
                  <Form.List name="parcels">
                    {(fields) => {
                      const count = purchaseOrderForm.getFieldValue('parcelCount') || 1;
                      if (count <= 1) return null;
                      return (
                        <div className="parcels-grid">
                          {fields.map((field, index) => {
                            if (index === 0) return null;
                            return (
                              <Form.Item {...field} key={field.key} name={[field.name, 'trackingNumber']} label={`Colis #${index + 1}`} style={{ marginBottom: 0 }}>
                                <Input placeholder="Tracking" className="neo-input" />
                              </Form.Item>
                            );
                          })}
                        </div>
                      );
                    }}
                  </Form.List>
                </div>

                <div className="neo-form-section" style={{ marginBottom: 0 }}>
                  <div className="section-title">Notes & Commentaires</div>
                  <Row gutter={24}>
                    <Col span={12}>
                      <Form.Item name="deliveryInfo" label="Infos livraison">
                        <Input.TextArea rows={3} className="neo-textarea" style={{ borderRadius: 10 }} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="deliveryComment" label="Notes internes">
                        <Input.TextArea rows={3} className="neo-textarea" style={{ borderRadius: 10 }} />
                      </Form.Item>
                    </Col>
                  </Row>
                </div>
              </Form>
            ) : (
              <div className="invoice-container">
                <div className="invoice-header">
                  <div className="invoice-brand">
                    <span className="invoice-title">BON DE COMMANDE</span>
                    <span className="invoice-ref">{selectedOrder.reference}</span>
                    <Typography.Text type="secondary" style={{ fontSize: 13, marginTop: 8, display: 'block' }}>
                      Créé le {dayjs(selectedOrder.createdAt).format("DD MMMM YYYY")}
                    </Typography.Text>
                  </div>
                  <div className="invoice-status-badge" style={{ 
                    background: getStatusColor(selectedOrder.status || 'draft') + '15',
                    color: getStatusColor(selectedOrder.status || 'draft'),
                    border: `1px solid ${getStatusColor(selectedOrder.status || 'draft')}30`
                  }}>
                    <Badge color={getStatusColor(selectedOrder.status || 'draft')} />
                    {getStatusLabel(selectedOrder.status || 'draft')}
                  </div>
                </div>

                <div className="invoice-grid">
                  <div className="invoice-info-block">
                    <span className="invoice-info-label">Source</span>
                    <span className="invoice-info-value">
                      {selectedOrder.type === "outbound" ? selectedOrder.sourceLocationName : selectedOrder.supplier}
                    </span>
                  </div>
                  <div className="invoice-info-block">
                    <span className="invoice-info-label">Destinataire</span>
                    <span className="invoice-info-value">{selectedOrder.deliveryAddress?.name || "N/A"}</span>
                    {selectedOrder.deliveryAddress?.street && (
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        {selectedOrder.deliveryAddress.street}, {selectedOrder.deliveryAddress.postalCode} {selectedOrder.deliveryAddress.city}
                      </Typography.Text>
                    )}
                  </div>
                  <div className="invoice-info-block">
                    <span className="invoice-info-label">Logistique</span>
                    <span className="invoice-info-value">
                      {CARRIERS.find(c => c.id === selectedOrder.carrierId)?.name || "Non assigné"}
                    </span>
                    {selectedOrder.trackingNumber && (
                      <div style={{ marginTop: 4 }}>
                        {(() => {
                          const carrier = CARRIERS.find(c => c.id === selectedOrder.carrierId);
                          if (carrier?.trackingUrlTemplate) {
                            const url = carrier.trackingUrlTemplate.replace("{trackingNumber}", encodeURIComponent(selectedOrder.trackingNumber || ""));
                            return <a href={url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12 }}>{selectedOrder.trackingNumber}</a>;
                          }
                          return <Typography.Text type="secondary" style={{ fontSize: 12 }}>Suivi: {selectedOrder.trackingNumber}</Typography.Text>;
                        })()}
                      </div>
                    )}
                  </div>
                  <div className="invoice-info-block">
                    <span className="invoice-info-label">Livraison prévue</span>
                    <span className="invoice-info-value">
                      {selectedOrder.expectedDeliveryDate 
                        ? dayjs(selectedOrder.expectedDeliveryDate).format("DD/MM/YYYY")
                        : "À confirmer"}
                    </span>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 14 }}>
                  <Card size="small" style={{ borderRadius: 12 }}>
                    <div style={{ fontSize: 12, color: '#64748b', marginBottom: 6 }}>Paiement entrant (client)</div>
                    {selectedOrder.incomingPayment?.invoice ? (
                      <Space direction="vertical" size={2}>
                        <Typography.Text strong>{selectedOrder.incomingPayment.invoice.invoiceNumber || selectedOrder.incomingPayment.invoice.invoiceId}</Typography.Text>
                        <Typography.Text type="secondary">
                          {selectedOrder.incomingPayment.invoice.amount?.toFixed?.(2)} {selectedOrder.incomingPayment.invoice.currency || "EUR"} • {selectedOrder.incomingPayment.invoice.status || "N/A"}
                        </Typography.Text>
                      </Space>
                    ) : (
                      <Typography.Text type="secondary">Aucune facture client rattachée</Typography.Text>
                    )}
                  </Card>
                  <Card size="small" style={{ borderRadius: 12 }}>
                    <div style={{ fontSize: 12, color: '#64748b', marginBottom: 6 }}>Paiement sortant (rétro fournisseur)</div>
                    {selectedOrder.outgoingPayment?.invoice ? (
                      <Space direction="vertical" size={2}>
                        <Typography.Text strong>{selectedOrder.outgoingPayment.invoice.invoiceNumber || selectedOrder.outgoingPayment.invoice.invoiceId}</Typography.Text>
                        <Typography.Text type="secondary">
                          {selectedOrder.outgoingPayment.invoice.amount?.toFixed?.(2)} {selectedOrder.outgoingPayment.invoice.currency || "EUR"} • {selectedOrder.outgoingPayment.invoice.status || "N/A"}
                        </Typography.Text>
                      </Space>
                    ) : (
                      <Typography.Text type="secondary">Aucune facture fournisseur rattachée</Typography.Text>
                    )}
                  </Card>
                </div>

                <table className="invoice-table">
                  <thead>
                    <tr>
                      <th>Produit</th>
                      <th style={{ textAlign: 'right' }}>Quantité</th>
                      <th style={{ textAlign: 'right' }}>P.U. (€)</th>
                      <th style={{ textAlign: 'right' }}>Total (€)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedOrder.items.map((item, idx) => (
                      <tr key={idx}>
                        <td style={{ maxWidth: 400 }}>
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{ fontSize: 15, fontWeight: 600 }}>{item.productName}</span>
                            <Typography.Text type="secondary" style={{ fontSize: 11 }}>SKU: {item.sku || 'N/A'}</Typography.Text>
                          </div>
                        </td>
                        <td style={{ textAlign: 'right', fontSize: 14 }}>{item.quantity} {item.unit || 'pcs'}</td>
                        <td style={{ textAlign: 'right', fontSize: 14 }}>{item.unitPrice?.toFixed(2)}</td>
                        <td style={{ textAlign: 'right', fontSize: 14, fontWeight: 700 }}>{item.totalPrice?.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="invoice-summary">
                  <div className="summary-row">
                    <span>Sous-total HT</span>
                    <span>{selectedOrder.items.reduce((sum, i) => sum + (i.totalPrice || 0), 0).toFixed(2)} €</span>
                  </div>
                  {(selectedOrder.importCost || selectedOrder.shippingCost) && (
                    <div className="summary-row">
                      <span>Frais logistiques</span>
                      <span>{(selectedOrder.importCost || selectedOrder.shippingCost || 0).toFixed(2)} €</span>
                    </div>
                  )}
                  <div className="summary-row total">
                    <span>MONTANT TOTAL</span>
                    <span>{selectedOrder.totalAmount?.toFixed(2)} €</span>
                  </div>
                </div>

                {(selectedOrder.deliveryAddress?.deliveryInfo || selectedOrder.deliveryAddress?.comment || selectedOrder.notes) && (
                  <div style={{ marginTop: 28, padding: 22, background: '#f8fafc', borderRadius: 16, border: '1px solid #f1f5f9' }}>
                    <span className="invoice-info-label" style={{ display: 'block', marginBottom: 16 }}>Notes & Instructions</span>
                    <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.6 }}>
                      {selectedOrder.deliveryAddress?.deliveryInfo && (
                        <p style={{ marginBottom: 12 }}><strong>Livraison:</strong> {selectedOrder.deliveryAddress.deliveryInfo}</p>
                      )}
                      {selectedOrder.deliveryAddress?.comment && (
                        <p style={{ marginBottom: 12 }}><strong>Interne:</strong> {selectedOrder.deliveryAddress.comment}</p>
                      )}
                      {selectedOrder.notes && (
                        <p style={{ marginBottom: 0 }}><strong>Général:</strong> {selectedOrder.notes}</p>
                      )}
                    </div>
                  </div>
                )}
                
                <div style={{ marginTop: 40, textAlign: 'center' }}>
                  <Typography.Text type="secondary" style={{ fontSize: 11, letterSpacing: '0.05em' }}>
                    HONNORÉ INTRANET • DOCUMENT GÉNÉRÉ LE {dayjs().format("DD/MM/YYYY HH:mm")}
                  </Typography.Text>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div style={{ padding: 100, textAlign: 'center' }}><Spin size="large" /></div>
        )}
      </Modal>

      <Modal
        title="Factures Pennylane liées au bon de commande"
        open={showPennylaneModal}
        onCancel={() => {
          pennylaneOpenRequestRef.current += 1;
          setShowPennylaneModal(false);
          if (reopenDetailsAfterPennylane && selectedOrder) {
            setShowDetailsModal(true);
            setReopenDetailsAfterPennylane(false);
          }
          setPennylaneOrder(null);
        }}
        width="60vw"
        className="premium-modal"
        styles={{ body: { padding: 0, overflow: "hidden" } }}
        footer={[
          <Button
            key="close"
            className="premium-input"
            onClick={() => {
              pennylaneOpenRequestRef.current += 1;
              setShowPennylaneModal(false);
              if (reopenDetailsAfterPennylane && selectedOrder) {
                setShowDetailsModal(true);
                setReopenDetailsAfterPennylane(false);
              }
              setPennylaneOrder(null);
            }}
          >
            Fermer
          </Button>,
        ]}
      >
        <div style={{ maxHeight: "78vh", overflowY: "auto", padding: 24 }}>
          <Tabs
            items={[
              {
                key: "incoming",
                label: "Paiement entrant (client)",
                children: (
                  <Space direction="vertical" size={14} style={{ width: "100%" }}>
                    <Card size="small">
                      <Space direction="vertical" size={6}>
                        <Typography.Text type="secondary">Facture actuellement rattachée</Typography.Text>
                        {pennylaneOrder?.incomingPayment?.invoice ? (
                          <Space>
                            <Tag color="blue">
                              {pennylaneOrder.incomingPayment.invoice.invoiceNumber ||
                                pennylaneOrder.incomingPayment.invoice.number ||
                                pennylaneOrder.incomingPayment.invoice.invoiceId}
                            </Tag>
                            <Button
                              size="small"
                              danger
                              onClick={() => handleClearPennylaneInvoice("incoming")}
                              loading={attachingPennylaneDirection === "incoming"}
                            >
                              Détacher
                            </Button>
                          </Space>
                        ) : (
                          <Space direction="vertical" size={6}>
                            <Typography.Text>Aucune facture client rattachée</Typography.Text>
                            <Select
                              size="small"
                              style={{ width: 210 }}
                              value={pennylaneOrder?.incomingPayment?.status || pennylaneOrder?.paymentStatus || "waiting"}
                              loading={attachingPennylaneDirection === "incoming"}
                              onChange={(value) => handleManualPaymentStatus("incoming", value)}
                              options={[
                                { value: "waiting", label: "En attente" },
                                { value: "partial", label: "Paiement partiel" },
                                { value: "paid", label: "Payé" },
                              ]}
                            />
                          </Space>
                        )}
                      </Space>
                    </Card>
                    <Input
                      placeholder="Recherche facture client (ex: nom client, numéro)"
                      value={pennylaneCustomerQuery}
                      onChange={(e) => setPennylaneCustomerQuery(e.target.value)}
                      onPressEnter={() => fetchPennylaneInvoices("customer")}
                      suffix={<SearchOutlined onClick={() => fetchPennylaneInvoices("customer")} />}
                    />
                    <Spin spinning={pennylaneLoading}>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
                        {pennylaneCustomerInvoices.length === 0 ? (
                          <Empty description="Aucune facture client trouvée" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        ) : (
                          pennylaneCustomerInvoices.map((item) => (
                            <Card
                              key={`customer-${item.id || item.invoiceId || item.number}`}
                              size="small"
                              className="premium-card"
                              title={item.number || item.invoiceNumber || item.id || item.invoiceId || "Facture"}
                              extra={<Tag color="blue">{item.status || "N/A"}</Tag>}
                              actions={[
                                <Button
                                  key="attach"
                                  type="primary"
                                  size="small"
                                  loading={attachingPennylaneDirection === "incoming"}
                                  onClick={() => handleAttachPennylaneInvoice("incoming", item)}
                                >
                                  Rattacher
                                </Button>,
                              ]}
                            >
                              <Space direction="vertical" size={2}>
                                <Typography.Text>{item.thirdpartyName || "Client non renseigné"}</Typography.Text>
                                <Typography.Text strong>{item.amount || 0} {item.currency || "EUR"}</Typography.Text>
                                <Typography.Text type="secondary">
                                  Émise: {item.issuedAt ? dayjs(item.issuedAt).format("DD/MM/YYYY") : "N/A"} • Échéance: {item.dueAt ? dayjs(item.dueAt).format("DD/MM/YYYY") : "N/A"}
                                </Typography.Text>
                              </Space>
                            </Card>
                          ))
                        )}
                      </div>
                    </Spin>
                  </Space>
                ),
              },
              {
                key: "outgoing",
                label: "Paiement sortant (rétro fournisseur)",
                children: (
                  <Space direction="vertical" size={14} style={{ width: "100%" }}>
                    <Card size="small">
                      <Space direction="vertical" size={6}>
                        <Typography.Text type="secondary">Facture actuellement rattachée</Typography.Text>
                        {pennylaneOrder?.outgoingPayment?.invoice ? (
                          <Space>
                            <Tag color="purple">
                              {pennylaneOrder.outgoingPayment.invoice.invoiceNumber ||
                                pennylaneOrder.outgoingPayment.invoice.number ||
                                pennylaneOrder.outgoingPayment.invoice.invoiceId}
                            </Tag>
                            <Button
                              size="small"
                              danger
                              onClick={() => handleClearPennylaneInvoice("outgoing")}
                              loading={attachingPennylaneDirection === "outgoing"}
                            >
                              Détacher
                            </Button>
                          </Space>
                        ) : (
                          <Space direction="vertical" size={6}>
                            <Typography.Text>Aucune facture fournisseur rattachée</Typography.Text>
                            <Select
                              size="small"
                              style={{ width: 210 }}
                              value={pennylaneOrder?.outgoingPayment?.status || pennylaneOrder?.supplierPaymentStatus || "pending"}
                              loading={attachingPennylaneDirection === "outgoing"}
                              onChange={(value) => handleManualPaymentStatus("outgoing", value)}
                              options={[
                                { value: "pending", label: "En attente" },
                                { value: "partial", label: "Paiement partiel" },
                                { value: "paid", label: "Payé" },
                              ]}
                            />
                          </Space>
                        )}
                      </Space>
                    </Card>
                    <Input
                      placeholder="Recherche facture fournisseur (ex: nom fournisseur, numéro)"
                      value={pennylaneSupplierQuery}
                      onChange={(e) => setPennylaneSupplierQuery(e.target.value)}
                      onPressEnter={() => fetchPennylaneInvoices("supplier")}
                      suffix={<SearchOutlined onClick={() => fetchPennylaneInvoices("supplier")} />}
                    />
                    <Spin spinning={pennylaneLoading}>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
                        {pennylaneSupplierInvoices.length === 0 ? (
                          <Empty description="Aucune facture fournisseur trouvée" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        ) : (
                          pennylaneSupplierInvoices.map((item) => (
                            <Card
                              key={`supplier-${item.id || item.invoiceId || item.number}`}
                              size="small"
                              className="premium-card"
                              title={item.number || item.invoiceNumber || item.id || item.invoiceId || "Facture"}
                              extra={<Tag color="purple">{item.status || "N/A"}</Tag>}
                              actions={[
                                <Button
                                  key="attach"
                                  type="primary"
                                  size="small"
                                  loading={attachingPennylaneDirection === "outgoing"}
                                  onClick={() => handleAttachPennylaneInvoice("outgoing", item)}
                                >
                                  Rattacher
                                </Button>,
                              ]}
                            >
                              <Space direction="vertical" size={2}>
                                <Typography.Text>{item.thirdpartyName || "Fournisseur non renseigné"}</Typography.Text>
                                <Typography.Text strong>{item.amount || 0} {item.currency || "EUR"}</Typography.Text>
                                <Typography.Text type="secondary">
                                  Émise: {item.issuedAt ? dayjs(item.issuedAt).format("DD/MM/YYYY") : "N/A"} • Échéance: {item.dueAt ? dayjs(item.dueAt).format("DD/MM/YYYY") : "N/A"}
                                </Typography.Text>
                              </Space>
                            </Card>
                          ))
                        )}
                      </div>
                    </Spin>
                  </Space>
                ),
              },
            ]}
          />
        </div>
      </Modal>


      {/* Modale pour le bon de commande conseillé */}
      <Modal
        title={
          <>
            <ShoppingCartOutlined /> Bon de commande conseillé
            {allLocationReorderData.length > 0 && (
              <span
                style={{
                  fontSize: "14px",
                  marginLeft: "8px",
                  fontWeight: "normal",
                }}
              >
                ({allLocationReorderData.length} emplacement
                {allLocationReorderData.length > 1 ? "s" : ""})
              </span>
            )}
          </>
        }
        open={showRecommendedOrderModal}
        onCancel={() => setShowRecommendedOrderModal(false)}
        width={1200}
        centered={true}
        footer={null}
      >
        {aggregatedReorderItems && aggregatedReorderItems.length > 0 && (
          <>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div
                style={{
                  marginBottom: 16,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <span style={{ marginRight: 8 }}>
                  Jours de stock souhaités (runway):
                </span>
                <InputNumber
                  min={1}
                  max={365}
                  value={desiredRunway}
                  onChange={(value) =>
                    setDesiredRunway((value as number) || 30)
                  }
                  style={{ width: 100 }}
                />
                <Tooltip title="Nombre de jours de stock souhaités pour recalculer les quantités recommandées">
                  <InfoCircleOutlined
                    style={{ marginLeft: 8, color: "#1890ff" }}
                  />
                </Tooltip>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <Button
                  type="primary"
                  onClick={() => {
                    // Ajouter tous les produits conseillés au bon de commande
                    const newItems = [...(newPO.items || [])];
                    const newSelectedProducts = { ...selectedProducts };
                    // Array pour stocker les produits recommandés avec leurs quantités par emplacement
                    const newRecommendedProducts: RecommendedProduct[] = [];

                    aggregatedReorderItems.forEach((item: any) => {
                      // Stocker les quantités recommandées par emplacement
                      const locationStocks: LocationStock[] = [];
                      let hasAtLeastOneItemWithQuantity = false;
                      let totalRecommendedQuantity = 0;

                      if (item.locationData) {
                        Object.entries(item.locationData).forEach(
                          ([locationId, locationInfo]: [string, any]) => {
                            if (locationInfo) {
                              // Calculer la quantité selon notre formule: runway * dailySales - inventory
                              const calculatedQuantity = Math.max(
                                0,
                                Math.round(
                                  desiredRunway * locationInfo.dailySales -
                                    locationInfo.inventory
                                )
                              );

                              // Vérifier si la quantité est supérieure à 0
                              if (calculatedQuantity > 0) {
                                hasAtLeastOneItemWithQuantity = true;
                                locationStocks.push({
                                  locationId,
                                  stock: calculatedQuantity,
                                });
                              }

                              totalRecommendedQuantity += calculatedQuantity;
                            }
                          }
                        );
                      } else {
                        // Fallback si locationData n'est pas disponible
                        const calculatedQuantity =
                          typeof item.dailySales === "number" &&
                          typeof item.inventory === "number"
                            ? Math.max(
                                0,
                                Math.round(
                                  desiredRunway * item.dailySales -
                                    item.inventory
                                )
                              )
                            : item.recommendedQuantity || 0;

                        totalRecommendedQuantity = calculatedQuantity;

                        // Si nous avons une quantité mais pas d'emplacement spécifique
                        if (calculatedQuantity > 0) {
                          hasAtLeastOneItemWithQuantity = true;
                          locationStocks.push({
                            locationId: "default", // Un identifiant par défaut
                            stock: calculatedQuantity,
                          });
                        }
                      }

                      // Enregistrer le produit dans notre state uniquement s'il a au moins une unité
                      if (hasAtLeastOneItemWithQuantity) {
                        newRecommendedProducts.push({
                          productId: item.productId,
                          locationStocks: locationStocks,
                        });
                      }

                      // Vérifier si le produit existe déjà dans la commande
                      const existingIndex = newItems.findIndex(
                        (existing) => existing.productId === item.productId
                      );

                      // Marquer le produit comme sélectionné
                      newSelectedProducts[item.productId] = true;

                      if (existingIndex >= 0) {
                        // Mettre à jour la quantité si le produit existe déjà
                        newItems[existingIndex].quantity +=
                          totalRecommendedQuantity;
                        newItems[existingIndex].totalPrice =
                          newItems[existingIndex].quantity *
                          newItems[existingIndex].unitPrice;
                      } else if (totalRecommendedQuantity > 0) {
                        // Sinon, ajouter le nouveau produit s'il a une quantité
                        newItems.push({
                          productId: item.productId,
                          productName: item.productName,
                          quantity: totalRecommendedQuantity,
                          unitPrice: item.cost,
                          totalPrice: item.cost * totalRecommendedQuantity,
                          unit: "unité",
                          vendor: item.vendor || "",
                          sku: item.sku || "",
                        });
                      }
                    });

                    // Mettre à jour les produits recommandés dans le state local
                    setRecommendedProducts(newRecommendedProducts);

                    // Mettre à jour les produits sélectionnés
                    setSelectedProducts(newSelectedProducts);

                    // Mettre à jour le bon de commande
                    setNewPO((prev) => ({
                      ...prev,
                      items: newItems,
                      totalAmount: roundTo2(
                        newItems.reduce(
                          (sum, item) => sum + (item.totalPrice || 0),
                          0
                        )
                      ),
                    }));

                    // Afficher le nombre de produits recommandés dans le message de succès
                    const productCount = newRecommendedProducts.length;

                    setShowRecommendedOrderModal(false);
                    showCreateDrawer(newRecommendedProducts);
                    message.success(
                      `${productCount} produit${
                        productCount > 1 ? "s" : ""
                      } recommandé${productCount > 1 ? "s" : ""} ajouté${
                        productCount > 1 ? "s" : ""
                      } au bon de commande`
                    );

                    // Pour débogage, log les produits recommandés
                    console.log(
                      "Produits recommandés:",
                      newRecommendedProducts
                    );
                  }}
                >
                  Passer à la création
                </Button>
              </div>
            </div>
            <div style={{ flex: 1, overflow: "auto" }}>
              <Table
                dataSource={
                  calculatedItems.length > 0
                    ? calculatedItems
                    : aggregatedReorderItems
                }
                rowClassName={(record: any) => {
                  // Appliquer une classe CSS basée sur le niveau de priorité
                  if (record.priorityLevel === "CRITIQUE") return "bg-red-50";
                  if (record.priorityLevel === "IMPORTANT")
                    return "bg-orange-50";
                  if (record.priorityLevel === "MODÉRÉ") return "bg-yellow-50";
                  return "";
                }}
                pagination={false} // Pas de pagination
                scroll={{ y: "calc(100vh - 300px)" }} // Table scrollable
                sortDirections={["ascend", "descend", "ascend"]}
                columns={[
                  {
                    title: "Priorité",
                    dataIndex: "priorityLevel",
                    key: "priorityLevel",
                    align: "center",
                    width: 100,
                    sorter: (a: ReorderItem, b: ReorderItem) => {
                      const priorityMap: Record<string, number> = {
                        CRITIQUE: 3,
                        IMPORTANT: 2,
                        MODÉRÉ: 1,
                        NORMAL: 0,
                      };
                      return (
                        priorityMap[a.priorityLevel] -
                        priorityMap[b.priorityLevel]
                      );
                    },
                    render: (level: string, record: ReorderItem) => {
                      let color = "";
                      if (level === "CRITIQUE") color = "red";
                      else if (level === "IMPORTANT") color = "orange";
                      else if (level === "MODÉRÉ") color = "gold";
                      else color = "blue";

                      return (
                        <div>
                          <Tag color={color} style={{ margin: 0 }}>
                            {level}
                          </Tag>
                          <div style={{ marginTop: 4, fontSize: "12px" }}>
                            Score: {record.priorityScore.toFixed(1)}
                          </div>
                        </div>
                      );
                    },
                  },
                  {
                    title: "Produit",
                    dataIndex: "productName",
                    key: "productName",
                    width: 200,
                    sorter: (a: ReorderItem, b: ReorderItem) =>
                      a.productName.localeCompare(b.productName),
                    render: (text: string, record: ReorderItem) => (
                      <div style={{ padding: "5px" }}>
                        <div>{text}</div>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            fontSize: "12px",
                            color: "#888",
                          }}
                        >
                          {record.vendor && <span>{record.vendor}</span>}
                        </div>
                      </div>
                    ),
                  },
                  {
                    title: "Stock actuel",
                    key: "stockInfo",
                    children: (() => {
                      // Récupérer tous les emplacements distincts des données agrégées
                      const uniqueLocations = new Map();
                      aggregatedReorderItems.forEach((item: any) => {
                        if (item.locationData) {
                          Object.entries(item.locationData).forEach(
                            ([locationId, locationInfo]: [string, any]) => {
                              if (
                                !uniqueLocations.has(locationId) &&
                                locationInfo.locationName
                              ) {
                                uniqueLocations.set(
                                  locationId,
                                  locationInfo.locationName
                                );
                              }
                            }
                          );
                        }
                      });

                      // Créer une sous-colonne pour chaque emplacement
                      const columns = Array.from(uniqueLocations.entries()).map(
                        ([locationId, locationName]) => ({
                          title: locationName,
                          dataIndex: ["locationData", locationId],
                          key: `inventory_${locationId}`,
                          align: "center" as const,
                          width: 100,
                          render: (_: any, record: any) => {
                            const locationData =
                              record.locationData?.[locationId];
                            if (!locationData) return <span>-</span>;

                            return (
                              <div>
                                <div
                                  style={{
                                    fontWeight:
                                      locationData.inventory === 0
                                        ? "bold"
                                        : "normal",
                                  }}
                                >
                                  {locationData.inventory}
                                </div>
                                {locationData.daysUntilStockout > 0 && (
                                  <div
                                    style={{ marginTop: 4, fontSize: "12px" }}
                                  >
                                    {locationData.daysUntilStockout?.toFixed(
                                      0
                                    ) || "-"}{" "}
                                    jours
                                  </div>
                                )}
                              </div>
                            );
                          },
                          sorter: (a: any, b: any) => {
                            const inventoryA =
                              a.locationData?.[locationId]?.inventory || 0;
                            const inventoryB =
                              b.locationData?.[locationId]?.inventory || 0;
                            return inventoryA - inventoryB;
                          },
                        })
                      );

                      return columns.length > 0
                        ? columns
                        : [
                            {
                              title: "Aucune donnée",
                              key: "noData",
                              align: "center" as const,
                              render: () => "-",
                            },
                          ];
                    })(),
                  },
                  {
                    title: "Ventes/jour",
                    key: "salesData",
                    children: (() => {
                      // Récupérer tous les emplacements distincts des données agrégées
                      const uniqueLocations = new Map();
                      aggregatedReorderItems.forEach((item: any) => {
                        if (item.locationData) {
                          Object.entries(item.locationData).forEach(
                            ([locationId, locationInfo]: [string, any]) => {
                              if (
                                !uniqueLocations.has(locationId) &&
                                locationInfo.locationName
                              ) {
                                uniqueLocations.set(
                                  locationId,
                                  locationInfo.locationName
                                );
                              }
                            }
                          );
                        }
                      });

                      // Créer une sous-colonne pour chaque emplacement
                      const columns = Array.from(uniqueLocations.entries()).map(
                        ([locationId, locationName]) => ({
                          title: locationName,
                          dataIndex: ["locationData", locationId],
                          key: `sales_${locationId}`,
                          align: "right" as const,
                          width: 120,
                          render: (_: any, record: any) => {
                            const locationData =
                              record.locationData?.[locationId];
                            return locationData ? (
                              <div>
                                <div>
                                  {locationData.dailySales.toFixed(2)}/jour
                                </div>
                              </div>
                            ) : (
                              <span>-</span>
                            );
                          },
                          sorter: (a: any, b: any) => {
                            const salesA =
                              a.locationData?.[locationId]?.dailySales || 0;
                            const salesB =
                              b.locationData?.[locationId]?.dailySales || 0;
                            return salesA - salesB;
                          },
                        })
                      );

                      return columns.length > 0
                        ? columns
                        : [
                            {
                              title: "Aucune donnée",
                              key: "noData",
                              align: "center" as const,
                              render: () => "-",
                            },
                          ];
                    })(),
                  },
                  {
                    title: "Qté recommandée",
                    key: "recommendedQuantity",
                    children: (() => {
                      // Récupérer tous les emplacements distincts des données agrégées
                      const uniqueLocations = new Map();
                      aggregatedReorderItems.forEach((item: any) => {
                        if (item.locationData) {
                          Object.entries(item.locationData).forEach(
                            ([locationId, locationInfo]: [string, any]) => {
                              if (
                                !uniqueLocations.has(locationId) &&
                                locationInfo.locationName
                              ) {
                                uniqueLocations.set(
                                  locationId,
                                  locationInfo.locationName
                                );
                              }
                            }
                          );
                        }
                      });

                      // Créer une sous-colonne pour chaque emplacement
                      const columns = Array.from(uniqueLocations.entries()).map(
                        ([locationId, locationName]) => ({
                          title: locationName,
                          dataIndex: ["locationData", locationId],
                          key: `rec_qty_${locationId}`,
                          align: "center" as const,
                          width: 100,
                          render: (_: any, record: any) => {
                            const locationData =
                              record.locationData?.[locationId];
                            if (!locationData) return <span>-</span>;

                            // Calculer la quantité recommandée selon la formule: runway * dailySales - inventory
                            const calculatedQuantity = Math.max(
                              0,
                              Math.round(
                                desiredRunway * locationData.dailySales -
                                  locationData.inventory
                              )
                            );

                            return (
                              <div
                                style={{
                                  fontWeight: "bold",
                                  color:
                                    calculatedQuantity > 0
                                      ? "#1890ff"
                                      : "inherit",
                                }}
                              >
                                {calculatedQuantity}
                              </div>
                            );
                          },
                          sorter: (a: any, b: any) => {
                            const locationA = a.locationData?.[locationId];
                            const locationB = b.locationData?.[locationId];

                            const qtyA = locationA
                              ? Math.max(
                                  0,
                                  Math.round(
                                    desiredRunway * locationA.dailySales -
                                      locationA.inventory
                                  )
                                )
                              : 0;
                            const qtyB = locationB
                              ? Math.max(
                                  0,
                                  Math.round(
                                    desiredRunway * locationB.dailySales -
                                      locationB.inventory
                                  )
                                )
                              : 0;

                            return qtyA - qtyB;
                          },
                        })
                      );

                      // Ne pas ajouter de colonne Total

                      return columns.length > 0
                        ? columns
                        : [
                            {
                              title: "Aucune donnée",
                              key: "noData",
                              align: "center" as const,
                              render: () => "-",
                            },
                          ];
                    })(),
                  },
                  {
                    title: "Coût unitaire",
                    dataIndex: "cost",
                    key: "cost",
                    width: 100,
                    align: "right",
                    sorter: (a: ReorderItem, b: ReorderItem) => a.cost - b.cost,
                    render: (cost: number) => `${cost.toFixed(2)} €`,
                  },
                  {
                    title: "Prix vente",
                    dataIndex: "price",
                    key: "price",
                    width: 100,
                    align: "right",
                    sorter: (a: ReorderItem, b: ReorderItem) =>
                      a.price - b.price,
                    render: (price: number) => `${price.toFixed(2)} €`,
                  },
                ]}
                summary={(pageData: readonly any[]): React.ReactNode => {
                  // Calculer le coût total, le prix total et le profit prévu
                  let totalCost = 0;
                  let totalPrice = 0;

                  pageData.forEach((item) => {
                    // Calculer le coût total pour cet item
                    let itemTotalCost = 0;
                    let itemTotalQuantity = 0;

                    if (item.locationData) {
                      Object.values(item.locationData).forEach(
                        (locationInfo: any) => {
                          if (locationInfo) {
                            // Calculer la quantité recommandée selon la formule: runway * dailySales - inventory
                            const calculatedQuantity = Math.max(
                              0,
                              Math.round(
                                desiredRunway * locationInfo.dailySales -
                                  locationInfo.inventory
                              )
                            );

                            itemTotalQuantity += calculatedQuantity;
                            itemTotalCost += calculatedQuantity * item.cost;
                          }
                        }
                      );
                    } else if (item.totalCost) {
                      // Fallback pour les anciens objets
                      itemTotalCost = item.totalCost;

                      // Pour les éléments sans locationData, utiliser dailySales et inventory globaux si disponibles
                      if (
                        typeof item.dailySales === "number" &&
                        typeof item.inventory === "number"
                      ) {
                        itemTotalQuantity = Math.max(
                          0,
                          Math.round(
                            desiredRunway * item.dailySales - item.inventory
                          )
                        );
                      } else {
                        itemTotalQuantity = item.recommendedQuantity || 0;
                      }
                    }

                    // Ajouter au total
                    totalCost += itemTotalCost;
                    totalPrice += itemTotalQuantity * item.price;
                  });

                  // Calculer le profit prévu
                  const expectedProfit = totalPrice - totalCost;
                  const profitMargin =
                    totalCost > 0
                      ? ((expectedProfit / totalCost) * 100).toFixed(2)
                      : "0";

                  // Trouver les indices des colonnes pour le positionnement correct
                  // Note: Ces indices doivent correspondre à l'ordre réel des colonnes dans le tableau
                  const priorityIndex = 0; // Colonne "Priorité"
                  const productIndex = 1; // Colonne "Produit"
                  const dailySalesIndex = 2; // Colonne "Ventes/jour"
                  const stockIndex = 3; // Colonne "Stock actuel"
                  const recommendedQtyIndex = 4; // Colonne "Qté recommandée"
                  const costIndex = 5; // Colonne "Coût unitaire"
                  const priceIndex = 6; // Colonne "Prix vente"

                  return (
                    <Table.Summary.Row>
                      {/* Cellules vides pour les premières colonnes */}
                      <Table.Summary.Cell index={priorityIndex} align="center">
                        <strong>TOTAL</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell
                        index={productIndex}
                      ></Table.Summary.Cell>
                      <Table.Summary.Cell
                        index={dailySalesIndex}
                      ></Table.Summary.Cell>
                      <Table.Summary.Cell
                        index={stockIndex}
                      ></Table.Summary.Cell>

                      {/* Profit prévu sous Qté recommandée */}
                      <Table.Summary.Cell
                        index={recommendedQtyIndex}
                        align="right"
                      >
                        <Statistic
                          title="Profit"
                          value={expectedProfit}
                          precision={2}
                          valueStyle={{
                            color: expectedProfit >= 0 ? "#52c41a" : "#f5222d",
                          }}
                          suffix="€"
                        />
                        <span
                          style={{
                            color: expectedProfit >= 0 ? "#52c41a" : "#f5222d",
                          }}
                        >
                          ({profitMargin}%)
                        </span>
                      </Table.Summary.Cell>

                      {/* Coût total sous Coût unitaire */}
                      <Table.Summary.Cell index={costIndex} align="right">
                        <Statistic
                          title="Coût total"
                          value={totalCost}
                          precision={2}
                          suffix="€"
                          valueStyle={{ fontWeight: "bold" }}
                        />
                      </Table.Summary.Cell>

                      {/* CA total sous Prix vente */}
                      <Table.Summary.Cell index={priceIndex} align="right">
                        <Statistic
                          title="CA total"
                          value={totalPrice}
                          precision={2}
                          suffix="€"
                          valueStyle={{ fontWeight: "bold" }}
                        />
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  );
                }}
              />
            </div>
          </>
        )}
      </Modal>

      {/* Modale pour la gestion des règles d'approvisionnement */}
      <Modal
        title="Règles d'approvisionnement"
        open={showRulesModal}
        onCancel={() => setShowRulesModal(false)}
        width={1000}
        footer={null}
      >
        <Tabs
          defaultActiveKey="list"
          items={[
            {
              key: "list",
              label: "Liste des règles",
              children: (
                <div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: 16,
                    }}
                  >
                    <Typography.Title level={5}>
                      Règles d'approvisionnement configurées
                    </Typography.Title>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => {
                        // Créer une nouvelle règle vide - sans ID généré côté client
                        setCurrentRule({
                          // Supprimer la génération d'ID client pour éviter l'erreur MongoDB
                          name: "",
                          type: RuleType.MinQuantityPerSupplier,
                          active: true,
                          value: 0,
                          createdAt: new Date().toISOString(),
                          updatedAt: new Date().toISOString(),
                        });
                      }}
                    >
                      Nouvelle règle
                    </Button>
                  </div>

                  <Table
                    dataSource={supplyRules}
                    rowKey="id"
                    pagination={false}
                    columns={[
                      {
                        title: "Nom",
                        dataIndex: "name",
                        key: "name",
                        render: (text) => (
                          <Typography.Text strong>{text}</Typography.Text>
                        ),
                      },
                      {
                        title: "Type",
                        dataIndex: "type",
                        key: "type",
                        render: (type: RuleType) => {
                          const typeLabels = {
                            // Règles basées sur les quantités
                            [RuleType.MinQuantityPerSupplier]:
                              "Qté min. par fournisseur",
                            [RuleType.MinQuantityPerProduct]:
                              "Qté min. par produit",
                            [RuleType.MaxQuantityPerProduct]:
                              "Qté max. par produit",
                            [RuleType.QuantityRange]: "Plage de quantité",

                            // Règles basées sur les valeurs monétaires
                            [RuleType.MinOrderValue]: "Valeur min. de commande",
                            [RuleType.MaxOrderValue]: "Valeur max. de commande",
                            [RuleType.OrderValueRange]: "Plage de valeur",

                            // Règles basées sur les stocks et approvisionnements
                            [RuleType.RunwayOverride]: "Override du runway",
                            [RuleType.AutoOrder]: "Commande automatique",

                            // Règles liées aux catégories et groupes
                            [RuleType.CategoryMinQuantity]:
                              "Qté min. par catégorie",
                            [RuleType.CategoryMaxQuantity]:
                              "Qté max. par catégorie",

                            // Règles temporelles
                            [RuleType.SeasonalAdjustment]:
                              "Ajustement saisonnier",
                            [RuleType.DayOfWeekOrder]:
                              "Commande jour spécifique",

                            // Règles de priorité
                            [RuleType.SupplierPriority]: "Priorité fournisseur",
                            [RuleType.EmergencyRestock]:
                              "Réapprovisionnement urgent",

                            // Règles spéciales
                            [RuleType.CustomFormula]: "Formule personnalisée",
                          };
                          return typeLabels[type] || type;
                        },
                      },
                      {
                        title: "Cible",
                        key: "target",
                        render: (_, record) => {
                          if (record.productName) {
                            return <Tag color="blue">{record.productName}</Tag>;
                          } else if (record.supplierName) {
                            return (
                              <Tag color="orange">{record.supplierName}</Tag>
                            );
                          } else if (record.locationName) {
                            return (
                              <Tag color="green">{record.locationName}</Tag>
                            );
                          } else {
                            return <Tag color="default">Global</Tag>;
                          }
                        },
                      },
                      {
                        title: "Valeur",
                        key: "value",
                        render: (_, record) => (
                          <Typography.Text>
                            {record.value} {record.unit || ""}
                          </Typography.Text>
                        ),
                      },
                      {
                        title: "Statut",
                        dataIndex: "active",
                        key: "active",
                        render: (active) => (
                          <Tag color={active ? "green" : "red"}>
                            {active ? "Actif" : "Inactif"}
                          </Tag>
                        ),
                      },
                      {
                        title: "Actions",
                        key: "actions",
                        render: (_, record) => (
                          <Space>
                            <Button
                              type="text"
                              icon={<EditOutlined />}
                              onClick={() => setCurrentRule(record)}
                            />
                            <Button
                              type="text"
                              icon={
                                record.active ? (
                                  <PauseCircleOutlined />
                                ) : (
                                  <PlayCircleOutlined />
                                )
                              }
                              onClick={() => handleToggleSupplyRule(record)}
                            />
                            <Button
                              type="text"
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() => {
                                Modal.confirm({
                                  title: "Confirmation",
                                  content:
                                    "Êtes-vous sûr de vouloir supprimer cette règle ?",
                                  okText: "Oui",
                                  cancelText: "Non",
                                  onOk: () => handleDeleteSupplyRule(record),
                                });
                              }}
                            />
                          </Space>
                        ),
                      },
                    ]}
                    locale={{ emptyText: "Aucune règle configurée" }}
                  />
                </div>
              ),
            },
            {
              key: "edit",
              label: currentRule
                ? `${currentRule.id ? "Modifier" : "Créer"} une règle`
                : "Nouvelle règle",
              disabled: !currentRule,
              children: currentRule && (
                <Form
                  layout="vertical"
                  initialValues={{
                    name: currentRule?.name,
                    type: currentRule?.type,
                    supplierId: currentRule?.supplierId,
                    productId: currentRule?.productId,
                    value: currentRule?.value,
                    unit: currentRule?.unit,
                    active: currentRule?.active,
                  }}
                  onFinish={(values) => {
                    // Mise à jour des valeurs de la règle
                    const updatedRule: SupplyRule = {
                      ...(currentRule || ({} as SupplyRule)),
                      name: values.name,
                      type: values.type,
                      supplierId: values.supplierId,
                      supplierName: values.supplierId
                        ? supplierList.find((s) => s.id === values.supplierId)
                            ?.name || ""
                        : undefined,
                      productId: values.productId,
                      productName: values.productId
                        ? allProducts.find((p) => p.id === values.productId)
                            ?.title || ""
                        : undefined,
                      locationId: undefined,
                      locationName: undefined,
                      value: values.value,
                      unit: values.unit,
                      active: values.active,
                      updatedAt: new Date().toISOString(),
                    };

                    // Enregistrer en base de données via l'API
                    handleSaveSupplyRule(updatedRule);

                    // Réinitialiser et revenir à la liste
                    setCurrentRule(null);
                  }}
                >
                  <Form.Item
                    label="Nom de la règle"
                    name="name"
                    rules={[
                      {
                        required: true,
                        message: "Veuillez entrer un nom pour cette règle",
                      },
                    ]}
                  >
                    <Input placeholder="Ex: Minimum de commande chez Fournisseur XYZ" />
                  </Form.Item>

                  <Form.Item
                    label="Type de règle"
                    name="type"
                    rules={[
                      {
                        required: true,
                        message: "Veuillez sélectionner un type de règle",
                      },
                    ]}
                  >
                    <Select>
                      <Select.Option value={RuleType.MinQuantityPerSupplier}>
                        Quantité minimale par fournisseur
                      </Select.Option>
                      <Select.Option value={RuleType.MinQuantityPerProduct}>
                        Quantité minimale par produit
                      </Select.Option>
                      <Select.Option value={RuleType.MinOrderValue}>
                        Valeur minimale de commande
                      </Select.Option>
                      <Select.Option value={RuleType.MaxQuantityPerProduct}>
                        Quantité maximale par produit
                      </Select.Option>
                      <Select.Option value={RuleType.RunwayOverride}>
                        Override du runway
                      </Select.Option>
                      <Select.Option value={RuleType.AutoOrder}>
                        Commande automatique
                      </Select.Option>
                    </Select>
                  </Form.Item>

                  <Form.Item
                    noStyle
                    shouldUpdate={(prevValues, currentValues) =>
                      prevValues.type !== currentValues.type
                    }
                  >
                    {({ getFieldValue }) => {
                      const type = getFieldValue("type");

                      // Affichage conditionnel des champs en fonction du type de règle
                      return (
                        <>
                          {/* Champ fournisseur pour les règles spécifiques au fournisseur */}
                          {(type === RuleType.MinQuantityPerSupplier ||
                            type === RuleType.MinOrderValue) && (
                            <Form.Item
                              label="Fournisseur"
                              name="supplierId"
                              rules={[
                                {
                                  required: true,
                                  message:
                                    "Veuillez sélectionner un fournisseur",
                                },
                              ]}
                            >
                              <Select
                                showSearch
                                placeholder="Sélectionner un fournisseur"
                                optionFilterProp="children"
                              >
                                {supplierList.map((supplier) => (
                                  <Select.Option
                                    key={supplier.id}
                                    value={supplier.id}
                                  >
                                    {supplier.name}
                                  </Select.Option>
                                ))}
                              </Select>
                            </Form.Item>
                          )}

                          {/* Champ produit pour les règles spécifiques au produit */}
                          {(type === RuleType.MinQuantityPerProduct ||
                            type === RuleType.MaxQuantityPerProduct ||
                            type === RuleType.RunwayOverride ||
                            type === RuleType.AutoOrder) && (
                            <Form.Item
                              label="Produit"
                              name="productId"
                              rules={[
                                {
                                  required: true,
                                  message: "Veuillez sélectionner un produit",
                                },
                              ]}
                            >
                              <Select
                                showSearch
                                placeholder="Sélectionner un produit"
                                optionFilterProp="children"
                                filterOption={(input, option) =>
                                  (option?.children as unknown as string)
                                    .toLowerCase()
                                    .indexOf(input.toLowerCase()) >= 0
                                }
                              >
                                {allProducts.map((product) => (
                                  <Select.Option
                                    key={product.id}
                                    value={product.id}
                                  >
                                    {product.title} ({product.sku || "Sans SKU"}
                                    )
                                  </Select.Option>
                                ))}
                              </Select>
                            </Form.Item>
                          )}

                          {/* Champ valeur pour toutes les règles */}
                          <Form.Item
                            label={(() => {
                              switch (type) {
                                case RuleType.MinQuantityPerSupplier:
                                  return "Quantité minimale à commander";
                                case RuleType.MinQuantityPerProduct:
                                  return "Quantité minimale à commander";
                                case RuleType.MinOrderValue:
                                  return "Valeur minimale de commande";
                                case RuleType.MaxQuantityPerProduct:
                                  return "Quantité maximale à commander";
                                case RuleType.RunwayOverride:
                                  return "Runway spécifique (en jours)";
                                case RuleType.AutoOrder:
                                  return "Seuil de stock minimal pour commande auto.";
                                default:
                                  return "Valeur";
                              }
                            })()}
                            name="value"
                            rules={[
                              {
                                required: true,
                                message: "Veuillez entrer une valeur",
                              },
                            ]}
                          >
                            <InputNumber
                              min={0}
                              step={1}
                              style={{ width: "100%" }}
                            />
                          </Form.Item>

                          {/* Champ unité pour certaines règles */}
                          {type === RuleType.MinOrderValue && (
                            <Form.Item
                              label="Unité"
                              name="unit"
                              initialValue="€"
                            >
                              <Input disabled defaultValue="€" />
                            </Form.Item>
                          )}

                          {/* Champ actif/inactif pour toutes les règles */}
                          <Form.Item name="active" valuePropName="checked">
                            <Checkbox>Règle active</Checkbox>
                          </Form.Item>
                        </>
                      );
                    }}
                  </Form.Item>

                  <Form.Item>
                    <Space>
                      <Button type="primary" htmlType="submit">
                        {currentRule.id ? "Mettre à jour" : "Créer"}
                      </Button>
                      <Button onClick={() => setCurrentRule(null)}>
                        Annuler
                      </Button>
                    </Space>
                  </Form.Item>
                </Form>
              ),
            },
          ]}
          activeKey={currentRule ? "edit" : "list"}
          onChange={(key: string) => {
            if (key === "list") {
              setCurrentRule(null);
            }
          }}
        />
      </Modal>
    </div>
  );
};

export default Supply;
